import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from "react";

const getLocalDate = () => {
  const n = new Date();
  return n.getFullYear() + '-' + String(n.getMonth()+1).padStart(2,'0') + '-' + String(n.getDate()).padStart(2,'0');
};

const getLocalDay = () => {
  return new Date().toLocaleDateString('en-US', { weekday: 'long' });
};

const API_URL = "https://web-production-8518a.up.railway.app";

const COLORS = {
  black: "#0A0A0A",
  charcoal: "#111318",
  card: "#161B22",
  border: "#1E2530",
  teal: "#00D4D4",
  tealDim: "#00A3A3",
  tealGlow: "rgba(0,212,212,0.15)",
  gold: "#D4A017",
  goldBright: "#FFD700",
  goldGlow: "rgba(212,160,23,0.15)",
  silver: "#9BA3AF",
  silverBright: "#C8D0DA",
  white: "#F0F4F8",
  error: "#FF4444",
  success: "#00D4D4",
};

const GOALS = [
  { id: "strength", icon: "⚡", label: "Build Strength", desc: "Hit PRs and lift heavy" },
  { id: "fat_loss", icon: "🔥", label: "Lose Fat", desc: "Lean out and cut" },
  { id: "muscle", icon: "💪", label: "Build Muscle", desc: "Gain size and mass" },
  { id: "health", icon: "🌱", label: "Improve Health", desc: "Feel and live better" },
  { id: "energy", icon: "⚡", label: "Boost Energy", desc: "More vitality daily" },
  { id: "athletic", icon: "🏃", label: "Athletic Performance", desc: "Sport specific training" },
];


// Celebration animation
function CelebrationBurst({ show }) {
  const [particles, setParticles] = useState([]);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    if (!show) { setParticles([]); setTextVisible(false); return; }
    const colors = [COLORS.teal, COLORS.gold, "#9B59B6", "#E67E22"];
    const ps = Array.from({ length: 16 }, (_, i) => {
      const angle = (i / 16) * Math.PI * 2;
      const dist = 60 + Math.random() * 50;
      return {
        id: i,
        tx: Math.cos(angle) * dist,
        ty: Math.sin(angle) * dist,
        color: colors[i % colors.length],
        size: 6 + Math.random() * 8,
        delay: i * 12,
      };
    });
    setParticles(ps);
    setTextVisible(true);
    const t = setTimeout(() => { setParticles([]); setTextVisible(false); }, 900);
    return () => clearTimeout(t);
  }, [show]);

  if (!show && particles.length === 0) return null;

  return (
    <div style={{ position: "fixed", top: "45%", left: "50%", zIndex: 9999, pointerEvents: "none" }}>
      <style>{`
        @keyframes p-fly {
          0% { opacity:1; transform: translate(0,0) scale(1); }
          100% { opacity:0; transform: translate(var(--ex), var(--ey)) scale(0.1); }
        }
        @keyframes c-text {
          0% { opacity:0; transform:translate(-50%,-60%) scale(0.6); }
          30% { opacity:1; transform:translate(-50%,-60%) scale(1.1); }
          70% { opacity:1; transform:translate(-50%,-60%) scale(1); }
          100% { opacity:0; transform:translate(-50%,-60%) scale(0.9); }
        }
      `}</style>
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute", width: p.size, height: p.size,
          borderRadius: "50%", background: p.color,
          boxShadow: `0 0 8px ${p.color}88`,
          top: 0, left: 0,
          marginLeft: -(p.size/2), marginTop: -(p.size/2),
          animationName: "p-fly",
          animationDuration: "0.7s",
          animationTimingFunction: "ease-out",
          animationFillMode: "forwards",
          animationDelay: `${p.delay}ms`,
          ["--ex"]: `${p.tx}px`,
          ["--ey"]: `${p.ty}px`,
        }} />
      ))}
      {textVisible && (
        <div style={{
          position: "absolute", top: 0, left: 0,
          fontFamily: "'Rajdhani', sans-serif",
          fontSize: "28px", fontWeight: "900",
          letterSpacing: "4px", color: COLORS.teal,
          textShadow: "0 0 20px rgba(0,212,212,0.9)",
          animation: "c-text 0.9s ease-out forwards",
          whiteSpace: "nowrap",
        }}>✓ LOGGED</div>
      )}
    </div>
  );
}

function AnimaWelcomeScreen({ user, token, onComplete }) {
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateWelcomePlan();
  }, []);

  const generateWelcomePlan = async () => {
    try {
      const res = await fetch(`${API_URL}/workouts/analyze`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      setPlan(data.analysis || data.message || "Welcome to FORIMA!");
    } catch (e) {
      setPlan("Welcome to FORIMA! Log your first workout and ANIMA will build your personalized program.");
    }
    setLoading(false);
  };

  const formatPlan = (text) => {
    return text
      .replace(/#{1,3} /g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1");
  };

  return (
    <div style={styles.screen}>
      <div style={{
        padding: "60px 24px 24px",
        background: `linear-gradient(180deg, ${COLORS.charcoal} 0%, ${COLORS.black} 100%)`,
        borderBottom: `1px solid ${COLORS.border}`,
      }}>
        <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase" }}>
          WELCOME TO FORIMA
        </div>
        <div style={{ fontSize: "32px", fontWeight: "900", letterSpacing: "4px", color: COLORS.white, fontFamily: "'Rajdhani', sans-serif" }}>
          {user?.name?.toUpperCase() || "ATHLETE"}
        </div>
        <div style={{ fontSize: "16px", color: COLORS.silver }}>Your sovereign AI coach is ready</div>
      </div>

      <div style={styles.content}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
          padding: "20px",
          background: COLORS.card,
          border: `1px solid ${COLORS.teal}`,
          borderRadius: "16px",
          boxShadow: `0 0 30px ${COLORS.tealGlow}`,
        }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.gold})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            flexShrink: 0,
          }}>⚡</div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: "900", letterSpacing: "3px", color: COLORS.white }}>ANIMA</div>
            <div style={{ fontSize: "16px", color: COLORS.teal, letterSpacing: "2px" }}>YOUR SOVEREIGN AI COACH</div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase" }}>
              ANIMA IS BUILDING YOUR PLAN...
            </div>
            <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "8px" }}>
              Personalizing everything for your goals
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.silver, textTransform: "uppercase", marginBottom: "16px" }}>
              ANIMA — YOUR PERSONALIZED STARTING POINT
            </div>
            <div style={styles.cardTeal}>
              <div style={styles.analysisText}>
                {formatPlan(plan)}
              </div>
            </div>
          </div>
        )}

        <div style={{ height: "24px" }} />
        <button
          style={{ ...styles.btnGold, opacity: loading ? 0.4 : 1 }}
          disabled={loading}
          onClick={onComplete}
        >
          Let's Start Training →
        </button>
      </div>
    </div>
  );
}

const styles = {
  app: {
     background: "transparent",
     minHeight: "100vh",
     fontFamily: "'Rajdhani', 'Bebas Neue', sans-serif",
     color: COLORS.white,
     width: "100%",
     maxWidth: "100vw",
     margin: "0 auto",
     position: "relative",
     overflowX: "hidden",
  },
  screen: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    padding: "0",
    background: `
      radial-gradient(ellipse at 15% 15%, rgba(0,212,212,0.15) 0%, transparent 50%),
      radial-gradient(ellipse at 85% 85%, rgba(212,160,23,0.10) 0%, transparent 50%),
      repeating-linear-gradient(rgba(0,212,212,0.05) 0px, rgba(0,212,212,0.05) 1px, transparent 1px, transparent 60px),
      repeating-linear-gradient(90deg, rgba(0,212,212,0.05) 0px, rgba(0,212,212,0.05) 1px, transparent 1px, transparent 60px),
      #0A0A0A`,
    background: "transparent",
    position: "relative",
  },
  header: {
    padding: "60px 24px 24px",
    background: `linear-gradient(180deg, rgba(17,19,24,0.98) 0%, rgba(10,10,10,0.95) 100%)`,
    backdropFilter: "blur(20px)",
    borderBottom: `1px solid ${COLORS.border}`,
    boxShadow: "0 1px 0 rgba(0,212,212,0.1)",
  },
  logo: {
    fontSize: "42px",
    fontWeight: "900",
    letterSpacing: "8px",
    background: `linear-gradient(135deg, ${COLORS.teal} 0%, ${COLORS.gold} 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontFamily: "'Rajdhani', sans-serif",
  },
  tagline: {
    fontSize: "16px",
    letterSpacing: "3px",
    color: COLORS.silver,
    marginTop: "4px",
    textTransform: "uppercase",
  },
  content: {
    flex: 1,
    padding: "28px 24px 120px 24px",
    overflowY: "auto",
  },
  card: {
    background: `linear-gradient(160deg, rgba(22,27,34,0.95) 0%, rgba(17,19,24,0.95) 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "20px",
    padding: "24px",
    marginBottom: "16px",
    boxShadow: "0 4px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)",
    backdropFilter: "blur(10px)",
  },
  cardTeal: {
    background: `linear-gradient(160deg, rgba(22,27,34,0.95) 0%, rgba(17,19,24,0.95) 100%)`,
    border: `1px solid ${COLORS.teal}`,
    borderRadius: "20px",
    padding: "24px",
    marginBottom: "16px",
    boxShadow: `0 0 40px ${COLORS.tealGlow}, 0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(0,212,212,0.1)`,
    backdropFilter: "blur(10px)",
  },
  input: {
    width: "100%",
    background: COLORS.charcoal,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "12px",
    padding: "16px",
    color: COLORS.white,
    fontSize: "16px",
    fontFamily: "'Rajdhani', sans-serif",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: "12px",
    transition: "border-color 0.2s",
  },
  btnPrimary: {
    width: "100%",
    background: `linear-gradient(135deg, ${COLORS.teal} 0%, ${COLORS.tealDim} 100%)`,
    border: "none",
    borderRadius: "12px",
    padding: "18px",
    color: COLORS.black,
    fontSize: "18px",
    fontWeight: "900",
    letterSpacing: "3px",
    cursor: "pointer",
    fontFamily: "'Rajdhani', sans-serif",
    textTransform: "uppercase",
    transition: "all 0.2s",
    boxShadow: `0 4px 20px ${COLORS.tealGlow}`,
  },
  btnGold: {
    width: "100%",
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldBright} 100%)`,
    border: "none",
    borderRadius: "12px",
    padding: "18px",
    color: COLORS.black,
    fontSize: "18px",
    fontWeight: "900",
    letterSpacing: "3px",
    cursor: "pointer",
    fontFamily: "'Rajdhani', sans-serif",
    textTransform: "uppercase",
    transition: "all 0.2s",
    boxShadow: `0 4px 20px ${COLORS.goldGlow}`,
  },
  btnOutline: {
    width: "100%",
    background: "transparent",
    border: `1px solid ${COLORS.border}`,
    borderRadius: "12px",
    padding: "16px",
    color: COLORS.silver,
    fontSize: "16px",
    cursor: "pointer",
    fontFamily: "'Rajdhani', sans-serif",
    letterSpacing: "2px",
    textTransform: "uppercase",
    transition: "all 0.2s",
  },
  label: {
    fontSize: "16px",
    letterSpacing: "2px",
    color: COLORS.silver,
    textTransform: "uppercase",
    marginBottom: "8px",
    display: "block",
  },
  title: {
    fontSize: "32px",
    fontWeight: "900",
    letterSpacing: "4px",
    color: COLORS.white,
    textTransform: "uppercase",
    marginBottom: "8px",
    fontFamily: "'Rajdhani', sans-serif",
  },
  subtitle: {
    fontSize: "16px",
    color: COLORS.silver,
    letterSpacing: "1px",
    lineHeight: "1.6",
    marginBottom: "24px",
  },
  tealText: {
    color: COLORS.teal,
    fontWeight: "700",
  },
  goldText: {
    color: COLORS.gold,
    fontWeight: "700",
  },
  error: {
    color: COLORS.error,
    fontSize: "16px",
    marginBottom: "12px",
    padding: "12px",
    background: "rgba(255,68,68,0.1)",
    borderRadius: "8px",
    border: "1px solid rgba(255,68,68,0.3)",
  },
  navBar: {
    position: "fixed",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    maxWidth: "430px",
    background: "rgba(17,19,24,0.95)",
    backdropFilter: "blur(20px)",
    borderTop: `1px solid ${COLORS.border}`,
    display: "flex",
    justifyContent: "space-around",
    padding: "12px 0 24px",
    zIndex: 100,
  },
  navItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "8px 16px",
    borderRadius: "12px",
    transition: "all 0.2s",
  },
  navIcon: {
    fontSize: "22px",
  },
  navLabel: {
    fontSize: "10px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginBottom: "16px",
  },
  statCard: {
    background: `linear-gradient(160deg, ${COLORS.card} 0%, ${COLORS.charcoal} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "16px",
    padding: "20px 16px",
    textAlign: "center",
    boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
  },
  statValue: {
    fontSize: "36px",
    fontWeight: "900",
    letterSpacing: "2px",
    color: COLORS.teal,
    fontFamily: "'Rajdhani', sans-serif",
    lineHeight: "1",
  },
  statLabel: {
    fontSize: "9px",
    letterSpacing: "3px",
    color: COLORS.silver,
    textTransform: "uppercase",
    marginTop: "6px",
  },
  goalCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "all 0.2s",
  },
  goalCardSelected: {
    background: COLORS.card,
    border: `1px solid ${COLORS.teal}`,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: `0 0 16px ${COLORS.tealGlow}`,
  },
  analysisText: {
    fontSize: "16px",
    lineHeight: "1.8",
    color: COLORS.silverBright,
    fontFamily: "'Rajdhani', sans-serif",
    letterSpacing: "0.3px",
  },
  analysisSectionHeader: {
    fontSize: "16px",
    fontWeight: "900",
    letterSpacing: "3px",
    color: COLORS.teal,
    textTransform: "uppercase",
    marginTop: "24px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${COLORS.border}`,
    fontFamily: "'Rajdhani', sans-serif",
  },
  analysisSectionHeaderGold: {
    fontSize: "16px",
    fontWeight: "900",
    letterSpacing: "3px",
    color: COLORS.gold,
    textTransform: "uppercase",
    marginTop: "24px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${COLORS.border}`,
    fontFamily: "'Rajdhani', sans-serif",
  },
  divider: {
    height: "1px",
    background: COLORS.border,
    margin: "20px 0",
  },
  pill: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "16px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    fontWeight: "700",
  },
  workoutItem: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

function LandingScreen({ onGetStarted, onLogin }) {
  return (
    <div style={styles.screen}>
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        background: `
          radial-gradient(ellipse at 15% 15%, rgba(0,212,212,0.1) 0%, transparent 45%),
          radial-gradient(ellipse at 85% 85%, rgba(212,160,23,0.07) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 50%, rgba(0,212,212,0.03) 0%, transparent 70%),
          linear-gradient(180deg, #0D1117 0%, #0A0A0A 100%)
        `,
      }}>
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: `linear-gradient(rgba(0,212,212,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,212,0.03) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }} />
        {/* Sticky Nav */}
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: "rgba(10,10,10,0.92)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${COLORS.border}`, padding: "10px 20px" }}>
          <div style={{ maxWidth: "430px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: "18px", fontWeight: "900", letterSpacing: "4px", background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.gold})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>FORIMA</span>
            <div style={{ display: "flex", gap: "14px" }}>
              {[
                { label: "Guide", href: "/guide.html" },
                { label: "Blog", href: "/blog.html" },
                { label: "Tools", href: "/tools.html" },
              ].map(link => (
                <a key={link.label} href={link.href} style={{ color: COLORS.silver, textDecoration: "none", fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase" }}>{link.label}</a>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: "96px 28px 0", position: "relative" }}>
          <div style={{ marginBottom: "4px" }}>
            <span style={{
              fontSize: "10px", letterSpacing: "4px", color: COLORS.teal,
              textTransform: "uppercase", fontFamily: "'Rajdhani', sans-serif", opacity: 0.8,
            }}>POWERED BY ANIMA AI</span>
          </div>
          <div style={{
            fontSize: "56px", fontWeight: "900", letterSpacing: "10px",
            background: `linear-gradient(135deg, ${COLORS.teal} 0%, ${COLORS.gold} 50%, ${COLORS.teal} 100%)`,
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            fontFamily: "'Rajdhani', sans-serif",
            animation: "shimmer 4s linear infinite",
          }}>FORIMA</div>
          <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.silver, textTransform: "uppercase" }}>
            For Anima · For You
          </div>
        </div>
        <div style={{ padding: "32px 28px 0", position: "relative", flex: 1 }}>
          <div style={{
            fontSize: "46px", fontWeight: "900", letterSpacing: "1px",
            lineHeight: "1.05", color: COLORS.white,
            fontFamily: "'Rajdhani', sans-serif", textTransform: "uppercase",
            marginBottom: "16px",
          }}>
            Train Your<br />
            <span style={{ color: COLORS.teal }}>Body.</span><br />
            Own Your<br />
            <span style={{ color: COLORS.gold }}>Data.</span>
          </div>
          <div style={{
            fontSize: "16px", color: COLORS.silver, lineHeight: "1.7",
            letterSpacing: "0.3px", marginBottom: "28px", maxWidth: "340px",
          }}>
            ANIMA is your sovereign AI coach — personalized training, nutrition, and health optimization built on infrastructure no government can control.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "8px" }}>
            {[
              { icon: "⚡", title: "ANIMA COACHING", desc: "AI that knows your lifts, meals, and goals", gold: false },
              { icon: "🔒", title: "SOVEREIGN DATA", desc: "Your health data stays yours — always", gold: true },
              { icon: "💬", title: "TWO-WAY CONVERSATION", desc: "Ask ANIMA anything, anytime", gold: false },
            ].map((card, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: "14px",
                padding: "14px 16px",
                background: "linear-gradient(135deg, rgba(22,27,34,0.9), rgba(17,19,24,0.9))",
                border: `1px solid ${card.gold ? "rgba(212,160,23,0.3)" : "rgba(0,212,212,0.2)"}`,
                borderRadius: "14px",
                backdropFilter: "blur(10px)",
                boxShadow: "0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)",
              }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "10px", flexShrink: 0,
                  background: card.gold ? "rgba(212,160,23,0.12)" : "rgba(0,212,212,0.12)",
                  border: `1px solid ${card.gold ? "rgba(212,160,23,0.3)" : "rgba(0,212,212,0.3)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "18px",
        }}><span style={{ fontSize: "20px", lineHeight: 1 }}>{card.icon}</span></div>
                <div>
                  <div style={{
                    fontSize: "16px", fontWeight: "700", letterSpacing: "2px",
                    color: card.gold ? COLORS.gold : COLORS.teal,
                    fontFamily: "'Rajdhani', sans-serif",
                  }}>{card.title}</div>
                  <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "2px" }}>{card.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{
          padding: "24px 28px 48px",
          background: "linear-gradient(0deg, rgba(10,10,10,0.98) 0%, transparent 100%)",
        }}>
          <div style={{ display: "flex", gap: "24px", marginBottom: "24px", justifyContent: "center" }}>
            {[
              { value: "ANIMA", label: "AI Coach", gold: false },
              { value: "100%", label: "Private", gold: true },
              { value: "FREE", label: "To Start", gold: false },
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "20px", fontWeight: "900",
                  color: stat.gold ? COLORS.gold : COLORS.teal,
                  letterSpacing: "2px", fontFamily: "'Rajdhani', sans-serif",
                }}>{stat.value}</div>
                <div style={{ fontSize: "9px", color: COLORS.silver, letterSpacing: "2px", textTransform: "uppercase" }}>{stat.label}</div>
              </div>
            ))}
          </div>
          <button style={styles.btnPrimary} onClick={onGetStarted}>Start Training</button>
          <div style={{ height: "12px" }} />
          <button style={styles.btnOutline} onClick={onLogin}>I Have An Account</button>
          <div style={{ height: "32px" }} />
          <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap", paddingBottom: "8px" }}>
            {[
              { label: "Guide", href: "/guide.html" },
              { label: "Blog", href: "/blog.html" },
              { label: "Tools", href: "/tools.html" },
              { label: "1RM Calc", href: "https://1rm.forima.net" },
              { label: "Macros", href: "https://macros.forima.net" },
              { label: "Terms", href: "/terms.html" },
              { label: "Privacy", href: "/privacy.html" },
            ].map(link => (
              <a key={link.label} href={link.href}
                style={{ color: COLORS.silver, textDecoration: "none", fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", letterSpacing: "2px", fontWeight: "700", textTransform: "uppercase", opacity: 0.6 }}>
                {link.label}
              </a>
            ))}
          </div>
          <div style={{ textAlign: "center", fontSize: "16px", color: COLORS.silver, opacity: 0.3, paddingBottom: "16px", letterSpacing: "1px" }}>
            © 2026 FORIMA · Sovereign AI Fitness Coaching
          </div>
        </div>
      </div>
    </div>
  );
}
function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    age: "", weight: "", height: "",
    goal: "", dietary: "", water: "filtered",
    fitness_goal: "maintain", activity_level: "moderate", diet_approach: "standard",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleRegister = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          age: parseInt(form.age) || null,
          weight: parseFloat(form.weight) || null,
          height: parseFloat(form.height) || null,
          goal: selectedGoal ? `${selectedGoal} - ${form.goal}` : form.goal,
          fitness_goal: form.fitness_goal,
          activity_level: form.activity_level,
          diet_approach: form.diet_approach,
          experience_level: form.experience_level,
          phone: form.phone,
          sms_reminder_enabled: form.sms_reminder_enabled,
          sms_reminder_time: form.sms_reminder_time,
        }),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem("forima_token", data.token);
        localStorage.setItem("forima_user", JSON.stringify(data.user));
        onComplete(data.token, data.user);
      } else {
        setError(data.errors?.join(", ") || "Registration failed");
      }
    } catch (e) {
      setError("Connection error. Please try again.");
    }
    setLoading(false);
  };

  const steps = [
    // Step 0 — Goal Selection
    <div key="goal">
      <div style={styles.title}>Your Goal</div>
      <div style={styles.subtitle}>What brings you to FORIMA? ANIMA will personalize everything around this.</div>
      {GOALS.map((g) => (
        <div
          key={g.id}
          style={selectedGoal === g.id ? styles.goalCardSelected : styles.goalCard}
          onClick={() => setSelectedGoal(g.id)}
        >
          <span style={{ fontSize: "28px" }}>{g.icon}</span>
          <div>
            <div style={{ fontSize: "18px", fontWeight: "700", letterSpacing: "2px", color: COLORS.white }}>{g.label}</div>
            <div style={{ fontSize: "16px", color: COLORS.silver }}>{g.desc}</div>
          </div>
          {selectedGoal === g.id && (
            <span style={{ marginLeft: "auto", color: COLORS.teal, fontSize: "20px" }}>✓</span>
          )}
        </div>
      ))}
      <div style={{ height: "16px" }} />
      <button
        style={{ ...styles.btnPrimary, opacity: selectedGoal ? 1 : 0.4 }}
        disabled={!selectedGoal}
        onClick={() => setStep(1)}
      >
        Continue
      </button>
      <div style={{ height: "16px" }} />
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "16px", letterSpacing: "2px", color: COLORS.silver, textTransform: "uppercase", marginBottom: "12px", fontFamily: "'Rajdhani', sans-serif" }}>Activity Level</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          {[
            { id: "sedentary", label: "Sedentary", desc: "Desk job" },
            { id: "light", label: "Light Active", desc: "1-3 days/week" },
            { id: "moderate", label: "Moderate", desc: "Gym 3-5 days" },
            { id: "active_job", label: "Active Job", desc: "On feet all day" },
            { id: "very", label: "Very Active", desc: "6-7 days/week" },
            { id: "athlete", label: "Athlete", desc: "Twice daily" },
          ].map(a => (
            <button key={a.id} onClick={() => setForm({...form, activity_level: a.id})}
              style={{ background: form.activity_level === a.id ? "rgba(0,212,212,0.15)" : "transparent", border: "1px solid " + (form.activity_level === a.id ? COLORS.teal : COLORS.border), borderRadius: "10px", padding: "10px 8px", color: form.activity_level === a.id ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", cursor: "pointer", textAlign: "left", width: "100%" }}>
              <div>{a.label}</div>
              <div style={{ fontSize: "10px", opacity: 0.6, fontWeight: "400", marginTop: "2px" }}>{a.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "16px", letterSpacing: "2px", color: COLORS.silver, textTransform: "uppercase", marginBottom: "12px", fontFamily: "'Rajdhani', sans-serif" }}>Dietary Approach</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
          {[
            { id: "standard", label: "Standard" },
            { id: "lower_carb", label: "Lower Carb" },
            { id: "flexible_low_carb", label: "Flex Low Carb" },
            { id: "keto", label: "Keto" },
            { id: "carnivore", label: "Carnivore" },
            { id: "plant_based", label: "Plant Based" },
          ].map(d => (
            <button key={d.id} onClick={() => setForm({...form, diet_approach: d.id})}
              style={{ background: form.diet_approach === d.id ? "rgba(212,160,23,0.15)" : "transparent", border: "1px solid " + (form.diet_approach === d.id ? COLORS.gold : COLORS.border), borderRadius: "10px", padding: "10px 4px", color: form.diet_approach === d.id ? COLORS.gold : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", cursor: "pointer", textAlign: "center", width: "100%" }}>
              {d.label}
            </button>
          ))}
        </div>
      </div>
    </div>,

    // Step 1 — Personal Details
    <div key="details">
      <div style={styles.title}>About You</div>
      <div style={styles.subtitle}>ANIMA uses this to calculate your optimal training zones and nutrition targets.</div>
      <label style={styles.label}>Your Name</label>
      <input style={styles.input} placeholder="First name" value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
        <div>
          <label style={styles.label}>Age</label>
          <input style={{ ...styles.input, marginBottom: 0 }} placeholder="30" type="number"
            value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} />
        </div>
        <div>
          <label style={styles.label}>Weight (lbs)</label>
          <input style={{ ...styles.input, marginBottom: 0 }} placeholder="170" type="number"
            value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} />
        </div>
        <div>
          <label style={styles.label}>Height (in)</label>
          <input style={{ ...styles.input, marginBottom: 0 }} placeholder="68" type="number"
            value={form.height} onChange={e => setForm({ ...form, height: e.target.value })} />
        </div>
      </div>
      <label style={styles.label}>Specific Goal (optional)</label>
      <input style={styles.input} placeholder='e.g. "Run a 5K" or "Lose 20 lbs"'
        value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })} />
      <button
        style={{ ...styles.btnPrimary, opacity: form.name ? 1 : 0.4 }}
        disabled={!form.name}
        onClick={() => setStep(2)}
      >
        Continue
      </button>
    </div>,

    // Step 2 — Health Awareness
    <div key="health">
      <div style={styles.title}>Health Profile</div>
      <div style={styles.subtitle}>FORIMA goes beyond the gym. Tell ANIMA about your health awareness so coaching covers your whole life.</div>

      <label style={styles.label}>Dietary Preferences</label>
      <select style={{ ...styles.input, cursor: "pointer" }}
        value={form.dietary} onChange={e => setForm({ ...form, dietary: e.target.value })}>
        <option value="">No specific preferences</option>
        <option value="clean_eating">Clean eating — no seed oils, whole foods</option>
        <option value="carnivore">Carnivore / animal based</option>
        <option value="paleo">Paleo</option>
        <option value="keto">Ketogenic</option>
        <option value="plant_based">Plant based</option>
        <option value="mediterranean">Mediterranean</option>
        <option value="calorie_counting">Flexible — calorie counting</option>
      </select>

      <label style={styles.label}>Water Source</label>
      <select style={{ ...styles.input, cursor: "pointer" }}
        value={form.water} onChange={e => setForm({ ...form, water: e.target.value })}>
        <option value="filtered">Filtered (reverse osmosis or similar)</option>
        <option value="glass_bottled">Glass bottled water</option>
        <option value="tap">Tap water</option>
        <option value="plastic_bottled">Plastic bottled water</option>
      </select>

      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.teal}`,
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "16px",
      }}>
        <div style={{ fontSize: "16px", color: COLORS.teal, letterSpacing: "1px", marginBottom: "8px", fontWeight: "700" }}>
          💡 WHY THIS MATTERS
        </div>
        <div style={{ fontSize: "16px", color: COLORS.silver, lineHeight: "1.6" }}>
          ANIMA considers your full environment — not just your workouts. Seed oils, microplastics, and water quality all affect your performance and recovery.
        </div>
      </div>

      <button style={styles.btnPrimary} onClick={() => setStep(3)}>
        Continue
      </button>
    </div>,

    // Step 3 — Account Creation
    <div key="account">
      <div style={styles.title}>Create Account</div>
      <div style={styles.subtitle}>Your data stays yours. FORIMA never sells your information.</div>
      {error && <div style={styles.error}>{error}</div>}
      <label style={styles.label}>Email</label>
      <input style={styles.input} placeholder="you@email.com" type="email"
        value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
      <label style={styles.label}>Password</label>
      <input style={styles.input} placeholder="Min 6 characters" type="password"
        value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px', cursor: 'pointer' }}>
          <input
            type='checkbox'
            checked={tosAccepted}
            onChange={e => setTosAccepted(e.target.checked)}
            style={{ marginTop: '3px', accentColor: COLORS.teal, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '13px', color: COLORS.silver, lineHeight: '1.6' }}>
            I have read and agree to the{' '}
            <a href='https://forima.net/terms' target='_blank' rel='noreferrer'
              style={{ color: COLORS.teal, textDecoration: 'underline' }}>Terms of Service</a>.
          </span>
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
          <input
            type='checkbox'
            checked={privacyAccepted}
            onChange={e => setPrivacyAccepted(e.target.checked)}
            style={{ marginTop: '3px', accentColor: COLORS.teal, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '13px', color: COLORS.silver, lineHeight: '1.6' }}>
            I have read and agree to the{' '}
            <a href='https://forima.net/privacy' target='_blank' rel='noreferrer'
              style={{ color: COLORS.teal, textDecoration: 'underline' }}>Privacy Policy</a>{' '}
            and consent to FORIMA collecting my fitness data to power ANIMA coaching.
          </span>
        </label>
      </div>
      <button
        style={{ ...styles.btnGold, opacity: form.email && form.password && tosAccepted && privacyAccepted ? 1 : 0.4 }}
        onClick={handleRegister}
      >
        {loading ? 'Creating Account...' : 'Launch FORIMA'}
      </button>
    </div>,
  ];

  return (
    <div style={styles.screen}>
      <div style={{ ...styles.header, paddingBottom: "20px" }}>
        <div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div>
        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{
              height: "3px",
              flex: 1,
              borderRadius: "2px",
              background: i <= step ? COLORS.teal : COLORS.border,
              transition: "background 0.3s",
            }} />
          ))}
        </div>
      </div>
      <div style={styles.content}>
        {steps[step]}
        {step > 0 && (
          <button style={{ ...styles.btnOutline, marginTop: "12px" }} onClick={() => setStep(step - 1)}>
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}


function ResetPasswordScreen({ resetToken, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleReset = async () => {
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/password_resets`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, password }),
      });
      const data = await res.json();
      if (data.message) {
        setSuccess(true);
        window.history.replaceState({}, document.title, "/");
        setTimeout(() => onDone(), 2500);
      } else {
        setError(data.error || "Reset failed. Link may have expired.");
      }
    } catch(e) {
      setError("Connection error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div>
        <div style={styles.tagline}>Set New Password</div>
      </div>
      <div style={styles.content}>
        <div style={{ height: "24px" }} />
        <div style={styles.title}>New Password</div>
        <div style={styles.subtitle}>Choose a new password for your FORIMA account.</div>
        {success ? (
          <div style={{ background: "rgba(0,212,212,0.1)", border: `1px solid ${COLORS.teal}`, borderRadius: "12px", padding: "24px", textAlign: "center", marginTop: "16px" }}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>✓</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: COLORS.teal, letterSpacing: "2px", marginBottom: "8px" }}>PASSWORD UPDATED</div>
            <div style={{ fontSize: "13px", color: COLORS.silver }}>Redirecting you to sign in...</div>
          </div>
        ) : (
          <div>
            {error && <div style={styles.error}>{error}</div>}
            <label style={styles.label}>New Password</label>
            <input style={styles.input} placeholder="Min 6 characters" type="password"
              value={password} onChange={e => setPassword(e.target.value)} />
            <label style={styles.label}>Confirm Password</label>
            <input style={styles.input} placeholder="Confirm new password" type="password"
              value={confirm} onChange={e => setConfirm(e.target.value)} />
            <div style={{ height: "8px" }} />
            <button style={{ ...styles.btnPrimary, opacity: password && confirm ? 1 : 0.4 }}
              disabled={!password || !confirm || loading} onClick={handleReset}>
              {loading ? "Updating..." : "Set New Password"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, onBack }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem("forima_token", data.token);
        localStorage.setItem("forima_user", JSON.stringify(data.user));
        onLogin(data.token, data.user);
      } else {
        setError("Invalid email or password");
      }
    } catch (e) {
      setError("Connection error. Please try again.");
    }
    setLoading(false);
  };

  const handleForgot = async () => {
    setResetLoading(true);
    try {
      await fetch(`${API_URL}/password_resets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      setResetSent(true);
    } catch(e) {}
    setResetLoading(false);
  };

  if (forgotMode) {
    return (
      <div style={styles.screen}>
        <div style={styles.header}>
          <div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div>
          <div style={styles.tagline}>Reset Password</div>
        </div>
        <div style={styles.content}>
          <div style={{ height: "24px" }} />
          <div style={styles.title}>Forgot Password?</div>
          <div style={styles.subtitle}>Enter your email and we will send you a reset link.</div>
          {resetSent ? (
            <div style={{ background: "rgba(0,212,212,0.1)", border: `1px solid ${COLORS.teal}`, borderRadius: "12px", padding: "20px", textAlign: "center", marginTop: "16px" }}>
              <div style={{ fontSize: "24px", marginBottom: "8px" }}>✓</div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: COLORS.teal, letterSpacing: "2px", marginBottom: "8px" }}>CHECK YOUR EMAIL</div>
              <div style={{ fontSize: "13px", color: COLORS.silver }}>If that email is registered you will receive a reset link shortly.</div>
            </div>
          ) : (
            <div>
              <label style={styles.label}>Email</label>
              <input style={styles.input} placeholder="you@email.com" type="email"
                value={resetEmail} onChange={e => setResetEmail(e.target.value)} />
              <div style={{ height: "8px" }} />
              <button style={{ ...styles.btnPrimary, opacity: resetEmail ? 1 : 0.4 }}
                disabled={!resetEmail || resetLoading} onClick={handleForgot}>
                {resetLoading ? "Sending..." : "Send Reset Link"}
              </button>
            </div>
          )}
          <div style={{ height: "12px" }} />
          <button style={styles.btnOutline} onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(""); }}>← Back to Sign In</button>
        </div>
      </div>
    );
  }


  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div>
        <div style={styles.tagline}>Welcome back</div>
      </div>
      <div style={styles.content}>
        <div style={{ height: "24px" }} />
        <div style={styles.title}>Sign In</div>
        <div style={styles.subtitle}>ANIMA remembers your journey. Pick up where you left off.</div>
        {error && <div style={styles.error}>{error}</div>}
        <label style={styles.label}>Email</label>
        <input style={styles.input} placeholder="you@email.com" type="email"
          value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <label style={styles.label}>Password</label>
        <input style={styles.input} placeholder="Password" type="password"
          value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
        <div style={{ height: "8px" }} />
        <button style={styles.btnPrimary} onClick={handleLogin} disabled={loading}>
          {loading ? "Signing In..." : "Sign In"}
        </button>
        <div style={{ height: "12px" }} />
        <div style={{ textAlign: "center", marginTop: "4px" }}>
          <button onClick={() => setForgotMode(true)} style={{ background: "transparent", border: "none", color: COLORS.silver, fontSize: "13px", cursor: "pointer", fontFamily: "'Rajdhani', sans-serif", letterSpacing: "1px" }}>Forgot password?</button>
        </div>
        <div style={{ height: "8px" }} />
        <button style={styles.btnOutline} onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}


function WeightChart({ token, user }) {
  const [logs, setLogs] = useState([]);
  const [inputWeight, setInputWeight] = useState("");
  const [logging, setLogging] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/weight_logs`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setLogs(data);
    } catch(e) {}
  };

  const logWeight = async () => {
    if (!inputWeight) return;
    setLogging(true);
    const n = new Date();
    const today = n.getFullYear() + "-" + String(n.getMonth()+1).padStart(2,"0") + "-" + String(n.getDate()).padStart(2,"0");
    try {
      await fetch(`${API_URL}/weight_logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ weight: parseFloat(inputWeight), date: today })
      });
      setInputWeight("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      fetchLogs();
    } catch(e) {}
    setLogging(false);
  };

  const renderChart = () => {
    if (logs.length < 2) return (
      <div style={{ textAlign: "center", padding: "20px 0", color: COLORS.silver, fontSize: "13px", letterSpacing: "1px" }}>
        Log at least 2 entries to see your trend
      </div>
    );
    const W = 320, H = 120, PAD = 24;
    const weights = logs.map(l => parseFloat(l.weight));
    const minW = Math.min(...weights) - 2;
    const maxW = Math.max(...weights) + 2;
    const points = logs.map((l, i) => {
      const x = PAD + (i / (logs.length - 1)) * (W - PAD * 2);
      const y = PAD + (1 - (parseFloat(l.weight) - minW) / (maxW - minW)) * (H - PAD * 2);
      return { x, y, weight: l.weight, date: l.date };
    });
    const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    const areaD = `${pathD} L ${points[points.length-1].x} ${H} L ${points[0].x} ${H} Z`;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
        <defs>
          <linearGradient id="wgrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.teal} stopOpacity="0.3" />
            <stop offset="100%" stopColor={COLORS.teal} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#wgrad)" />
        <path d={pathD} fill="none" stroke={COLORS.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={COLORS.teal} />
        ))}
        <text x={points[0].x} y={H-4} fill={COLORS.silver} fontSize="9" textAnchor="middle">{logs[0]?.date?.slice(5)}</text>
        <text x={points[points.length-1].x} y={H-4} fill={COLORS.silver} fontSize="9" textAnchor="middle">{logs[logs.length-1]?.date?.slice(5)}</text>
        <text x={W - PAD + 4} y={points[points.length-1].y + 4} fill={COLORS.teal} fontSize="10" fontWeight="bold">{weights[weights.length-1]}lbs</text>
      </svg>
    );
  };

  const startWeight = logs.length > 0 ? parseFloat(logs[0].weight) : null;
  const currentWeight = logs.length > 0 ? parseFloat(logs[logs.length-1].weight) : null;
  const diff = startWeight && currentWeight ? (currentWeight - startWeight).toFixed(1) : null;

  return (
    <div style={{ background: COLORS.card, border: "1px solid rgba(0,212,212,0.2)", borderRadius: "16px", padding: "20px", marginBottom: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "3px", color: COLORS.white }}>WEIGHT PROGRESS</div>
        {diff !== null && (
          <div style={{ fontSize: "13px", fontWeight: "700", color: parseFloat(diff) <= 0 ? COLORS.teal : COLORS.gold, letterSpacing: "1px" }}>
            {parseFloat(diff) > 0 ? "+" : ""}{diff} lbs
          </div>
        )}
      </div>
      {renderChart()}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <input
          type="number"
          placeholder="Today's weight (lbs)"
          value={inputWeight}
          onChange={e => setInputWeight(e.target.value)}
          style={{ flex: 1, background: COLORS.charcoal, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "12px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none" }}
        />
        <button
          onClick={logWeight}
          disabled={logging || !inputWeight}
          style={{ ...styles.btnPrimary, padding: "12px 20px", opacity: logging || !inputWeight ? 0.5 : 1 }}
        >
          {success ? "✓" : logging ? "..." : "LOG"}
        </button>
      </div>
    </div>
  );
}

function DailyCheckinCard({ token }) {
  const EMOJI_ENERGY = ["😴","😐","🙂","😀","⚡"];
  const EMOJI_SLEEP  = ["😩","😪","😐","😴","🌙"];
  const EMOJI_SORE   = ["🔥🔥","🔥","😐","🟢","✅"];

  const [checkin, setCheckin] = useState(null);
  const [streak, setStreak] = useState(0);
  const [energy, setEnergy] = useState(null);
  const [sleep, setSleep] = useState(null);
  const [soreness, setSoreness] = useState(null);
  const [logging, setLogging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [milestoneMsg, setMilestoneMsg] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ciRes, stRes] = await Promise.all([
          fetch(`${API_URL}/daily_checkins/today?date=${getLocalDate()}`, { headers: { "Authorization": `Bearer ${token}` } }),
          fetch(`${API_URL}/daily_checkins/streak?date=${getLocalDate()}`, { headers: { "Authorization": `Bearer ${token}` } }),
        ]);
        const ciData = await ciRes.json();
        const stData = await stRes.json();
        if (ciData.checkin) setCheckin(ciData.checkin);
        setStreak(stData.streak || 0);
      } catch(e) {}
      setLoaded(true);
    };
    fetchData();
  }, []);

  const submit = async () => {
    if (energy === null || sleep === null || soreness === null) return;
    setLogging(true);
    try {
      const res = await fetch(`${API_URL}/daily_checkins`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ energy: energy + 1, sleep_quality: sleep + 1, soreness: soreness + 1, date: getLocalDate() })
      });
      const data = await res.json();
      if (data.checkin) {
        setCheckin(data.checkin);
        const newStreak = streak + 1;
        setStreak(newStreak);
        setCelebrating(true);
        setTimeout(() => setCelebrating(false), 900);
        if (newStreak === 7) setMilestoneMsg("🔥 7 DAY STREAK! LEGENDARY!");
        else if (newStreak === 30) setMilestoneMsg("⚡ 30 DAYS. YOU ARE UNSTOPPABLE.");
        else if (newStreak === 3) setMilestoneMsg("🔥 3 DAY STREAK. KEEP GOING.");
        else setMilestoneMsg("");
      }
    } catch(e) {}
    setLogging(false);
  };

  if (!loaded) return null;

  const EmojiRow = ({ label, emojis, value, setValue }) => (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ fontSize: "11px", letterSpacing: "2px", color: COLORS.silver, marginBottom: "8px" }}>{label}</div>
      <div style={{ display: "flex", gap: "8px" }}>
        {emojis.map((e, i) => (
          <button key={i} onClick={() => setValue(i)} style={{ flex: 1, padding: "10px 4px", borderRadius: "10px", border: `1px solid ${value === i ? COLORS.teal : COLORS.border}`, background: value === i ? "rgba(0,212,212,0.1)" : COLORS.charcoal, fontSize: "20px", cursor: "pointer", transition: "all 0.15s" }}>
            {e}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${checkin ? COLORS.teal : COLORS.border}`, borderRadius: "16px", padding: "20px", marginBottom: "20px" }}>
      <CelebrationBurst show={celebrating} />
      {milestoneMsg ? (
        <div style={{ textAlign: "center", fontSize: "16px", fontWeight: "900", letterSpacing: "2px", color: COLORS.gold, marginBottom: "12px", padding: "10px", background: "rgba(212,160,23,0.1)", borderRadius: "10px" }}>{milestoneMsg}</div>
      ) : null}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "3px", color: COLORS.white }}>DAILY CHECK-IN</div>
        {streak > 0 && (
          <div style={{ fontSize: "14px", fontWeight: "700", color: COLORS.gold, letterSpacing: "1px" }}>🔥 {streak} DAY STREAK</div>
        )}
      </div>

      {checkin ? (
        <div>
          <div style={{ fontSize: "13px", color: COLORS.teal, letterSpacing: "2px", marginBottom: "12px" }}>✓ CHECKED IN TODAY</div>
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "24px" }}>{EMOJI_ENERGY[(checkin.energy || 1) - 1]}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px", marginTop: "4px" }}>ENERGY</div>
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "24px" }}>{EMOJI_SLEEP[(checkin.sleep_quality || 1) - 1]}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px", marginTop: "4px" }}>SLEEP</div>
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "24px" }}>{EMOJI_SORE[(checkin.soreness || 1) - 1]}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px", marginTop: "4px" }}>SORENESS</div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <EmojiRow label="ENERGY" emojis={EMOJI_ENERGY} value={energy} setValue={setEnergy} />
          <EmojiRow label="SLEEP QUALITY" emojis={EMOJI_SLEEP} value={sleep} setValue={setSleep} />
          <EmojiRow label="SORENESS" emojis={EMOJI_SORE} value={soreness} setValue={setSoreness} />
          <button
            onClick={submit}
            disabled={logging || energy === null || sleep === null || soreness === null}
            style={{ ...styles.btnPrimary, width: "100%", marginTop: "4px", opacity: (logging || energy === null || sleep === null || soreness === null) ? 0.4 : 1 }}
          >
            {logging ? "LOGGING..." : "CHECK IN"}
          </button>
        </div>
      )}
    </div>
  );
}

function SwipeableCard({ panels, dotColor }) {
  const [index, setIndex] = useState(0);
  const startX = useRef(null);

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current === null) return;
    const diff = startX.current - e.changedTouches[0].clientX;
    if (diff > 50 && index < panels.length - 1) setIndex(i => i + 1);
    if (diff < -50 && index > 0) setIndex(i => i - 1);
    startX.current = null;
  };

  return (
    <div style={{ ...styles.card, marginBottom: "16px", border: "1px solid rgba(0,212,212,0.25)" }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div style={{ overflow: "hidden", minHeight: "0" }}>
        <div style={{ display: "flex", transition: "transform 0.3s ease", transform: `translateX(-${index * 100}%)` }}>
          {panels.map((panel, i) => (
            <div key={i} style={{ minWidth: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", maxHeight: "280px" }}>
              {panel}
            </div>
          ))}
        </div>
      </div>
      {panels.length > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginTop: "12px" }}>
          {panels.map((_, i) => (
            <div key={i} onClick={() => setIndex(i)} style={{ width: i === index ? "16px" : "6px", height: "6px", borderRadius: "3px", background: i === index ? (dotColor || COLORS.teal) : COLORS.border, transition: "all 0.2s", cursor: "pointer" }} />
          ))}
        </div>
      )}
    </div>
  );
}

function DashboardScreen({ user, token, workouts, meals, onRefresh, onTabChange }) {
  const totalWorkouts = workouts.length;

  // Today local date
  const now = new Date();
  const today = now.getFullYear() + "-" + String(now.getMonth()+1).padStart(2,"0") + "-" + String(now.getDate()).padStart(2,"0");

  // This week's workouts (Mon-Sun)
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
  weekStart.setHours(0,0,0,0);
  const workoutsThisWeek = new Set(workouts.filter(w => new Date(w.date + "T12:00:00") >= weekStart).map(w => w.date)).size;

  // Today's meals
  const todayMeals = meals.filter(m => m.date === today);
  const totals = todayMeals.reduce((acc, m) => ({
    calories: acc.calories + (m.calories || 0),
    protein: acc.protein + (m.protein || 0),
    carbs: acc.carbs + (m.carbs || 0),
    fat: acc.fat + (m.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  // Macro targets from profile
  const weightLbs = user?.weight || 180;
  const fitnessGoal = user?.fitness_goal || "maintain";
  const activityLevel = user?.activity_level || "moderate";
  const proteinMult = fitnessGoal === "aggressive_cut" ? 1.2 : fitnessGoal === "cut" ? 1.1 : 1.0;
  const proteinTarget = Math.round(weightLbs * proteinMult);
  const actMult = activityLevel === "sedentary" ? 1.2 : activityLevel === "light" ? 1.375 : activityLevel === "moderate" ? 1.55 : activityLevel === "active_job" ? 1.65 : activityLevel === "very" ? 1.725 : 1.9;
  const heightCm = (user?.height || 70) * 2.54;
  const weightKg = weightLbs * 0.453592;
  const age = user?.age || 30;
  const bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5;
  const tdee = Math.round(bmr * actMult);
  const calAdj = fitnessGoal === "aggressive_cut" ? -500 : fitnessGoal === "cut" ? -250 : fitnessGoal === "lean_bulk" ? 250 : fitnessGoal === "bulk" ? 500 : 0;
  const calorieTarget = tdee + calAdj;
  const carbsTarget = 150;
  const fatTarget = 80;

  // ANIMA preview from cache
  const animaCache = localStorage.getItem("anima_analysis");
  const animaPreview = animaCache ? animaCache.replace(/#{1,3} /g, "").replace(/\*\*(.*?)\*\*/g, "$1").split("\n").filter(l => l.trim().length > 20)[0] : null;

  // SVG ring component
  const MacroRing = ({ value, target, color, label, unit, size }) => {
    const pct = target ? Math.min(100, (value / target) * 100) : 50;
    const r = 28;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    const sz = size || 72;
    return (
      <div style={{ textAlign: "center", flex: 1 }}>
        <svg width={sz} height={sz} viewBox="0 0 72 72">
          <circle cx="36" cy="36" r={r} fill="none" stroke={COLORS.border} strokeWidth="5" />
          <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="5"
            strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ * 0.25}
            strokeLinecap="round" style={{ transition: "stroke-dasharray 0.5s ease" }} />
          <text x="36" y="36" textAnchor="middle" dominantBaseline="middle"
            fill={color} fontSize="12" fontWeight="900" fontFamily="Rajdhani, sans-serif">
            {Math.round(value)}
          </text>
          <text x="36" y="48" textAnchor="middle" fill={COLORS.silver} fontSize="7" fontFamily="Rajdhani, sans-serif">
            {unit || ""}
          </text>
        </svg>
        <div style={{ fontSize: "9px", letterSpacing: "1px", color: COLORS.silver, textTransform: "uppercase", marginTop: "2px" }}>{label}</div>
        {target && <div style={{ fontSize: "9px", color, opacity: 0.6 }}>/ {target}{unit}</div>}
      </div>
    );
  };

  return (
    <div style={{ ...styles.screen, paddingBottom: "100px" }}>
      <div style={{ padding: "60px 24px 24px", background: `linear-gradient(180deg, ${COLORS.charcoal} 0%, ${COLORS.black} 100%)`, borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase" }}>
              Good {now.getHours() < 12 ? "Morning" : now.getHours() < 17 ? "Afternoon" : "Evening"}
            </div>
            <div style={{ fontSize: "32px", fontWeight: "900", letterSpacing: "3px", color: COLORS.white, fontFamily: "'Rajdhani', sans-serif" }}>
              {user?.name?.toUpperCase() || "ATHLETE"}
            </div>
            <div style={{ fontSize: "16px", color: COLORS.teal, letterSpacing: "1px", textTransform: "capitalize" }}>
              {fitnessGoal.replace("_", " ")} · {activityLevel.replace("_", " ")} activity
            </div>
          </div>
          <div onClick={() => onTabChange && onTabChange("profile")} style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", flexShrink: 0, marginTop: "4px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "rgba(0,212,212,0.15)", border: `2px solid ${COLORS.teal}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: "900", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif" }}>
              {user?.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div style={{ fontSize: "8px", color: COLORS.teal, letterSpacing: "1px", marginTop: "3px" }}>PROFILE</div>
          </div>
        </div>
      </div>

      <div style={styles.content}>

        {/* Daily Check-in */}
        <DailyCheckinCard token={token} />

        {/* Swipeable Nutrition Card */}
        {(() => {
          // Build 7-day data
          const weekDays = [0,1,2,3,4,5,6].map(d => {
            const dd = new Date(weekStart);
            dd.setDate(weekStart.getDate() + d);
            const ds = dd.getFullYear() + "-" + String(dd.getMonth()+1).padStart(2,"0") + "-" + String(dd.getDate()).padStart(2,"0");
            const dayMeals = meals.filter(m => m.date === ds);
            return {
              label: ["M","T","W","T","F","S","S"][d],
              date: ds,
              calories: dayMeals.reduce((a,m) => a + (m.calories||0), 0),
              protein: dayMeals.reduce((a,m) => a + (m.protein||0), 0),
              carbs: dayMeals.reduce((a,m) => a + (m.carbs||0), 0),
              fat: dayMeals.reduce((a,m) => a + (m.fat||0), 0),
              isToday: ds === today,
            };
          });

          const maxCal = Math.max(...weekDays.map(d => d.calories), calorieTarget, 1);
          const maxPro = Math.max(...weekDays.map(d => d.protein), proteinTarget, 1);
          const maxCarbs = Math.max(...weekDays.map(d => d.carbs), carbsTarget, 1);
          const maxFat = Math.max(...weekDays.map(d => d.fat), fatTarget, 1);

          const WeekBar = ({ value, max, color, isToday }) => (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "60px", justifyContent: "flex-end" }}>
              <div style={{ width: "100%", maxWidth: "18px", height: `${Math.max(4, (value/max)*56)}px`, background: isToday ? color : `${color}88`, borderRadius: "3px 3px 0 0", transition: "height 0.3s" }} />
            </div>
          );

          const todayPanel = (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "16px" }}>⚡ TODAY'S NUTRITION</div>
              {todayMeals.length === 0 ? (
                <div style={{ textAlign: "center", padding: "12px 0", color: COLORS.silver, fontSize: "16px" }}>
                  No meals logged today yet.
                  <button onClick={() => onTabChange && onTabChange("nutrition")} style={{ display: "block", margin: "12px auto 0", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px 20px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", letterSpacing: "2px", cursor: "pointer" }}>+ LOG MEAL</button>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "4px", padding: "4px 0" }}>
                  <MacroRing value={totals.calories} target={calorieTarget} color={COLORS.gold} label="CAL" unit="" size={76} />
                  <MacroRing value={totals.protein} target={proteinTarget} color={COLORS.teal} label="PROTEIN" unit="g" size={76} />
                  <MacroRing value={totals.carbs} target={carbsTarget} color="#9B59B6" label="CARBS" unit="g" size={76} />
                  <MacroRing value={totals.fat} target={fatTarget} color="#E67E22" label="FAT" unit="g" size={76} />
                </div>
              )}
            </div>
          );

          const weekPanel = (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "12px" }}>📊 THIS WEEK</div>
              <div style={{ display: "flex", gap: "2px", marginBottom: "6px" }}>
                <div style={{ width: "44px" }} />
                {weekDays.map((d, i) => (
                  <div key={i} style={{ flex: 1, textAlign: "center", fontSize: "11px", fontWeight: d.isToday ? "900" : "400", color: d.isToday ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif" }}>{d.label}</div>
                ))}
              </div>
              {[
                { label: "CAL", color: COLORS.gold, key: "calories", unit: "" },
                { label: "PRO", color: COLORS.teal, key: "protein", unit: "g" },
                { label: "CARBS", color: "#9B59B6", key: "carbs", unit: "g" },
                { label: "FAT", color: "#E67E22", key: "fat", unit: "g" },
              ].map(({ label, color, key, unit }) => (
                <div key={key} style={{ display: "flex", gap: "2px", alignItems: "center", marginBottom: "8px" }}>
                  <div style={{ width: "44px", fontSize: "9px", color: color, letterSpacing: "1px", fontWeight: "700" }}>{label}</div>
                  {weekDays.map((d, i) => (
                    <div key={i} style={{ flex: 1, textAlign: "center", fontSize: "10px", fontWeight: d.isToday ? "900" : "400", color: d[key] > 0 ? (d.isToday ? color : COLORS.white) : COLORS.border, fontFamily: "'Rajdhani', sans-serif", background: d[key] > 0 ? (d.isToday ? `${color}22` : "transparent") : "transparent", borderRadius: "4px", padding: "2px 0" }}>
                      {d[key] > 0 ? `${Math.round(d[key])}${unit}` : "—"}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );

          const trendsPanel = (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "12px" }}>📈 7-DAY TOTALS</div>
              {[
                { label: "AVG CALORIES/DAY", value: Math.round(weekDays.reduce((a,d) => a + d.calories, 0) / 7), target: calorieTarget, color: COLORS.gold, unit: "" },
                { label: "AVG PROTEIN/DAY", value: Math.round(weekDays.reduce((a,d) => a + d.protein, 0) / 7), target: proteinTarget, color: COLORS.teal, unit: "g" },
                { label: "AVG CARBS/DAY", value: Math.round(weekDays.reduce((a,d) => a + d.carbs, 0) / 7), target: carbsTarget, color: "#9B59B6", unit: "g" },
                { label: "AVG FAT/DAY", value: Math.round(weekDays.reduce((a,d) => a + d.fat, 0) / 7), target: fatTarget, color: "#E67E22", unit: "g" },
              ].map(({ label, value, target, color, unit }) => (
                <div key={label} style={{ marginBottom: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                    <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px" }}>{label}</div>
                    <div style={{ fontSize: "12px", fontWeight: "900", color: value >= target * 0.8 ? color : COLORS.silver }}>{value}{unit} <span style={{ fontSize: "9px", opacity: 0.6 }}>/ {target}{unit}</span></div>
                  </div>
                  <div style={{ height: "3px", background: COLORS.border, borderRadius: "2px" }}>
                    <div style={{ height: "100%", width: `${Math.min(100, (value/target)*100)}%`, background: color, borderRadius: "2px", transition: "width 0.4s" }} />
                  </div>
                </div>
              ))}
            </div>
          );

          return <SwipeableCard panels={[todayPanel, weekPanel, trendsPanel]} dotColor={COLORS.teal} />;
        })()}

        {/* Swipeable Training Card */}
        {(() => {
          // PRs - best weight per exercise
          const prMap = {};
          workouts.forEach(w => {
            const key = w.exercise.toLowerCase().trim();
            if (!prMap[key] || w.weight > prMap[key].weight) prMap[key] = { name: w.exercise, weight: w.weight };
          });
          const topPRs = Object.values(prMap).sort((a,b) => b.weight-a.weight).slice(0, 5);

          // Weekly volume
          const weekWorkouts = workouts.filter(w => {
            const d = new Date(w.date + "T12:00:00");
            return d >= weekStart;
          });
          const totalSets = weekWorkouts.reduce((a,w) => a + (w.sets||0), 0);
          const totalReps = weekWorkouts.reduce((a,w) => a + ((w.sets||0)*(w.reps||0)), 0);
          const totalVolume = weekWorkouts.reduce((a,w) => a + ((w.sets||0)*(w.reps||0)*(w.weight||0)), 0);

          const weekDotPanel = (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.gold, textTransform: "uppercase", marginBottom: "16px" }}>📅 THIS WEEK</div>
              <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "12px" }}>
                {[0,1,2,3,4,5,6].map(d => {
                  const dayDate = new Date(weekStart);
                  dayDate.setDate(weekStart.getDate() + d);
                  const dayStr = dayDate.getFullYear() + "-" + String(dayDate.getMonth()+1).padStart(2,"0") + "-" + String(dayDate.getDate()).padStart(2,"0");
                  const hasWorkout = workouts.some(w => w.date === dayStr);
                  const isTodayD = dayStr === today;
                  return (
                    <div key={d} style={{ textAlign: "center", flex: 1 }}>
                      <div style={{ width: "32px", height: "32px", borderRadius: "50%", margin: "0 auto 4px", background: hasWorkout ? COLORS.teal : "transparent", border: `2px solid ${isTodayD ? COLORS.gold : hasWorkout ? COLORS.teal : COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {hasWorkout && <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: COLORS.black }} />}
                      </div>
                      <div style={{ fontSize: "13px", color: isTodayD ? COLORS.gold : COLORS.silver, fontWeight: isTodayD ? "900" : "400", fontFamily: "'Rajdhani', sans-serif" }}>{["M","T","W","T","F","S","S"][d]}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ textAlign: "center", fontSize: "18px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", letterSpacing: "1px", marginTop: "8px" }}>
              <div style={{ textAlign: "center", fontSize: "16px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", letterSpacing: "1px", marginTop: "8px" }}><span style={{ color: COLORS.teal, fontWeight: "900", fontSize: "24px" }}>{workoutsThisWeek}</span> of 7 days trained this week</div>
              </div>
            </div>
          );

          const volumePanel = (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.gold, textTransform: "uppercase", marginBottom: "16px" }}>💪 WEEK VOLUME</div>
              {[
                { label: "TOTAL SETS", value: totalSets, color: COLORS.teal },
                { label: "TOTAL REPS", value: totalReps, color: COLORS.gold },
                { label: "TOTAL VOLUME", value: `${Math.round(totalVolume).toLocaleString()}lbs`, color: "#9B59B6" },
                { label: "SESSIONS", value: weekWorkouts.length > 0 ? new Set(weekWorkouts.map(w => w.date)).size : 0, color: COLORS.teal },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: "11px", letterSpacing: "2px", color: COLORS.silver }}>{label}</div>
                  <div style={{ fontSize: "20px", fontWeight: "900", color, fontFamily: "'Rajdhani', sans-serif" }}>{value}</div>
                </div>
              ))}
            </div>
          );

          const prPanel = (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.gold, textTransform: "uppercase", marginBottom: "16px" }}>🏆 YOUR PRs</div>
              {topPRs.length === 0 ? (
                <div style={{ textAlign: "center", color: COLORS.silver, fontSize: "14px", padding: "20px 0" }}>Log workouts to track your PRs</div>
              ) : topPRs.map((pr) => (
                <div key={pr.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: "14px", letterSpacing: "1px", color: COLORS.white, fontWeight: "700" }}>{pr.name.toUpperCase()}</div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: COLORS.gold, fontFamily: "'Rajdhani', sans-serif" }}>{pr.weight}lbs</div>
                </div>
              ))}
            </div>
          );

          return <SwipeableCard panels={[weekDotPanel, volumePanel, prPanel]} dotColor={COLORS.gold} />;
        })()}

        {/* ANIMA Preview */}
        <div style={{ ...styles.cardTeal, marginBottom: "16px" }}>
          <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "12px" }}>
            ⚡ ANIMA
          </div>
          {animaPreview ? (
            <div>
              <div style={{ fontSize: "16px", color: COLORS.silverBright, lineHeight: "1.7", marginBottom: "12px" }}>
                {animaPreview.slice(0, 120)}{animaPreview.length > 120 ? "..." : ""}
              </div>
              <button onClick={() => onTabChange && onTabChange("coach")} style={{ background: "transparent", border: `1px solid ${COLORS.teal}`, borderRadius: "8px", padding: "8px 16px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", letterSpacing: "2px", cursor: "pointer" }}>
                READ FULL ANALYSIS →
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: "16px", color: COLORS.silver, lineHeight: "1.6", marginBottom: "12px" }}>
                {totalWorkouts === 0 ? "Log your first workout and ANIMA will build your personalized program." : "Get your personalized coaching analysis from ANIMA."}
              </div>
              <button onClick={() => onTabChange && onTabChange("coach")} style={{ background: "transparent", border: `1px solid ${COLORS.teal}`, borderRadius: "8px", padding: "8px 16px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", letterSpacing: "2px", cursor: "pointer" }}>
                {totalWorkouts === 0 ? "GET STARTED →" : "GET ANALYSIS →"}
              </button>
            </div>
          )}
        </div>

        {/* Weight Chart */}
        <WeightChart token={token} user={user} />

        {/* Quick Actions */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <button onClick={() => onTabChange && onTabChange("train")} style={{ background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.tealDim})`, border: "none", borderRadius: "14px", padding: "14px 12px", color: COLORS.black, fontFamily: "'Rajdhani', sans-serif", fontSize: "15px", fontWeight: "900", letterSpacing: "2px", cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: "20px", marginBottom: "4px" }}>🏋️</div>
            LOG WORKOUT
          </button>
          <button onClick={() => onTabChange && onTabChange("nutrition")} style={{ background: `linear-gradient(135deg, ${COLORS.gold}, #E8B84B)`, border: "none", borderRadius: "14px", padding: "14px 12px", color: COLORS.black, fontFamily: "'Rajdhani', sans-serif", fontSize: "15px", fontWeight: "900", letterSpacing: "2px", cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: "20px", marginBottom: "4px" }}>🍽️</div>
            LOG MEAL
          </button>
        </div>

      </div>
    </div>
  );
}

function LogWorkoutScreen({ token, user, onLogged, defaultMode, hideHeader }) {
  const [mode, setMode] = useState(defaultMode || "workout");
  const [celebrating, setCelebrating] = useState(false);

  // Workout state
  const [category, setCategory] = useState("");
  const [exercise, setExercise] = useState("");
  const [otherExercise, setOtherExercise] = useState("");
  const [sets, setSets] = useState("");
  const [reps, setReps] = useState("");
  const [weight, setWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState((() => { const n = new Date(); return n.getFullYear() + '-' + String(n.getMonth()+1).padStart(2,'0') + '-' + String(n.getDate()).padStart(2,'0'); })());
  const [session, setSession] = useState([]);
  const [loggingSession, setLoggingSession] = useState(false);
  const [sessionSuccess, setSessionSuccess] = useState(false);

  // Meal state
  const [mealType, setMealType] = useState("");
  const [mealDesc, setMealDesc] = useState("");
  const [mealCalories, setMealCalories] = useState("");
  const [mealProtein, setMealProtein] = useState("");
  const [mealCarbs, setMealCarbs] = useState("");
  const [mealFat, setMealFat] = useState("");
  const [mealSession, setMealSession] = useState([]);
  const [loggingMeals, setLoggingMeals] = useState(false);
  const [mealSuccess, setMealSuccess] = useState(false);

  const [error, setError] = useState("");
  // Voice logging state
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceParsed, setVoiceParsed] = useState(null);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceLogging, setVoiceLogging] = useState(false);
  const [voiceSuccess, setVoiceSuccess] = useState(false);
  const [voiceDate, setVoiceDate] = useState(() => { const n = new Date(); return n.getFullYear() + "-" + String(n.getMonth()+1).padStart(2,"0") + "-" + String(n.getDate()).padStart(2,"0"); });

  const EXERCISES = {
    "Chest": [
      "Bench Press","Incline Bench Press","Decline Bench Press","Close-Grip Bench Press",
      "Dumbbell Flyes","Cable Flyes","Chest Dips","Pec Deck","Push-Ups","Incline Dumbbell Press","Other"
    ],
    "Back": [
      "Deadlift","Barbell Rows","Dumbbell Rows","Cable Rows","Lat Pulldown","Pull-Ups","Chin-Ups",
      "T-Bar Rows","Face Pulls","Rack Pulls","Hyperextensions","Seated Cable Row","Other"
    ],
    "Legs": [
      "Squat","Front Squat","Leg Press","Romanian Deadlift","Leg Curl","Leg Extension",
      "Walking Lunges","Bulgarian Split Squat","Hack Squat","Belt Squat","Calf Raises",
      "Hip Thrust","Sumo Deadlift","Step-Ups","Other"
    ],
    "Shoulders": [
      "Overhead Press","Dumbbell Shoulder Press","Arnold Press","Lateral Raises","Front Raises",
      "Rear Delt Flyes","Upright Rows","Shrugs","Cable Lateral Raises","Other"
    ],
    "Arms": [
      "Barbell Curls","Dumbbell Curls","Hammer Curls","Preacher Curls","Cable Curls",
      "Concentration Curls","Tricep Pushdown","Skull Crushers","Overhead Tricep Extension",
      "Dips","Tricep Kickbacks","Other"
    ],
    "Core / Abs": [
      "Plank","Crunches","Sit-Ups","Leg Raises","Cable Crunches","Ab Wheel Rollout",
      "Russian Twists","Hanging Leg Raises","Side Plank","Decline Sit-Ups","Other"
    ],
    "Cardio": [
      "Running","Cycling","Rowing Machine","Jump Rope","Stair Climber","Elliptical",
      "Swimming","Battle Ropes","Box Jumps","Sled Push","Farmers Carry",
      "Kettlebell Swings","Assault Bike","Sprints","Other"
    ],
    "Olympic / Power": [
      "Clean and Jerk","Snatch","Power Clean","Power Snatch","Push Press",
      "Push Jerk","Hang Clean","Hang Snatch","Other"
    ],
  };

  const MEAL_TYPES = [
    "Breakfast","Lunch","Dinner","Snack","Pre-Workout","Post-Workout"
  ];

  const exerciseName = exercise === "Other" ? otherExercise : exercise;
  const canAddExercise = category && exercise && (exercise !== "Other" || otherExercise) && sets && reps;

  const addToSession = () => {
    setSession([...session, {
      exercise: exerciseName,
      sets: parseInt(sets),
      reps: parseInt(reps),
      weight: parseFloat(weight) || 0,
      notes,
    }]);
    setExercise("");
    setOtherExercise("");
    setSets("");
    setReps("");
    setWeight("");
    setNotes("");
  };

  const removeFromSession = (idx) => {
    setSession(session.filter((_, i) => i !== idx));
  };

  const logSession = async () => {
    if (session.length === 0) return;
    setLoggingSession(true);
    setError("");
    try {
      for (const ex of session) {
        await fetch(`${API_URL}/workouts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ ...ex, date }),
        });
      }
      localStorage.removeItem("anima_analysis");
      setCelebrating(true);
      setTimeout(() => setCelebrating(false), 900);
      setSession([]);
      setCategory("");
      setDate((() => { const n = new Date(); return n.getFullYear() + '-' + String(n.getMonth()+1).padStart(2,'0') + '-' + String(n.getDate()).padStart(2,'0'); })());
      setSessionSuccess(true);
      onLogged();
      setTimeout(() => setSessionSuccess(false), 3000);
    } catch (e) {
      setError("Connection error");
    }
    setLoggingSession(false);
  };

  const addToMealSession = () => {
    setMealSession([...mealSession, {
      meal_type: mealType,
      description: mealDesc,
      calories: parseInt(mealCalories) || null,
      protein: parseFloat(mealProtein) || null,
      carbs: parseFloat(mealCarbs) || null,
      fat: parseFloat(mealFat) || null,
      date,
    }]);
    setMealType("");
    setMealDesc("");
    setMealCalories("");
    setMealProtein("");
    setMealCarbs("");
    setMealFat("");
  };

  const removeFromMealSession = (idx) => {
    setMealSession(mealSession.filter((_, i) => i !== idx));
  };

  const logMealSession = async () => {
    if (mealSession.length === 0) return;
    setLoggingMeals(true);
    setError("");
    try {
      for (const meal of mealSession) {
        await fetch(`${API_URL}/meals`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(meal),
        });
      }
      localStorage.removeItem("anima_analysis");
      setCelebrating(true);
      setTimeout(() => setCelebrating(false), 900);
      setMealSession([]);
      setMealSuccess(true);
      setTimeout(() => setMealSuccess(false), 3000);
    } catch (e) {
      setError("Connection error");
    }
    setLoggingMeals(false);
  };


  const startVoiceLog = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice input not supported in this browser. Try Chrome.');
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    setVoiceListening(true);
    setVoiceTranscript("");
    setVoiceParsed(null);
    recognition.start();
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setVoiceTranscript(transcript);
      setVoiceListening(false);
      parseVoiceInput(transcript);
    };
    recognition.onerror = () => setVoiceListening(false);
    recognition.onend = () => setVoiceListening(false);
  };

  const parseVoiceInput = async (transcript) => {
    setVoiceParsing(true);
    const isPremium = user?.premium;
    const now = new Date(); const today = now.getFullYear() + '-' + String(now.getMonth()+1).padStart(2,'0') + '-' + String(now.getDate()).padStart(2,'0');

    if (isPremium) {
      try {
        const res = await fetch(API_URL + '/workouts/parse_voice', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcript }),
        });
        const parsed = await res.json();
        if (parsed.type) {
          const localDate = (() => { const n = new Date(); return n.getFullYear() + "-" + String(n.getMonth()+1).padStart(2,"0") + "-" + String(n.getDate()).padStart(2,"0"); })();
          const fixedParsed = { ...parsed, entries: parsed.entries.map(e => ({ ...e, date: localDate })) };
          setVoiceDate(localDate);
          setVoiceParsed(fixedParsed);
        } else {
          fallbackParse(transcript, today);
        }
      } catch(e) {
        console.log('Parse error:', e);
        fallbackParse(transcript, today);
      }
    } else {
      fallbackParse(transcript, today);
    }
    setVoiceParsing(false);
  };

  const fallbackParse = (transcript, today) => {
    // Convert word numbers to digits
    const wordNums = {'one':'1','two':'2','three':'3','four':'4','five':'5','six':'6','seven':'7','eight':'8','nine':'9','ten':'10','twelve':'12','fifteen':'15','twenty':'20','twenty-five':'25','thirty':'30','forty':'40','forty-five':'45','fifty':'50'};
    let t = transcript.toLowerCase();
    Object.keys(wordNums).forEach(w => { t = t.replace(new RegExp('\\b' + w + '\\b', 'g'), wordNums[w]); });
    const transcript2 = t;
    // Free tier: regex parser
    const lower = transcript.toLowerCase();
    const entries = [];

    // Meal keywords
    const mealKeywords = ['ate','eaten','had','breakfast','lunch','dinner','snack','meal','protein','calories','carbs','pre-workout','post-workout'];
    const isMeal = mealKeywords.some(k => lower.includes(k));

    if (isMeal) {
      const mealTypes = ['breakfast','lunch','dinner','snack','pre-workout','post-workout'];
      let mealType = 'Snack';
      for (const mt of mealTypes) {
        if (lower.includes(mt)) { mealType = mt.charAt(0).toUpperCase() + mt.slice(1); break; }
      }
      const calMatch = lower.match(/(\d+)\s*cal/);
      const protMatch = lower.match(/(\d+)\s*g?\s*protein/);
      entries.push({
        meal_type: mealType,
        description: transcript,
        calories: calMatch ? parseInt(calMatch[1]) : null,
        protein: protMatch ? parseInt(protMatch[1]) : null,
        date: today,
      });
      setVoiceParsed({ type: 'meal', entries });
      return;
    }

    // Workout parser — match patterns like "bench press 225 4 sets 8 reps"
    const exercisePatterns = [
      /(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?|kg)?\s*(?:x|for|×)?\s*(\d+)\s*(?:sets?)\s*(?:x|of|×|for)?\s*(\d+)\s*(?:reps?)/gi,
      /(\d+)\s*(?:sets?)\s*(?:x|of|×)?\s*(\d+)\s*(?:reps?)\s*(?:at|@|of)?\s*(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?|kg)?/gi,
      /(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?|kg)\s*(?:for)?\s*(\d+)\s*(?:sets?)\s*(?:x|of|×)?\s*(\d+)\s*(?:reps?)/gi,
    ];

    // Simple extraction: find numbers in context
    const numMatches = transcript2.match(/\d+(?:\.\d+)?/g) || [];
    if (numMatches.length >= 2) {
      // Guess exercise name (words before numbers)
      const words = transcript2.split(/\s+/);
      let exerciseName = '';
      for (const w of words) {
        if (/^\d/.test(w)) break;
        exerciseName += w + ' ';
      }
      exerciseName = exerciseName.trim() || 'Exercise';

      const weight = parseFloat(numMatches[0]) || 0;
      const sets = parseInt(numMatches[1]) || 1;
      const reps = parseInt(numMatches[2]) || parseInt(numMatches[1]) || 1;

      entries.push({ exercise: exerciseName, sets, reps, weight, date: today });
    }

    if (entries.length > 0) {
      setVoiceParsed({ type: 'workout', entries });
    } else {
      setVoiceParsed({ type: 'error', message: 'Could not parse. Try: "Bench press 225 pounds, 4 sets of 8 reps"' });
    }
  };

  const logVoiceParsed = async () => {
    if (!voiceParsed || voiceParsed.type === 'error') return;
    setVoiceLogging(true);
    try {
      if (voiceParsed.type === 'workout') {
        for (const entry of voiceParsed.entries) {
          await fetch(`${API_URL}/workouts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(entry),
          });
        }
      } else if (voiceParsed.type === 'meal') {
        for (const entry of voiceParsed.entries) {
          await fetch(`${API_URL}/meals`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(entry),
          });
        }
      }
      localStorage.removeItem('anima_analysis');
      setCelebrating(true);
      setTimeout(() => setCelebrating(false), 900);
      setVoiceSuccess(true);
      setVoiceParsed(null);
      setVoiceTranscript('');
      onLogged();
      setTimeout(() => setVoiceSuccess(false), 3000);
    } catch(e) {
      setError('Connection error');
    }
    setVoiceLogging(false);
  };

  // celebration render handled in JSX
  const toggleStyle = (active) => ({
    flex: 1,
    padding: "12px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "700",
    letterSpacing: "2px",
    fontFamily: "'Rajdhani', sans-serif",
    background: active ? COLORS.teal : "transparent",
    color: active ? COLORS.black : COLORS.silver,
    transition: "all 0.2s",
  });

  return (
    <div style={{ ...styles.screen, paddingBottom: "100px" }}>
      <CelebrationBurst show={celebrating} />
      {!hideHeader && <div style={styles.header}><div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div><div style={styles.tagline}>Log Session</div></div>}
      <div style={{ ...styles.content, paddingTop: hideHeader ? "8px" : undefined }}>

        {/* Mode Toggle — hidden when used inside Train/Nutrition screens */}
        {!hideHeader && (
        <div style={{ display: "flex", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "12px", padding: "4px", marginBottom: "24px" }}>
          <button style={toggleStyle(mode === "workout")} onClick={() => setMode("workout")}>🏋️ WORKOUT</button>
          <button style={toggleStyle(mode === "meal")} onClick={() => setMode("meal")}>🍽️ MEAL</button>
          <button style={toggleStyle(mode === "voice")} onClick={() => setMode("voice")}>🎤 VOICE</button>
        </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {/* WORKOUT MODE */}
        {mode === "workout" && (
          <div>
            {sessionSuccess && (
              <div style={{
                background: "rgba(0,212,212,0.1)",
                border: `1px solid ${COLORS.teal}`,
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "16px",
                color: COLORS.teal,
                fontSize: "16px",
                fontWeight: "700",
                letterSpacing: "2px",
                textAlign: "center",
              }}>
                ✓ SESSION LOGGED — ANIMA IS ANALYZING
              </div>
            )}

            <label style={styles.label}>Date</label>
            <input style={styles.input} type="date" value={date}
              onChange={e => setDate(e.target.value)} />

            <label style={styles.label}>Category</label>
            <select style={{ ...styles.input, cursor: "pointer" }}
              value={category} onChange={e => { setCategory(e.target.value); setExercise(""); }}>
              <option value="">Select category</option>
              {Object.keys(EXERCISES).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {category && (
              <>
                <label style={styles.label}>Exercise</label>
                <select style={{ ...styles.input, cursor: "pointer" }}
                  value={exercise} onChange={e => setExercise(e.target.value)}>
                  <option value="">Select exercise</option>
                  {EXERCISES[category].map(ex => (
                    <option key={ex} value={ex}>{ex}</option>
                  ))}
                </select>
              </>
            )}

            {exercise === "Other" && (
              <>
                <label style={styles.label}>Exercise Name</label>
                <input style={styles.input} placeholder="Enter exercise name"
                  value={otherExercise} onChange={e => setOtherExercise(e.target.value)} />
              </>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
              <div>
                <label style={styles.label}>Sets</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="4" type="number"
                  value={sets} onChange={e => setSets(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Reps</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="8" type="number"
                  value={reps} onChange={e => setReps(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Weight (lbs)</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="135" type="number"
                  value={weight} onChange={e => setWeight(e.target.value)} />
              </div>
            </div>

            <label style={styles.label}>Notes (optional)</label>
            <textarea style={{ ...styles.input, minHeight: "60px", resize: "none", fontFamily: "'Rajdhani', sans-serif" }}
              placeholder="PRs, how it felt, etc."
              value={notes} onChange={e => setNotes(e.target.value)} />

            <button
              style={{ ...styles.btnOutline, opacity: canAddExercise ? 1 : 0.4, marginBottom: "16px" }}
              disabled={!canAddExercise}
              onClick={addToSession}
            >
              + Add to Session
            </button>

            {/* Session List */}
            {session.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "12px" }}>
                  THIS SESSION — {session.length} EXERCISE{session.length > 1 ? "S" : ""}
                </div>
                {session.map((ex, i) => (
                  <div key={i} style={{
                    ...styles.workoutItem,
                    marginBottom: "8px",
                  }}>
                    <div>
                      <div style={{ fontSize: "16px", fontWeight: "700", letterSpacing: "2px", color: COLORS.white }}>
                        {ex.exercise.toUpperCase()}
                      </div>
                      <div style={{ fontSize: "16px", color: COLORS.silver }}>
                        {ex.sets} sets × {ex.reps} reps {ex.weight > 0 ? `@ ${ex.weight}lbs` : "(bodyweight)"}
                      </div>
                    </div>
                    <button onClick={() => removeFromSession(i)} style={{
                      background: "transparent",
                      border: "none",
                      color: COLORS.error,
                      fontSize: "18px",
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}>✕</button>
                  </div>
                ))}

                <button
                  style={{ ...styles.btnPrimary, marginTop: "8px", opacity: loggingSession ? 0.6 : 1 }}
                  disabled={loggingSession}
                  onClick={logSession}
                >
                  {loggingSession ? "Logging..." : `Log Session (${session.length} Exercise${session.length > 1 ? "s" : ""})`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* MEAL MODE */}
        {mode === "meal" && (
          <div>
            {mealSuccess && (
              <div style={{
                background: "rgba(0,212,212,0.1)",
                border: `1px solid ${COLORS.teal}`,
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "16px",
                color: COLORS.teal,
                fontSize: "16px",
                fontWeight: "700",
                letterSpacing: "2px",
                textAlign: "center",
              }}>
                ✓ MEALS LOGGED
              </div>
            )}

            <label style={styles.label}>Date</label>
            <input style={styles.input} type="date" value={date}
              onChange={e => setDate(e.target.value)} />

            <label style={styles.label}>Meal Type</label>
            <select style={{ ...styles.input, cursor: "pointer" }}
              value={mealType} onChange={e => setMealType(e.target.value)}>
              <option value="">Select meal type</option>
              {MEAL_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            <label style={styles.label}>What did you eat?</label>
            <textarea style={{
              ...styles.input,
              minHeight: "100px",
              resize: "none",
              fontFamily: "'Rajdhani', sans-serif",
            }} placeholder="e.g. Chicken breast 8oz, white rice 1 cup, broccoli 2 cups"
              value={mealDesc} onChange={e => setMealDesc(e.target.value)} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={styles.label}>Calories</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="650" type="number"
                  value={mealCalories} onChange={e => setMealCalories(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Protein g</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="55" type="number"
                  value={mealProtein} onChange={e => setMealProtein(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Carbs g</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="60" type="number"
                  value={mealCarbs} onChange={e => setMealCarbs(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Fat g</label>
                <input style={{ ...styles.input, textAlign: "center" }} placeholder="20" type="number"
                  value={mealFat} onChange={e => setMealFat(e.target.value)} />
              </div>
            </div>


            <button
              style={{ ...styles.btnOutline, opacity: mealType && mealDesc ? 1 : 0.4, marginBottom: "16px" }}
              disabled={!mealType || !mealDesc}
              onClick={addToMealSession}
            >
              + Add to Day
            </button>

            {mealSession.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.gold, textTransform: "uppercase", marginBottom: "12px" }}>
                  TODAY'S MEALS — {mealSession.length} ADDED
                </div>
                {mealSession.map((meal, i) => (
                  <div key={i} style={{
                    ...styles.workoutItem,
                    borderLeft: `3px solid ${COLORS.gold}`,
                    marginBottom: "8px",
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "16px", fontWeight: "700", letterSpacing: "2px", color: COLORS.gold }}>
                        {meal.meal_type.toUpperCase()}
                      </div>
                      <div style={{ fontSize: "16px", color: COLORS.silverBright, lineHeight: "1.5" }}>
                        {meal.description}
                      </div>
                      {(meal.calories || meal.protein) && (
                        <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "4px" }}>
                          {meal.calories ? `${meal.calories} cal` : ""}
                          {meal.calories && meal.protein ? " · " : ""}
                          {meal.protein ? `${meal.protein}g protein` : ""}
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeFromMealSession(i)} style={{
                      background: "transparent",
                      border: "none",
                      color: COLORS.error,
                      fontSize: "18px",
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}>✕</button>
                  </div>
                ))}
                <button
                  style={{ ...styles.btnPrimary, marginTop: "8px", opacity: loggingMeals ? 0.6 : 1 }}
                  disabled={loggingMeals}
                  onClick={logMealSession}
                >
                  {loggingMeals ? "Logging..." : `Log ${mealSession.length} Meal${mealSession.length > 1 ? "s" : ""}`}
                </button>
              </div>
            )}
          </div>
        )}
        {/* VOICE MODE */}
        {mode === "voice" && (
          <div>
            {voiceSuccess && (
              <div style={{
                background: "rgba(0,212,212,0.1)", border: `1px solid ${COLORS.teal}`,
                borderRadius: "12px", padding: "16px", marginBottom: "16px",
                color: COLORS.teal, fontSize: "16px", fontWeight: "700",
                letterSpacing: "2px", textAlign: "center",
              }}>✓ LOGGED SUCCESSFULLY</div>
            )}
            <div style={{ ...styles.cardTeal, textAlign: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, fontWeight: "700", marginBottom: "12px" }}>
                ⚡ VOICE LOGGING {user?.premium ? "— ANIMA POWERED" : "— FREE TIER"}
              </div>
              <div style={{ fontSize: "16px", color: COLORS.silver, marginBottom: "20px", lineHeight: "1.7" }}>
                {user?.premium ? "Speak naturally. ANIMA parses your workout or meal automatically." : 'Say: "Bench press 225 pounds, 4 sets of 8 reps"'}
              </div>
              <button onClick={startVoiceLog} disabled={voiceListening || voiceParsing} style={{
                width: "80px", height: "80px", borderRadius: "50%", border: "none",
                background: voiceListening ? "rgba(255,68,68,0.2)" : "rgba(0,212,212,0.15)",
                cursor: "pointer", margin: "0 auto 16px", display: "flex",
                alignItems: "center", justifyContent: "center",
                outline: voiceListening ? `2px solid ${COLORS.error}` : `2px solid ${COLORS.teal}`,
                transition: "all 0.2s",
              }}>
                {voiceListening ? (
                  <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                    {[8,14,20,14,8].map((h,n) => (
                      <div key={n} style={{ width: "4px", height: `${h}px`, background: COLORS.error,
                        borderRadius: "2px", animation: "waveform 0.8s ease-in-out infinite", animationDelay: `${n*0.1}s` }} />
                    ))}
                  </div>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={COLORS.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                )}
              </button>
              <div style={{ fontSize: "16px", color: voiceListening ? COLORS.error : COLORS.silver, letterSpacing: "2px" }}>
                {voiceListening ? "LISTENING..." : voiceParsing ? "PARSING..." : "TAP TO SPEAK"}
              </div>
            </div>
            {voiceTranscript ? (
              <div style={{ ...styles.card, marginBottom: "16px" }}>
                <div style={{ fontSize: "10px", letterSpacing: "2px", color: COLORS.silver, marginBottom: "8px" }}>YOU SAID</div>
                <div style={{ fontSize: "16px", color: COLORS.silverBright, lineHeight: "1.6", fontStyle: "italic" }}>"{voiceTranscript}"</div>
              </div>
            ) : null}
            {voiceParsed && voiceParsed.type === "error" ? (
              <div style={{ ...styles.error, marginBottom: "16px" }}>{voiceParsed.message}</div>
            ) : null}
            {voiceParsed && voiceParsed.type !== "error" ? (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "12px" }}>
                  ⚡ CONFIRM TO LOG
                </div>
                {voiceParsed.entries.map((entry, idx) => (
                  <div key={idx} style={{ ...styles.workoutItem, marginBottom: "8px", borderLeft: `3px solid ${voiceParsed.type === "meal" ? COLORS.gold : COLORS.teal}` }}>
                    <div style={{ flex: 1 }}>
                      {voiceParsed.type === "workout" ? (
                        <div>
                          <div style={{ fontSize: "16px", fontWeight: "700", letterSpacing: "2px", color: COLORS.white }}>{entry.exercise ? entry.exercise.toUpperCase() : "EXERCISE"}</div>
                          <div style={{ fontSize: "16px", color: COLORS.silver }}>{entry.sets} sets x {entry.reps} reps {entry.weight > 0 ? `@ ${entry.weight}lbs` : ""}</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: "16px", fontWeight: "700", color: COLORS.gold }}>{entry.meal_type ? entry.meal_type.toUpperCase() : "MEAL"}</div>
                          <div style={{ fontSize: "16px", color: COLORS.silverBright }}>{entry.description}</div>
                          {(entry.calories || entry.protein) && (
                            <div style={{ fontSize: "16px", color: COLORS.teal, marginTop: "6px", fontWeight: "700" }}>
                              {entry.calories ? `${entry.calories} cal` : ""}{entry.calories && entry.protein ? " · " : ""}{entry.protein ? `${entry.protein}g protein` : ""}{entry.carbs ? ` · ${entry.carbs}g carbs` : ""}{entry.fat ? ` · ${entry.fat}g fat` : ""}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: "12px", marginBottom: "4px" }}>
                  <div style={{ fontSize: "10px", letterSpacing: "2px", color: COLORS.silver, marginBottom: "6px" }}>LOG DATE</div>
                  <input type="date" value={voiceDate} onChange={e => {
                    setVoiceDate(e.target.value);
                    setVoiceParsed(prev => ({
                      ...prev,
                      entries: prev.entries.map(en => ({ ...en, date: e.target.value }))
                    }));
                  }} style={{ width: "100%", background: COLORS.charcoal, border: "1px solid " + COLORS.border, borderRadius: "10px", padding: "12px 16px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none", boxSizing: "border-box" }} />
                </div>
                <button style={{ ...styles.btnPrimary, marginTop: "8px", opacity: voiceLogging ? 0.6 : 1 }} disabled={voiceLogging} onClick={logVoiceParsed}>
                  {voiceLogging ? "LOGGING..." : "CONFIRM & LOG"}
                </button>
                <button style={{ ...styles.btnOutline, marginTop: "8px" }} onClick={() => { setVoiceParsed(null); setVoiceTranscript(""); }}>
                  Clear
                </button>
              </div>
            ) : null}
            {!user?.premium ? (
              <div style={{ ...styles.card, marginTop: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", letterSpacing: "2px", color: COLORS.gold, marginBottom: "8px" }}>⚡ UPGRADE FOR NATURAL VOICE LOGGING</div>
                <div style={{ fontSize: "16px", color: COLORS.silver, marginBottom: "16px", lineHeight: "1.6" }}>
                  Premium unlocks ANIMA-powered parsing — just talk naturally and ANIMA figures out the rest.
                </div>
                <button style={styles.btnPrimary} onClick={async () => {
                  try {
                    const res = await fetch(API_URL + "/subscriptions/create", { method: "POST", headers: { "Authorization": "Bearer " + token, "Content-Type": "application/json" } });
                    const data = await res.json();
                    if (data.url) window.location.href = data.url;
                  } catch(e) {}
                }}>UPGRADE — $4.95/MO</button>
              </div>
            ) : null}
          </div>
        )}


      </div>
    </div>
  );
}

function HistoryScreen({ workouts, meals, token, user, onMealUpdated }) {
  // Combine workouts and meals into unified feed
  const allEntries = [
    ...workouts.map(w => ({ ...w, entryType: "workout" })),
    ...meals.map(m => ({ ...m, entryType: "meal" })),
  ];

  const [editingMeal, setEditingMeal] = useState(null);
  const [editForm, setEditForm] = useState({});

  const saveMealEdit = async () => {
    try {
      await fetch(`${API_URL}/meals/${editingMeal}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      setEditingMeal(null);
      if (onMealUpdated) onMealUpdated();
    } catch(e) {}
  };

  const deleteEntry = async (type, id) => {
    if (!window.confirm(type === "workout" ? "Delete this exercise?" : "Delete this meal?")) return;
    try {
      await fetch(`${API_URL}/${type === "workout" ? "workouts" : "meals"}/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (onMealUpdated) onMealUpdated();
    } catch(e) {}
  };

  const grouped = allEntries.reduce((acc, entry) => {
    const date = entry.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const isEmpty = workouts.length === 0 && meals.length === 0;

  return (
    <div style={{ ...styles.screen, paddingBottom: "100px" }}>
      <div style={styles.header}>
        <div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div>
        <div style={styles.tagline}>History</div>
      </div>
      <div style={styles.content}>

        {/* Summary pills */}
        {!isEmpty && (
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
            <div style={{
              flex: 1, textAlign: "center", padding: "12px",
              background: COLORS.card, border: `1px solid ${COLORS.teal}`,
              borderRadius: "12px",
            }}>
              <div style={{ fontSize: "22px", fontWeight: "900", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif" }}>{workouts.length}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "2px" }}>SESSIONS</div>
            </div>
            <div style={{
              flex: 1, textAlign: "center", padding: "12px",
              background: COLORS.card, border: `1px solid ${COLORS.gold}`,
              borderRadius: "12px",
            }}>
              <div style={{ fontSize: "22px", fontWeight: "900", color: COLORS.gold, fontFamily: "'Rajdhani', sans-serif" }}>{meals.length}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "2px" }}>MEALS</div>
            </div>
          </div>
        )}

        {/* Weekly Nutrition Chart */}
        {meals.length > 0 && (() => {
          const now = new Date();
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - 6);
          weekStart.setHours(0,0,0,0);

          const days = [];
          for (let i = 6; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(now.getDate() - i);
            const dateStr = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
            const dayMeals = meals.filter(m => m.date === dateStr);
            const cal = dayMeals.reduce((a, m) => a + (m.calories || 0), 0);
            const pro = dayMeals.reduce((a, m) => a + (m.protein || 0), 0);
            days.push({ dateStr, cal, pro, label: ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()] });
          }

          const maxCal = Math.max(...days.map(d => d.cal), 1);
          const weightLbs = user?.weight || 180;
          const fitnessGoal = user?.fitness_goal || "maintain";
          const actMult = (user?.activity_level || "moderate") === "sedentary" ? 1.2 : (user?.activity_level) === "light" ? 1.375 : (user?.activity_level) === "active_job" ? 1.65 : (user?.activity_level) === "very" ? 1.725 : (user?.activity_level) === "athlete" ? 1.9 : 1.55;
          const heightCm = (user?.height || 70) * 2.54;
          const weightKg = weightLbs * 0.453592;
          const bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * (user?.age || 30)) + 5;
          const calAdj = fitnessGoal === "aggressive_cut" ? -500 : fitnessGoal === "cut" ? -250 : fitnessGoal === "lean_bulk" ? 250 : fitnessGoal === "bulk" ? 500 : 0;
          const calorieTarget = Math.round(bmr * actMult) + calAdj;
          const proteinTarget = Math.round(weightLbs * (fitnessGoal === "aggressive_cut" ? 1.2 : fitnessGoal === "cut" ? 1.1 : 1.0));

          if (days.every(d => d.cal === 0)) return null;

          return (
            <div style={{ background: `linear-gradient(160deg, ${COLORS.card}, ${COLORS.charcoal})`, border: `1px solid ${COLORS.border}`, borderRadius: "16px", padding: "20px", marginBottom: "16px" }}>
              <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.gold, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", marginBottom: "16px" }}>
                📅 7-DAY NUTRITION
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "80px", marginBottom: "8px" }}>
                {days.map((day, i) => {
                  const now2 = new Date();
                  const todayStr = now2.getFullYear() + "-" + String(now2.getMonth()+1).padStart(2,"0") + "-" + String(now2.getDate()).padStart(2,"0");
                  const isToday = day.dateStr === todayStr;
                  const calPct = day.cal > 0 ? Math.min(100, (day.cal / maxCal) * 100) : 0;
                  const hitTarget = day.cal >= calorieTarget * 0.85;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <div style={{ fontSize: "9px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif" }}>
                        {day.cal > 0 ? Math.round(day.cal) : ""}
                      </div>
                      <div style={{ width: "100%", height: `${Math.max(calPct * 0.6, day.cal > 0 ? 4 : 0)}px`, background: hitTarget ? COLORS.teal : day.cal > 0 ? COLORS.gold : COLORS.border, borderRadius: "3px 3px 0 0", transition: "height 0.4s ease", minHeight: day.cal > 0 ? "4px" : "0" }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                {days.map((day, i) => {
                  const now2 = new Date();
                  const todayStr = now2.getFullYear() + "-" + String(now2.getMonth()+1).padStart(2,"0") + "-" + String(now2.getDate()).padStart(2,"0");
                  const isToday = day.dateStr === todayStr;
                  return (
                    <div key={i} style={{ flex: 1, textAlign: "center", fontSize: "10px", fontFamily: "'Rajdhani', sans-serif", fontWeight: isToday ? "900" : "400", color: isToday ? COLORS.gold : COLORS.silver }}>
                      {day.label}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: "16px", marginTop: "12px", fontSize: "16px", color: COLORS.silver }}>
                <div><span style={{ color: COLORS.teal }}>■</span> Hit target</div>
                <div><span style={{ color: COLORS.gold }}>■</span> Logged</div>
                <div><span style={{ color: COLORS.border }}>■</span> No data</div>
                <div style={{ marginLeft: "auto", color: COLORS.silver }}>Target: {calorieTarget} cal</div>
              </div>
            </div>
          );
        })()}
        {/* Daily Macro Summary */}
        {meals.length > 0 && (() => {
          const now = new Date(); const today = now.getFullYear() + "-" + String(now.getMonth()+1).padStart(2,"0") + "-" + String(now.getDate()).padStart(2,"0");
          const todayMeals = meals.filter(m => m.date === today);
          if (todayMeals.length === 0) return null;
          const totals = todayMeals.reduce((acc, m) => ({
            calories: acc.calories + (m.calories || 0),
            protein: acc.protein + (m.protein || 0),
            carbs: acc.carbs + (m.carbs || 0),
            fat: acc.fat + (m.fat || 0),
          }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
          const weightLbs = user?.weight || 180;
          const fitnessGoal = user?.fitness_goal || "maintain";
          const activityLevel = user?.activity_level || "moderate";

          // Protein multiplier by goal
          const proteinMult = fitnessGoal === "aggressive_cut" ? 1.2 :
                              fitnessGoal === "cut" ? 1.1 : 1.0;
          const proteinTarget = Math.round(weightLbs * proteinMult);

          // Activity multiplier for TDEE
          const actMult = activityLevel === "sedentary" ? 1.2 :
                          activityLevel === "light" ? 1.375 :
                          activityLevel === "moderate" ? 1.55 :
                          activityLevel === "active_job" ? 1.65 :
                          activityLevel === "very" ? 1.725 : 1.9;

          // BMR (Mifflin-St Jeor, assume male average if no sex data)
          const heightCm = (user?.height || 70) * 2.54;
          const weightKg = weightLbs * 0.453592;
          const age = user?.age || 30;
          const bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5;
          const tdee = Math.round(bmr * actMult);

          // Calorie adjustment by goal
          const calAdj = fitnessGoal === "aggressive_cut" ? -500 :
                         fitnessGoal === "cut" ? -250 :
                         fitnessGoal === "lean_bulk" ? 250 :
                         fitnessGoal === "bulk" ? 500 : 0;
          const calorieTarget = tdee + calAdj;
          return (
            <div style={{ background: `linear-gradient(160deg, ${COLORS.card}, ${COLORS.charcoal})`, border: `1px solid ${COLORS.teal}`, borderRadius: "16px", padding: "20px", marginBottom: "20px", boxShadow: `0 0 30px ${COLORS.tealGlow}` }}>
              <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", marginBottom: "16px" }}>
                ⚡ TODAY'S NUTRITION
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: "16px" }}>
                {[
                  { label: "CAL", value: totals.calories, target: calorieTarget, color: COLORS.teal },
                  { label: "PROTEIN", value: totals.protein, target: proteinTarget, color: COLORS.teal, unit: "g" },
                  { label: "CARBS", value: totals.carbs, target: null, color: COLORS.gold, unit: "g" },
                  { label: "FAT", value: totals.fat, target: null, color: "#9B59B6", unit: "g" },
                ].map(({ label, value, target, color, unit }) => (
                  <div key={label} style={{ textAlign: "center", background: "rgba(0,0,0,0.2)", borderRadius: "10px", padding: "10px 4px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "900", color, fontFamily: "'Rajdhani', sans-serif", lineHeight: 1 }}>{Math.round(value)}{unit || ""}</div>
                    {target && <div style={{ fontSize: "9px", color: COLORS.silver, marginTop: "2px" }}>/ {target}{unit || ""}</div>}
                    <div style={{ fontSize: "9px", letterSpacing: "1px", color: COLORS.silver, marginTop: "4px" }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px" }}>PROTEIN</span>
                  <span style={{ fontSize: "10px", color: COLORS.teal }}>{Math.round(totals.protein)}g / {proteinTarget}g</span>
                </div>
                <div style={{ height: "4px", background: COLORS.border, borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (totals.protein / proteinTarget) * 100)}%`, background: COLORS.teal, borderRadius: "2px", transition: "width 0.5s ease" }} />
                </div>
              </div>
              <div style={{ marginBottom: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px" }}>CALORIES</span>
                  <span style={{ fontSize: "10px", color: COLORS.gold }}>{Math.round(totals.calories)} / {calorieTarget}</span>
                </div>
                <div style={{ height: "4px", background: COLORS.border, borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (totals.calories / calorieTarget) * 100)}%`, background: COLORS.gold, borderRadius: "2px", transition: "width 0.5s ease" }} />
                </div>
              </div>
              <div style={{ marginBottom: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px" }}>CARBS</span>
                  <span style={{ fontSize: "10px", color: COLORS.gold }}>{Math.round(totals.carbs)}g</span>
                </div>
                <div style={{ height: "4px", background: COLORS.border, borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (totals.carbs / 200) * 100)}%`, background: COLORS.gold, borderRadius: "2px", transition: "width 0.5s ease" }} />
                </div>
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px" }}>FAT</span>
                  <span style={{ fontSize: "10px", color: "#9B59B6" }}>{Math.round(totals.fat)}g</span>
                </div>
                <div style={{ height: "4px", background: COLORS.border, borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (totals.fat / 80) * 100)}%`, background: "#9B59B6", borderRadius: "2px", transition: "width 0.5s ease" }} />
                </div>
              </div>
            </div>
          );
        })()}
        {isEmpty ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
            <div style={{ fontSize: "20px", letterSpacing: "3px", color: COLORS.silver }}>NOTHING LOGGED YET</div>
            <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "8px" }}>Log a workout or meal to start building history</div>
          </div>
        ) : (
          Object.entries(grouped).sort((a, b) => new Date(b[0]) - new Date(a[0])).map(([date, entries]) => (
            <div key={date}>
              <div style={{
                fontSize: "16px", letterSpacing: "3px", color: COLORS.teal,
                textTransform: "uppercase", marginBottom: "10px", marginTop: "20px",
              }}>
                {new Date(date + "T12:00:00").toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}
              </div>
              {entries.map((entry, i) => (
                entry.entryType === "workout" ? (
                  <div key={i} style={styles.workoutItem}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "18px", fontWeight: "700", letterSpacing: "2px", color: COLORS.white }}>
                        {entry.exercise?.toUpperCase()}
                      </div>
                      <div style={{ fontSize: "16px", color: COLORS.silver }}>
                        {entry.sets} × {entry.reps} @ {entry.weight}lbs
                      </div>
                      {entry.notes && <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "4px", fontStyle: "italic" }}>{entry.notes}</div>}
                      {editingMeal === "w_" + entry.id ? (
                        <div style={{ marginTop: "12px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                            {[["sets","Sets"],["reps","Reps"],["weight","Weight lbs"]].map(([field, label]) => (
                              <div key={field}>
                                <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px", marginBottom: "4px" }}>{label}</div>
                                <input type="number" placeholder={label}
                                  value={editForm[field] || ""}
                                  onChange={e => setEditForm({...editForm, [field]: e.target.value})}
                                  style={{ width: "100%", background: COLORS.charcoal, border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none", boxSizing: "border-box" }}
                                />
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={async () => {
                              try {
                                await fetch(`${API_URL}/workouts/${entry.id}`, {
                                  method: "PUT",
                                  headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                                  body: JSON.stringify(editForm),
                                });
                                setEditingMeal(null);
                                if (onMealUpdated) onMealUpdated();
                              } catch(e) {}
                            }} style={{ flex: 1, background: COLORS.teal, border: "none", borderRadius: "8px", padding: "10px", color: COLORS.black, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", fontSize: "16px", letterSpacing: "2px", cursor: "pointer" }}>SAVE</button>
                            <button onClick={() => setEditingMeal(null)} style={{ flex: 1, background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "10px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", cursor: "pointer" }}>CANCEL</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                          <button onClick={() => { setEditingMeal("w_" + entry.id); setEditForm({ sets: entry.sets, reps: entry.reps, weight: entry.weight }); }}
                            style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "6px", padding: "4px 10px", color: COLORS.silver, fontSize: "16px", letterSpacing: "1px", cursor: "pointer", fontFamily: "'Rajdhani', sans-serif" }}>
                            ✏️ Edit
                          </button>
                          <button onClick={() => deleteEntry("workout", entry.id)}
                            style={{ background: "transparent", border: "none", color: "rgba(255,68,68,0.5)", fontSize: "16px", cursor: "pointer", padding: "2px 6px", fontFamily: "'Rajdhani', sans-serif", letterSpacing: "1px" }}>
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: "900", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", textAlign: "center", minWidth: "50px" }}>
                      {entry.weight}
                      <div style={{ fontSize: "10px", color: COLORS.silver }}>LBS</div>
                    </div>
                  </div>
                ) : (
                  <div key={i} style={{
                    ...styles.workoutItem,
                    borderLeft: `3px solid ${COLORS.gold}`,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span style={{ fontSize: "16px" }}>🍽️</span>
                        <div style={{ fontSize: "16px", fontWeight: "700", letterSpacing: "2px", color: COLORS.gold }}>
                          {entry.meal_type?.toUpperCase()}
                        </div>
                      </div>
                      <div style={{ fontSize: "16px", color: COLORS.silverBright, lineHeight: "1.5" }}>
                        {entry.description}
                      </div>
                      {(entry.calories || entry.protein) && (
                        <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "4px" }}>
                          {entry.calories ? `${entry.calories} cal` : ""}
                          {entry.calories && entry.protein ? " · " : ""}
                          {entry.protein ? `${entry.protein}g protein` : ""}
                        </div>
                      )}
                      <button onClick={() => deleteEntry("meal", entry.id)} style={{ marginTop: "6px", background: "transparent", border: "none", color: "rgba(255,68,68,0.5)", fontSize: "16px", cursor: "pointer", padding: "2px 0", fontFamily: "'Rajdhani', sans-serif", letterSpacing: "1px" }}>🗑️ Delete</button>
                      {editingMeal === entry.id ? (
                        <div style={{ marginTop: "12px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                            {[["calories","Cal"],["protein","Protein g"],["carbs","Carbs g"],["fat","Fat g"]].map(([field, label]) => (
                              <div key={field}>
                                <div style={{ fontSize: "10px", color: COLORS.silver, letterSpacing: "1px", marginBottom: "4px" }}>{label}</div>
                                <input type="number" placeholder={label}
                                  value={editForm[field] || ""}
                                  onChange={e => setEditForm({...editForm, [field]: parseFloat(e.target.value) || ""})}
                                  style={{ width: "100%", background: COLORS.charcoal, border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none", boxSizing: "border-box" }}
                                />
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={saveMealEdit} style={{ flex: 1, background: COLORS.teal, border: "none", borderRadius: "8px", padding: "10px", color: COLORS.black, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", fontSize: "16px", letterSpacing: "2px", cursor: "pointer" }}>SAVE</button>
                            <button onClick={() => setEditingMeal(null)} style={{ flex: 1, background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "10px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", cursor: "pointer" }}>CANCEL</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingMeal(entry.id); setEditForm({ calories: entry.calories || "", protein: entry.protein || "", carbs: entry.carbs || "", fat: entry.fat || "" }); }}
                          style={{ marginTop: "8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "6px", padding: "4px 10px", color: COLORS.silver, fontSize: "16px", letterSpacing: "1px", cursor: "pointer", fontFamily: "'Rajdhani', sans-serif" }}>
                          ✏️ Edit Macros
                        </button>
                      )}

                    </div>
                  </div>
                )
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CoachScreen({ token, workouts, user, onTabChange }) {
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);

  const getAnalysis = async (chatContext = []) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/workouts/analyze`, {
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        method: "POST",
        body: JSON.stringify({ chat_context: chatContext }),
      });
      const data = await res.json();
      if (data.analysis) {
        setAnalysis(data.analysis);
        localStorage.setItem("anima_analysis", data.analysis);
      } else if (data.limit_reached) {
        setLimitReached(true);
      } else {
        setError("ANIMA is unavailable right now. Please try again later.");
      }
    } catch (e) {
      setError("Connection error");
    }
    setLoading(false);
  };

  useEffect(() => {
  const cachedMessages = localStorage.getItem("anima_messages");
  const cached = localStorage.getItem("anima_analysis");
    if (cachedMessages) setMessages(JSON.parse(cachedMessages));
    if (cached) {
      setAnalysis(cached);
      return;
    }
    getAnalysis();
  }, []);
  const startVoice = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice input is not supported in this browser. Try Chrome.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    setIsListening(true);
    recognition.start();
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setUserInput(transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };
  const sendMessage = async () => {
    const newMessages = [...messages, { role: 'user', content: userInput }];
    setMessages(newMessages);
    setUserInput('');
    setChatLoading(true);
    try {
      const res = await fetch(API_URL + '/workouts/chat', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userInput, messages: messages }),
      });
      const data = await res.json();
      if (data.limit_reached) {
        setLimitReached(true);
        setMessages([...newMessages, { role: 'assistant', content: 'You have used your 3 free ANIMA sessions this week. Upgrade to Premium for unlimited coaching.' }]);
      } else if (data.reply) {
        setMessages([...newMessages, { role: 'assistant', content: data.reply }]);
        localStorage.setItem("anima_messages", JSON.stringify([...newMessages, { role: "assistant", content: data.reply }]));
      }
    } catch(e) {
      setMessages([...newMessages, { role: 'assistant', content: 'Connection error. Try again.' }]);
    }
    setChatLoading(false);
  };


  const renderAnalysis = (text) => {
    const sectionColors = {
      "PERFORMANCE ANALYSIS": "teal",
      "NUTRITION ASSESSMENT": "gold",
      "NEXT WORKOUT": "teal",
      "NEXT WORKOUT RECOMMENDATION": "teal",
      "PROGRESS": "gold",
      "PROGRESS TOWARD GOAL": "gold",
      "COACHING TIP": "teal",
    };

    const clean = text
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/#{1,3} /g, "");

    const lines = clean.split("\n");
    const elements = [];
    let currentBody = [];

    const flushBody = (key) => {
      if (currentBody.length > 0) {
        elements.push(
          <div key={key + "-body"} style={styles.analysisText}>
            {currentBody.join("\n")}
          </div>
        );
        currentBody = [];
      }
    };

    lines.forEach((line, i) => {
      const trimmed = line.trim();
      const upperLine = trimmed.toUpperCase();
      const matchedSection = Object.keys(sectionColors).find(s => upperLine.startsWith(s));

      if (matchedSection) {
        flushBody(i);
        const color = sectionColors[matchedSection];
        elements.push(
          <div key={i} style={color === "gold" ? styles.analysisSectionHeaderGold : styles.analysisSectionHeader}>
            {trimmed.replace(/[—-]ANIMA/g, "").trim()}
          </div>
        );
      } else if (trimmed === "—ANIMA" || trimmed === "— ANIMA") {
        flushBody(i);
        elements.push(
          <div key={i} style={{ fontSize: "16px", color: COLORS.teal, letterSpacing: "3px", marginTop: "20px", textAlign: "right", fontFamily: "'Rajdhani', sans-serif" }}>
            — ANIMA
          </div>
        );
      } else if (trimmed.startsWith("- ")) {
        flushBody(i);
        elements.push(
          <div key={i} style={{ fontSize: "16px", color: COLORS.silverBright, lineHeight: "1.8", paddingLeft: "12px", borderLeft: `2px solid ${COLORS.border}`, marginBottom: "6px", fontFamily: "'Rajdhani', sans-serif" }}>
            {trimmed.slice(2)}
          </div>
        );
      } else {
        if (trimmed) currentBody.push(trimmed);
      }
    });

    flushBody("final");
    return elements;
  };

  return (
    <div style={{ ...styles.screen, paddingBottom: "100px" }}>
      <AppHeader title="ANIMA — Your AI Coach" user={user} onProfileTap={() => onTabChange && onTabChange("profile")} />
      <div style={styles.content}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
          padding: "20px",
          background: COLORS.card,
          border: `1px solid ${COLORS.teal}`,
          borderRadius: "16px",
          boxShadow: `0 0 30px ${COLORS.tealGlow}`,
        }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.gold})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            flexShrink: 0,
          }}>⚡</div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: "900", letterSpacing: "3px", color: COLORS.white }}>ANIMA</div>
            <div style={{ fontSize: "16px", color: COLORS.teal, letterSpacing: "2px" }}>SOVEREIGN AI COACH · ONLINE</div>
          </div>
        </div>
        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase" }}>
              ANIMA IS ANALYZING...
            </div>
            <div style={{ fontSize: "16px", color: COLORS.silver, marginTop: "8px" }}>
              {workouts.length > 0 ? `Processing ${workouts.length} session${workouts.length > 1 ? "s" : ""}` : "Preparing your welcome plan..."}
            </div>
          </div>
        ) : analysis ? (
          <div>
            <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.silver, textTransform: "uppercase", marginBottom: "16px" }}>
              ANIMA ANALYSIS — {new Date().toLocaleDateString("en", { month: "long", day: "numeric" })}
            </div>
            <div style={styles.cardTeal}>
              <div>
                {renderAnalysis(analysis)}
              </div>
            </div>
            <button style={{ ...styles.btnOutline, marginTop: "8px" }} onClick={() => {
              localStorage.removeItem("anima_analysis");
              localStorage.removeItem("anima_messages");
              getAnalysis(messages);
            }}>
              Refresh Analysis
            </button>
            {limitReached && (
              <div style={{
                background: 'rgba(212,160,23,0.08)',
                border: '1px solid ' + COLORS.gold,
                borderRadius: '16px',
                padding: '20px',
                marginTop: '12px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '2px', color: COLORS.gold, marginBottom: '8px' }}>⚡ WEEKLY LIMIT REACHED</div>
                <div style={{ fontSize: '12px', color: COLORS.silver, lineHeight: '1.6', marginBottom: '16px' }}>
                  You have used your 3 free ANIMA analyses this week. Resets Monday.
                </div>
                <button
                  style={{ ...styles.btnPrimary, marginBottom: '8px' }}
                  onClick={async () => {
                    try {
                      const res = await fetch(API_URL + '/subscriptions/create', {
                        method: 'POST',
                        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                      });
                      const data = await res.json();
                      if (data.url) window.location.href = data.url;
                    } catch(e) { alert('Unable to start checkout. Try again.'); }
                  }}
                >
                  UPGRADE TO PREMIUM — $4.95/MO
                </button>
                <div style={{ fontSize: '11px', color: COLORS.silver }}>Unlimited ANIMA coaching · Cancel anytime</div>
              </div>
            )}
          </div>
        ) : limitReached ? (
          <div style={{
            background: 'rgba(212,160,23,0.08)',
            border: '1px solid ' + COLORS.gold,
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '28px', marginBottom: '12px' }}>⚡</div>
            <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '2px', color: COLORS.gold, marginBottom: '8px' }}>WEEKLY LIMIT REACHED</div>
            <div style={{ fontSize: '13px', color: COLORS.silver, lineHeight: '1.6', marginBottom: '20px' }}>
              You have used your 3 free ANIMA analyses this week. Resets Monday.
            </div>
            <button
              style={{ ...styles.btnPrimary, marginBottom: '12px' }}
              onClick={async () => {
                try {
                  const res = await fetch(API_URL + '/subscriptions/create', {
                    method: 'POST',
                    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                  });
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                } catch(e) { alert('Unable to start checkout. Try again.'); }
              }}
            >
              UPGRADE TO PREMIUM — $4.95/MO
            </button>
            <div style={{ fontSize: '11px', color: COLORS.silver, letterSpacing: '1px' }}>
              Unlimited ANIMA coaching · Full nutrition planning · Cancel anytime
            </div>
          </div>
        ) : null}
      </div>
        <div style={{ marginTop: '24px', padding: '0 24px 24px' }}>
          <div style={{
            fontSize: '11px', letterSpacing: '3px', color: COLORS.teal,
            textTransform: 'uppercase', marginBottom: '12px'
          }}>
            ⚡ ASK ANIMA ANYTHING
          </div>
          {messages.length > 0 && (
            <div style={{
              background: COLORS.card, border: '1px solid ' + COLORS.border,
              borderRadius: '16px', padding: '16px', marginBottom: '12px',
              maxHeight: '400px', overflowY: 'auto',
            }}>
              {messages.map((msg, i) => (
                <div key={i} style={{
                  marginBottom: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    fontSize: '9px', letterSpacing: '2px',
                    color: msg.role === 'user' ? COLORS.teal : COLORS.gold,
                    marginBottom: '6px', textTransform: 'uppercase',
                    fontWeight: '700',
                  }}>
                    {msg.role === 'user' ? 'YOU' : '⚡ ANIMA'}
                  </div>
                  <div style={{
                    maxWidth: '85%',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, rgba(0,212,212,0.15), rgba(0,212,212,0.05))'
                      : 'linear-gradient(135deg, #161B22, #111318)',
                    border: '1px solid ' + (msg.role === 'user' ? COLORS.teal : COLORS.border),
                    borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    padding: '14px 18px',
                    fontSize: '14px', color: COLORS.silverBright, lineHeight: '1.7',
                    fontFamily: "'Rajdhani', sans-serif",
                    boxShadow: msg.role === 'user'
                      ? '0 4px 20px rgba(0,212,212,0.1)'
                      : '0 4px 20px rgba(0,0,0,0.3)',
                  }}>
                    {msg.content.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').replace(/\{\{.*?\}\}/g, '').replace(/\}\}/g, '').trim()}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  marginBottom: '16px',
                }}>
                  <div style={{
                    fontSize: '9px', letterSpacing: '2px', color: COLORS.gold,
                    marginBottom: '6px', textTransform: 'uppercase', fontWeight: '700',
                  }}>⚡ ANIMA</div>
                  <div style={{
                    background: 'linear-gradient(135deg, #161B22, #111318)',
                    border: '1px solid ' + COLORS.border,
                    borderRadius: '20px 20px 20px 4px',
                    padding: '14px 20px',
                    display: 'flex', gap: '6px', alignItems: 'center',
                  }}>
                    {[0,1,2].map(n => (
                      <div key={n} style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: COLORS.teal,
                        animation: 'waveform 1s ease-in-out infinite',
                        animationDelay: n * 0.2 + 's',
                      }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              style={{
                ...styles.input, flex: 1, marginBottom: 0,
                border: '1px solid ' + COLORS.teal,
              }}
              placeholder={isListening ? "Listening..." : "Ask ANIMA anything..."}
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && sendMessage()}
            />
            <button
              style={{
                width: '52px', height: '52px', borderRadius: '12px',
                border: 'none', cursor: 'pointer', flexShrink: 0,
                background: isListening ? 'rgba(255,68,68,0.15)' : 'rgba(0,212,212,0.15)',
                border: isListening ? '1px solid #FF4444' : '1px solid ' + COLORS.teal,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '2px', transition: 'all 0.2s',
              }}
              onClick={startVoice}
              title="Voice input"
            >
              {isListening ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  {[1,2,3,4,5].map(n => (
                    <div key={n} style={{
                      width: '3px',
                      height: `${[8,14,20,14,8][n-1]}px`,
                      background: '#FF4444',
                      borderRadius: '2px',
                      animation: `waveform 0.8s ease-in-out infinite`,
                      animationDelay: `${(n-1) * 0.1}s`,
                    }} />
                  ))}
                </div>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              )}
            </button>
            <button
              style={{
                ...styles.btnPrimary, width: 'auto', padding: '16px 20px',
                opacity: userInput.trim() && !chatLoading ? 1 : 0.4,
              }}
              disabled={!userInput.trim() || chatLoading}
              onClick={sendMessage}
            >
              ➤
            </button>
          </div>
        </div>
    </div>
  );
}

function ProfileScreen({ user, token, onLogout, onSave }) {
  const [form, setForm] = useState({
    age: user?.age || "",
    weight: user?.weight || "",
    height: user?.height || "",
    height: user?.height || "",
    goal: user?.goal || "",
    fitness_goal: user?.fitness_goal || "maintain",
    activity_level: user?.activity_level || "moderate",
    diet_approach: user?.diet_approach || "standard",
    phone: user?.phone || "",
    sms_reminder_enabled: user?.sms_reminder_enabled || false,
    sms_reminder_time: user?.sms_reminder_time || "21:00",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm({
      age: user?.age || "",
      weight: user?.weight || "",
      height: user?.height || "",
      goal: user?.goal || "",
      fitness_goal: user?.fitness_goal || "maintain",
      activity_level: user?.activity_level || "moderate",
      diet_approach: user?.diet_approach || "standard",
    phone: user?.phone || "",
    sms_reminder_enabled: user?.sms_reminder_enabled || false,
    sms_reminder_time: user?.sms_reminder_time || "21:00",
    });
  }, [user]);

  const handleSave = async () => {
    try {
      const res = await fetch(`${API_URL}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          age: parseInt(form.age),
          weight: parseFloat(form.weight),
          height: parseFloat(form.height),
          goal: form.goal,
          fitness_goal: form.fitness_goal,
          activity_level: form.activity_level,
          diet_approach: form.diet_approach,
          experience_level: form.experience_level,
          phone: form.phone,
          sms_reminder_enabled: form.sms_reminder_enabled,
          sms_reminder_time: form.sms_reminder_time,
        }),
      });
      const data = await res.json();
      if (data.message) {
        localStorage.setItem("forima_user", JSON.stringify(data.user));
        onSave(data.user);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (e) {}
  };

  return (
    <div style={{ ...styles.screen, paddingBottom: "100px" }}>
      <AppHeader title="Athlete Profile" user={user} onProfileTap={() => {}} />
      <div style={styles.content}>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.gold})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "36px",
            margin: "0 auto 12px",
          }}>
            {user?.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div style={{ fontSize: "24px", fontWeight: "900", letterSpacing: "4px", color: COLORS.white }}>
            {user?.name?.toUpperCase()}
          </div>
          <div style={{ fontSize: "16px", color: COLORS.silver }}>{user?.email}</div>
        </div>

        {saved && (
          <div style={{
            background: "rgba(0,212,212,0.1)",
            border: `1px solid ${COLORS.teal}`,
            borderRadius: "12px",
            padding: "12px",
            marginBottom: "16px",
            color: COLORS.teal,
            textAlign: "center",
            fontSize: "16px",
            letterSpacing: "2px",
          }}>
            ✓ PROFILE UPDATED
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <div>
            <label style={styles.label}>Age</label>
            <input style={{ ...styles.input, textAlign: "center", marginBottom: 0 }} type="number"
              value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} />
          </div>
          <div>
            <label style={styles.label}>Weight (lbs)</label>
            <input style={{ ...styles.input, textAlign: "center", marginBottom: 0 }} type="number"
              value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} />
          </div>
          <div>
            <label style={styles.label}>Height (in)</label>
            <input style={{ ...styles.input, textAlign: "center", marginBottom: 0 }} type="number"
              value={form.height} onChange={e => setForm({ ...form, height: e.target.value })} />
          </div>
        </div>

        <label style={styles.label}>Specific Goal (optional)</label>
        <input style={styles.input} placeholder="e.g. Bench 225, lose 20 lbs, run a 5K"
          value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })} />

        <label style={styles.label}>Fitness Goal</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "16px" }}>
          {[
            { id: "aggressive_cut", icon: "🔥", label: "Aggressive Cut" },
            { id: "cut", icon: "📉", label: "Lean Cut" },
            { id: "maintain", icon: "⚖️", label: "Maintain" },
            { id: "recomp", icon: "🔄", label: "Recomp" },
            { id: "lean_bulk", icon: "💪", label: "Lean Bulk" },
            { id: "bulk", icon: "📈", label: "Bulk" },
          ].map(g => (
            <button key={g.id} onClick={() => setForm({...form, fitness_goal: g.id})}
              style={{ background: form.fitness_goal === g.id ? "rgba(0,212,212,0.15)" : "transparent", border: `1px solid ${form.fitness_goal === g.id ? COLORS.teal : COLORS.border}`, borderRadius: "10px", padding: "10px 4px", color: form.fitness_goal === g.id ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", letterSpacing: "1px", cursor: "pointer", textAlign: "center" }}>
              <div style={{ fontSize: "18px", marginBottom: "4px" }}>{g.icon}</div>
              {g.label}
            </button>
          ))}
        </div>

        <label style={styles.label}>Activity Level</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
          {[
            { id: "sedentary", label: "🪑 Sedentary", desc: "Desk job" },
            { id: "light", label: "🚶 Light", desc: "1-3 days/week" },
            { id: "moderate", label: "🏋️ Moderate", desc: "3-5 days/week" },
            { id: "active_job", label: "🏗️ Active Job", desc: "On feet all day" },
            { id: "very", label: "⚡ Very Active", desc: "6-7 days/week" },
            { id: "athlete", label: "🏆 Athlete", desc: "Twice daily" },
          ].map(a => (
            <button key={a.id} onClick={() => setForm({...form, activity_level: a.id})}
              style={{ background: form.activity_level === a.id ? "rgba(0,212,212,0.15)" : "transparent", border: `1px solid ${form.activity_level === a.id ? COLORS.teal : COLORS.border}`, borderRadius: "10px", padding: "10px 12px", color: form.activity_level === a.id ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", letterSpacing: "1px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: "8px" }}>
              <div>
                <div>{a.label}</div>
                <div style={{ fontSize: "10px", opacity: 0.7, fontWeight: "400" }}>{a.desc}</div>
              </div>
            </button>
          ))}
        </div>

        <label style={styles.label}>Dietary Approach</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
          {[
            { id: "standard", label: "🍽️ Standard" },
            { id: "lower_carb", label: "🥩 Lower Carb" },
            { id: "keto", label: "🧈 Keto" },
            { id: "carnivore", label: "🥩 Carnivore" },
            { id: "flexible_low_carb", label: "🔄 Flexible Low Carb" },
            { id: "plant_based", label: "🌱 Plant Based" },
          ].map(d => (
            <button key={d.id} onClick={() => setForm({...form, diet_approach: d.id})}
              style={{ background: form.diet_approach === d.id ? "rgba(212,160,23,0.15)" : "transparent", border: `1px solid ${form.diet_approach === d.id ? COLORS.gold : COLORS.border}`, borderRadius: "10px", padding: "10px 12px", color: form.diet_approach === d.id ? COLORS.gold : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", letterSpacing: "1px", cursor: "pointer", textAlign: "center" }}>
              {d.label}
            </button>
          ))}
        </div>

        <label style={styles.label}>Experience Level</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "16px" }}>
          {[
            { id: "beginner", icon: "🌱", label: "Beginner", desc: "Under 1 year" },
            { id: "intermediate", icon: "💪", label: "Intermediate", desc: "1-3 years" },
            { id: "advanced", icon: "⚡", label: "Advanced", desc: "3+ years" },
          ].map(e => (
            <button key={e.id} onClick={() => setForm({...form, experience_level: e.id})}
              style={{ background: form.experience_level === e.id ? "rgba(0,212,212,0.15)" : "transparent", border: `1px solid ${form.experience_level === e.id ? COLORS.teal : COLORS.border}`, borderRadius: "10px", padding: "12px 6px", color: form.experience_level === e.id ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", fontWeight: "700", cursor: "pointer", textAlign: "center" }}>
              <div style={{ fontSize: "20px", marginBottom: "4px" }}>{e.icon}</div>
              <div>{e.label}</div>
              <div style={{ fontSize: "9px", opacity: 0.6, marginTop: "2px" }}>{e.desc}</div>
            </button>
          ))}
        </div>

        {/* SMS Reminder — Premium Only */}
        <div style={{ ...styles.card, marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", letterSpacing: "3px", color: COLORS.teal, marginBottom: "16px" }}>⚡ NIGHTLY RECAP REMINDER</div>
          {!user?.premium ? (
            <div style={{ fontSize: "14px", color: COLORS.silver, textAlign: "center", padding: "8px 0" }}>
              🔒 Premium feature — upgrade to enable SMS reminders
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: "12px" }}>
                <div style={{ fontSize: "11px", letterSpacing: "2px", color: COLORS.silver, marginBottom: "8px" }}>PHONE NUMBER</div>
                <input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value})}
                  style={{ width: "100%", background: COLORS.charcoal, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "12px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none", boxSizing: "border-box" }}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <div style={{ fontSize: "11px", letterSpacing: "2px", color: COLORS.silver, marginBottom: "8px" }}>REMINDER TIME</div>
                <input
                  type="time"
                  value={form.sms_reminder_time}
                  onChange={e => setForm({...form, sms_reminder_time: e.target.value})}
                  style={{ width: "100%", background: COLORS.charcoal, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "12px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none", boxSizing: "border-box" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: "14px", color: COLORS.white, letterSpacing: "1px" }}>Enable SMS Reminder</div>
                <div
                  onClick={() => setForm({...form, sms_reminder_enabled: !form.sms_reminder_enabled})}
                  style={{ width: "48px", height: "26px", borderRadius: "13px", background: form.sms_reminder_enabled ? COLORS.teal : COLORS.border, cursor: "pointer", position: "relative", transition: "background 0.2s" }}
                >
                  <div style={{ position: "absolute", top: "3px", left: form.sms_reminder_enabled ? "24px" : "3px", width: "20px", height: "20px", borderRadius: "50%", background: COLORS.white, transition: "left 0.2s" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <button style={styles.btnPrimary} onClick={handleSave}>Save Profile</button>

        <div style={styles.divider} />

        {!user?.premium && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(212,160,23,0.08), rgba(212,160,23,0.04))',
            border: '1px solid ' + COLORS.gold,
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.gold, fontWeight: '700', marginBottom: '8px' }}>
              ⚡ UNLOCK PREMIUM
            </div>
            <div style={{ fontSize: '13px', color: COLORS.silver, lineHeight: '1.6', marginBottom: '16px' }}>
              Unlimited ANIMA coaching, full nutrition planning, and two-way AI conversation.
            </div>
            <button
              style={{ ...styles.btnPrimary, marginBottom: '8px' }}
              onClick={async () => {
                try {
                  const res = await fetch(API_URL + '/subscriptions/create', {
                    method: 'POST',
                    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                  });
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                } catch(e) { alert('Unable to start checkout. Try again.'); }
              }}
            >
              UPGRADE — $4.95/MO
            </button>
            <div style={{ fontSize: '11px', color: COLORS.silver }}>Cancel anytime · No commitment</div>
          </div>
        )}
        <div style={{ ...styles.card, marginBottom: "12px" }}>
          <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.gold, textTransform: "uppercase", marginBottom: "8px" }}>
            🔐 YOUR SOVEREIGNTY
          </div>
          <div style={{ fontSize: "16px", color: COLORS.silver, lineHeight: "1.7" }}>
            FORIMA is powered by ANIMA AI and built on ANIMACORE — decentralized infrastructure designed to ensure no single entity controls your health data or your AI coach.
          </div>
        </div>

        {user?.premium && (
          <div style={{ marginBottom: "12px" }}>
            <button style={{ ...styles.btnOutline, borderColor: COLORS.gold, color: COLORS.gold, marginBottom: "8px" }}
              onClick={async () => {
                if (!window.confirm("Cancel your Premium subscription? You will keep access until the end of your billing period.")) return;
                try {
                  const res = await fetch(API_URL + "/subscriptions/cancel", {
                    method: "POST",
                    headers: { "Authorization": "Bearer " + token, "Content-Type": "application/json" }
                  });
                  const data = await res.json();
                  if (data.message) {
                    alert("Subscription cancelled. You will retain Premium access until the end of your billing period.");
                  } else {
                    alert("Error: " + (data.error || "Could not cancel"));
                  }
                } catch(e) { alert("Connection error"); }
              }}>
              Cancel Premium Subscription
            </button>
          </div>
        )}
        <button style={{ ...styles.btnOutline, borderColor: COLORS.error, color: COLORS.error }} onClick={onLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
}




function CommonsScreen({ token, user, onTabChange }) {
  const CATEGORIES = [
    { id: "training", label: "TRAINING", icon: "💪" },
    { id: "nutrition", label: "NUTRITION", icon: "🥩" },
    { id: "research", label: "RESEARCH", icon: "📖" },
    { id: "deals", label: "DEALS", icon: "🛒" },
    { id: "qa", label: "Q&A", icon: "❓" },
    { id: "milestones", label: "MILESTONES", icon: "🔥" },
  ];

  const [view, setView] = useState("home");
  const [activeCategory, setActiveCategory] = useState(null);
  const [sortMode, setSortMode] = useState("recent");
  const [posts, setPosts] = useState([]);
  const [catStats, setCatStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPost, setNewPost] = useState("");
  const [error, setError] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => { fetchCategoryStats(); }, []);

  const fetchCategoryStats = async () => {
    try {
      const res = await fetch(`${API_URL}/community_posts/categories`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setCatStats(data);
    } catch(e) {}
  };

  const fetchPosts = async (category, sort = "recent") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.append("category", category);
      if (sort === "top") params.append("sort", "top");
      const res = await fetch(`${API_URL}/community_posts?${params}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setPosts(data);
    } catch(e) {}
    setLoading(false);
  };

  const openCategory = (cat) => {
    setActiveCategory(cat);
    setSortMode("recent");
    setView("feed");
    fetchPosts(cat.id, "recent");
  };

  const openAllPosts = (sort = "recent") => {
    setActiveCategory(null);
    setSortMode(sort);
    setView("feed");
    fetchPosts(null, sort);
  };

  const fetchReplies = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/replies`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setReplies(r => ({ ...r, [postId]: data }));
    } catch(e) {}
  };

  const submitPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/community_posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ title: newTitle.trim(), body: newPost.trim(), category: activeCategory?.id || "general" })
      });
      const data = await res.json();
      if (res.ok) {
        setPosts(p => [data, ...p]);
        setNewPost(""); setNewTitle(""); setShowCompose(false);
        setCelebrating(true);
        setTimeout(() => setCelebrating(false), 900);
        fetchCategoryStats();
      } else {
        setError(data.error || "Post rejected by ANIMA.");
      }
    } catch(e) { setError("Connection error."); }
    setPosting(false);
  };

  const submitReply = async (postId) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ body: replyText.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setReplies(r => ({ ...r, [postId]: [...(r[postId] || []), data] }));
        setPosts(p => p.map(post => post.id === postId ? { ...post, reply_count: (post.reply_count || 0) + 1 } : post));
        setReplyText("");
      }
    } catch(e) {}
    setSubmittingReply(false);
  };

  const toggleAlign = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/align`, {
        method: "POST", headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(p => p.map(post => post.id === postId ? { ...post, user_aligned: data.aligned, aligned_count: data.aligned_count } : post));
    } catch(e) {}
  };

  const toggleUpvote = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/upvote`, {
        method: "POST", headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(p => p.map(post => post.id === postId ? { ...post, upvote_count: data.upvote_count } : post));
    } catch(e) {}
  };

  const togglePin = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/pin`, {
        method: "POST", headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(p => p.map(post => post.id === postId ? { ...post, pinned: data.pinned } : post));
    } catch(e) {}
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setPosts(p => p.filter(post => post.id !== postId));
        setView("feed");
        setExpandedPost(null);
      }
    } catch(e) {}
  };

  const flagPost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/flag`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.flagged ? "Post flagged and sent for ANIMA review." : "Flag recorded. Post will be reviewed if more flags are received.");
      }
    } catch(e) {}
  };

  const approvePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/community_posts/${postId}/approve`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setPosts(p => p.filter(post => post.id !== postId));
        setView("feed");
        setExpandedPost(null);
      }
    } catch(e) {}
  };

  const openPost = (post) => {
    setExpandedPost(post);
    setView("post");
    if (!replies[post.id]) fetchReplies(post.id);
  };

  const getCatStat = (id) => catStats.find(c => c.id === id) || {};

  const PostCard = ({ post }) => (
    <div style={{ background: COLORS.card, border: `1px solid ${post.pinned ? COLORS.gold : post.post_type === "milestone" ? "rgba(0,212,212,0.3)" : COLORS.border}`, borderRadius: "14px", padding: "16px", marginBottom: "12px" }}>
      {post.pinned && <div style={{ fontSize: "9px", color: COLORS.gold, letterSpacing: "2px", marginBottom: "6px" }}>📌 PINNED</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>
            {post.post_type === "milestone" ? "⚡" : post.author?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "1px", color: post.post_type === "milestone" ? COLORS.teal : COLORS.white }}>{post.author}</div>
            <div style={{ fontSize: "10px", color: COLORS.silver }}>{post.time_ago}</div>
          </div>
        </div>
        {post.post_type === "milestone" && <div style={{ fontSize: "9px", letterSpacing: "2px", color: COLORS.teal, background: "rgba(0,212,212,0.1)", padding: "3px 8px", borderRadius: "4px" }}>ANIMA VERIFIED</div>}
      </div>
      {post.title && <div style={{ fontSize: "18px", fontWeight: "900", letterSpacing: "1px", color: COLORS.white, marginBottom: "8px" }}>{post.title}</div>}
      <div style={{ fontSize: "15px", color: COLORS.white, lineHeight: "1.6", marginBottom: "12px" }}>{post.body.length > 120 ? post.body.slice(0, 120) + "..." : post.body}</div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <button onClick={() => toggleAlign(post.id)} style={{ background: post.user_aligned ? "rgba(0,212,212,0.1)" : "transparent", border: `1px solid ${post.user_aligned ? COLORS.teal : COLORS.border}`, borderRadius: "8px", padding: "5px 12px", color: post.user_aligned ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", letterSpacing: "1px", cursor: "pointer" }}>
          ⚡ {post.user_aligned ? "ALIGNED" : "ALIGN"}{post.aligned_count > 0 ? ` · ${post.aligned_count}` : ""}
        </button>
        <button onClick={() => toggleUpvote(post.id)} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "5px 12px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", cursor: "pointer" }}>
          ▲ {post.upvote_count || 0}
        </button>
        <button onClick={() => openPost(post)} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "5px 12px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", cursor: "pointer" }}>
          💬 {post.reply_count || 0}
        </button>
        <button onClick={() => openPost(post)} style={{ background: "transparent", border: "none", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", cursor: "pointer", marginLeft: "auto" }}>READ MORE →</button>
      </div>
    </div>
  );

  if (view === "home") return (
    <div style={styles.screen}>
      <AppHeader title="The Commons" user={user} onProfileTap={() => onTabChange && onTabChange("profile")} />
      <div style={{ ...styles.content, paddingTop: "16px" }}>
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          <button onClick={() => openAllPosts("recent")} style={{ flex: 1, background: "rgba(0,212,212,0.08)", border: `1px solid ${COLORS.teal}`, borderRadius: "10px", padding: "12px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>🕐 RECENT</button>
          <button onClick={() => openAllPosts("top")} style={{ flex: 1, background: "rgba(212,160,23,0.08)", border: `1px solid ${COLORS.gold}`, borderRadius: "10px", padding: "12px", color: COLORS.gold, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>🔥 TOP</button>
        </div>
        {!user?.premium && (
          <div style={{ background: "rgba(212,160,23,0.06)", border: `1px solid rgba(212,160,23,0.2)`, borderRadius: "12px", padding: "14px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: "13px", fontWeight: "700", color: COLORS.gold, letterSpacing: "1px" }}>🔒 PREMIUM COMMUNITY</div>
              <div style={{ fontSize: "11px", color: COLORS.silver, marginTop: "3px" }}>Upgrade to post, reply, and join the conversation.</div>
            </div>
            <button onClick={() => onTabChange && onTabChange("profile")} style={{ background: COLORS.gold, border: "none", borderRadius: "8px", padding: "8px 14px", color: COLORS.black, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", fontWeight: "700", letterSpacing: "1px", cursor: "pointer", flexShrink: 0 }}>UPGRADE</button>
          </div>
        )}
        <div style={{ fontSize: "11px", letterSpacing: "3px", color: COLORS.silver, marginBottom: "12px" }}>BROWSE BY CATEGORY</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          {CATEGORIES.map(cat => {
            const stat = getCatStat(cat.id);
            return (
              <div key={cat.id} onClick={() => openCategory(cat)} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "14px", padding: "16px", cursor: "pointer" }}>
                <div style={{ fontSize: "28px", marginBottom: "8px" }}>{cat.icon}</div>
                <div style={{ fontSize: "14px", fontWeight: "900", letterSpacing: "2px", color: COLORS.white, marginBottom: "4px" }}>{cat.label}</div>
                <div style={{ fontSize: "11px", color: COLORS.silver }}>{stat.count || 0} posts</div>
                <div style={{ fontSize: "10px", color: COLORS.teal, marginTop: "2px", opacity: 0.7 }}>{stat.last_activity || "Be first to post"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (view === "post" && expandedPost) return (
    <div style={styles.screen}>
      <div style={{ background: "rgba(17,19,24,0.97)", borderBottom: `1px solid ${COLORS.border}`, padding: "60px 24px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={() => setView("feed")} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px 16px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", letterSpacing: "2px", cursor: "pointer" }}>← BACK</button>
        <div style={{ fontSize: "13px", color: COLORS.teal, letterSpacing: "2px" }}>{activeCategory?.label || "THE COMMONS"}</div>
      </div>
      <div style={{ ...styles.content, paddingTop: "16px" }}>
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "14px", padding: "20px", marginBottom: "20px" }}>
          {expandedPost.pinned && <div style={{ fontSize: "10px", color: COLORS.gold, letterSpacing: "2px", marginBottom: "8px" }}>📌 PINNED</div>}
          {expandedPost.title && <div style={{ fontSize: "22px", fontWeight: "900", letterSpacing: "2px", color: COLORS.white, marginBottom: "12px" }}>{expandedPost.title}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>
              {expandedPost.author?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: "700", color: COLORS.white }}>{expandedPost.author}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver }}>{expandedPost.time_ago}</div>
            </div>
          </div>
          <div style={{ fontSize: "16px", color: COLORS.white, lineHeight: "1.7", marginBottom: "16px" }}>{expandedPost.body}</div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button onClick={() => toggleAlign(expandedPost.id)} style={{ background: expandedPost.user_aligned ? "rgba(0,212,212,0.1)" : "transparent", border: `1px solid ${expandedPost.user_aligned ? COLORS.teal : COLORS.border}`, borderRadius: "8px", padding: "6px 14px", color: expandedPost.user_aligned ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", cursor: "pointer" }}>
              ⚡ ALIGN {expandedPost.aligned_count > 0 && `· ${expandedPost.aligned_count}`}
            </button>
            <button onClick={() => toggleUpvote(expandedPost.id)} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "6px 14px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", cursor: "pointer" }}>
              ▲ {expandedPost.upvote_count || 0}
            </button>
            {user?.admin && (
              <button onClick={() => togglePin(expandedPost.id)} style={{ background: expandedPost.pinned ? "rgba(212,160,23,0.1)" : "transparent", border: `1px solid ${expandedPost.pinned ? COLORS.gold : COLORS.border}`, borderRadius: "8px", padding: "6px 14px", color: expandedPost.pinned ? COLORS.gold : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", cursor: "pointer" }}>
                📌 {expandedPost.pinned ? "UNPIN" : "PIN"}
              </button>
            )}
            {(user?.admin || expandedPost.is_admin) && (
              <button onClick={() => deletePost(expandedPost.id)} style={{ background: "rgba(255,59,48,0.1)", border: "1px solid rgba(255,59,48,0.3)", borderRadius: "8px", padding: "6px 14px", color: "#FF3B30", fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", cursor: "pointer" }}>
                🗑 DELETE
              </button>
            )}
            {!expandedPost.is_admin && (
              <button onClick={() => flagPost(expandedPost.id)} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "6px 14px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", cursor: "pointer" }}>
                🚩 FLAG
              </button>
            )}
          </div>
        </div>
        <div style={{ fontSize: "11px", letterSpacing: "3px", color: COLORS.silver, marginBottom: "12px" }}>REPLIES ({expandedPost.reply_count || 0})</div>
        {(replies[expandedPost.id] || []).map((r, i) => (
          <div key={i} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "14px", marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: COLORS.teal }}>{r.author}</div>
              <div style={{ fontSize: "10px", color: COLORS.silver }}>{r.time_ago}</div>
            </div>
            <div style={{ fontSize: "15px", color: COLORS.white, lineHeight: "1.6" }}>{r.body}</div>
          </div>
        ))}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "12px", padding: "14px", marginTop: "12px" }}>
          <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Write a reply..." rows={3}
            style={{ width: "100%", background: "transparent", border: "none", color: COLORS.white, fontSize: "15px", fontFamily: "'Rajdhani', sans-serif", resize: "none", outline: "none" }} />
          <button onClick={() => submitReply(expandedPost.id)} disabled={submittingReply || !replyText.trim()}
            style={{ ...styles.btnPrimary, width: "100%", marginTop: "8px", opacity: submittingReply || !replyText.trim() ? 0.5 : 1 }}>
            {submittingReply ? "POSTING..." : "POST REPLY"}
          </button>
        </div>
      </div>
    </div>
  );

  const currentCat = CATEGORIES.find(c => c.id === activeCategory?.id);
  return (
    <div style={styles.screen}>
      <CelebrationBurst show={celebrating} />
      <div style={{ background: "rgba(17,19,24,0.97)", borderBottom: `1px solid ${COLORS.border}`, padding: "60px 24px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
          <button onClick={() => setView("home")} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px 16px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", letterSpacing: "2px", cursor: "pointer" }}>← HOME</button>
          <div style={{ fontSize: "20px" }}>{currentCat?.icon || "⚡"}</div>
          <div style={{ fontSize: "16px", fontWeight: "900", letterSpacing: "3px", color: COLORS.white }}>{currentCat?.label || "ALL POSTS"}</div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => { setSortMode("recent"); fetchPosts(activeCategory?.id, "recent"); }}
            style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `1px solid ${sortMode === "recent" ? COLORS.teal : COLORS.border}`, background: sortMode === "recent" ? "rgba(0,212,212,0.1)" : "transparent", color: sortMode === "recent" ? COLORS.teal : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", letterSpacing: "2px", cursor: "pointer" }}>RECENT</button>
          <button onClick={() => { setSortMode("top"); fetchPosts(activeCategory?.id, "top"); }}
            style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `1px solid ${sortMode === "top" ? COLORS.gold : COLORS.border}`, background: sortMode === "top" ? "rgba(212,160,23,0.1)" : "transparent", color: sortMode === "top" ? COLORS.gold : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", letterSpacing: "2px", cursor: "pointer" }}>TOP</button>
          {user?.premium ? (
            <button onClick={() => setShowCompose(true)}
              style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `1px solid ${COLORS.teal}`, background: "rgba(0,212,212,0.08)", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", letterSpacing: "2px", cursor: "pointer", fontWeight: "700" }}>+ POST</button>
          ) : (
            <button onClick={() => onTabChange && onTabChange("profile")}
              style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `1px solid ${COLORS.gold}`, background: "rgba(212,160,23,0.08)", color: COLORS.gold, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", letterSpacing: "2px", cursor: "pointer", fontWeight: "700" }}>🔒 UPGRADE</button>
          )}
        </div>
      </div>
      <div style={{ ...styles.content, paddingTop: "16px" }}>
        {showCompose && user?.premium && (
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.teal}`, borderRadius: "14px", padding: "16px", marginBottom: "20px" }}>
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Title (optional)" maxLength={100}
              style={{ width: "100%", background: "transparent", border: "none", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.white, fontSize: "18px", fontWeight: "700", fontFamily: "'Rajdhani', sans-serif", outline: "none", marginBottom: "12px", paddingBottom: "8px", boxSizing: "border-box" }} />
            <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder={`Share something in ${currentCat?.label || "The Commons"}...`} maxLength={500} rows={4}
              style={{ width: "100%", background: "transparent", border: "none", color: COLORS.white, fontSize: "15px", fontFamily: "'Rajdhani', sans-serif", resize: "none", outline: "none", lineHeight: "1.6" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
              <span style={{ fontSize: "11px", color: newPost.length > 450 ? COLORS.gold : COLORS.teal }}>{newPost.length}/500</span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => { setShowCompose(false); setNewPost(""); setNewTitle(""); setError(""); }}
                  style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px 16px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", cursor: "pointer" }}>CANCEL</button>
                <button onClick={submitPost} disabled={posting || !newPost.trim()}
                  style={{ ...styles.btnPrimary, padding: "8px 20px", opacity: posting || !newPost.trim() ? 0.5 : 1 }}>
                  {posting ? "ANIMA REVIEWING..." : "POST"}
                </button>
              </div>
            </div>
            {error && <div style={{ fontSize: "13px", color: COLORS.gold, marginTop: "8px" }}>⚠ {error}</div>}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: COLORS.silver, fontSize: "13px", letterSpacing: "3px" }}>LOADING...</div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: "32px", marginBottom: "16px" }}>{currentCat?.icon || "⚡"}</div>
            <div style={{ fontSize: "14px", color: COLORS.silver, letterSpacing: "2px" }}>NO POSTS YET.</div>
            <div style={{ fontSize: "13px", color: COLORS.silver, marginTop: "8px", opacity: 0.6 }}>Be the first to post here.</div>
          </div>
        ) : posts.map(post => <PostCard key={post.id} post={post} />)}
      </div>
    </div>
  );
}

function AppHeader({ title, user, onProfileTap }) {
  return (
    <div style={styles.header}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={styles.logo}>FORIMA<span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "2px", color: COLORS.teal, verticalAlign: "super", marginLeft: "8px", opacity: 0.7, WebkitTextFillColor: COLORS.teal }}>BETA</span></div>
          {title && <div style={styles.tagline}>{title}</div>}
        </div>
        {user && (
          <div onClick={onProfileTap} style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", marginTop: "4px", flexShrink: 0 }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "rgba(0,212,212,0.15)", border: `2px solid ${COLORS.teal}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "900", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif" }}>
              {user?.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div style={{ fontSize: "8px", color: COLORS.teal, letterSpacing: "1px", marginTop: "3px" }}>PROFILE</div>
          </div>
        )}
      </div>
    </div>
  );
}


function ExerciseAnimation({ name, size=70 }) {
  const n = (name || "").toLowerCase().trim();
  const getSvg = () => {
    if (n.includes("squat") && !n.includes("belt") && !n.includes("hack")) return `<style>@keyframes sq{0%,100%{transform:translateY(0)}50%{transform:translateY(10px)}}.sq{animation:sq 1.4s ease-in-out infinite;}</style><g class="sq"><circle cx="40" cy="14" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="20" width="14" height="18" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="26" x2="22" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="26" x2="58" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="38" x2="32" y2="58" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="38" x2="48" y2="58" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="14" y1="26" x2="66" y2="26" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("bench") && !n.includes("incline")) return `<style>@keyframes bp{0%,100%{transform:translateY(4px)}50%{transform:translateY(-6px)}}.bp{animation:bp 1.4s ease-in-out infinite;}</style><rect x="10" y="52" width="60" height="10" rx="4" fill="#1E2530"/><circle cx="40" cy="36" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="42" width="14" height="14" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="40" y1="56" x2="32" y2="70" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="56" x2="48" y2="70" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="bp"><line x1="12" y1="46" x2="68" y2="46" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="14" cy="46" r="5" fill="#D4A017" opacity="0.8"/><circle cx="66" cy="46" r="5" fill="#D4A017" opacity="0.8"/></g>`;
    if (n.includes("incline")) return `<style>@keyframes ip{0%,100%{transform:translateY(3px)}50%{transform:translateY(-5px)}}.ip{animation:ip 1.4s ease-in-out infinite;}</style><line x1="10" y1="70" x2="55" y2="40" stroke="#1E2530" stroke-width="8" stroke-linecap="round"/><circle cx="42" cy="28" r="7" fill="#00D4D4" opacity="0.9"/><rect x="36" y="34" width="12" height="15" rx="4" fill="#00D4D4" opacity="0.8" transform="rotate(-15 42 40)"/><g class="ip"><line x1="15" y1="38" x2="65" y2="28" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="16" cy="38" r="5" fill="#D4A017" opacity="0.8"/><circle cx="64" cy="28" r="5" fill="#D4A017" opacity="0.8"/></g>`;
    if (n.includes("overhead press") || (n.includes("shoulder press") && !n.includes("dumbbell") && !n.includes("db"))) return `<style>@keyframes ohp{0%,100%{transform:translateY(6px)}50%{transform:translateY(-8px)}}.ohp{animation:ohp 1.4s ease-in-out infinite;}</style><circle cx="40" cy="58" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="62" width="14" height="14" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="66" x2="22" y2="60" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="66" x2="58" y2="60" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="ohp"><line x1="12" y1="44" x2="68" y2="44" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="13" cy="44" r="5" fill="#D4A017" opacity="0.8"/><circle cx="67" cy="44" r="5" fill="#D4A017" opacity="0.8"/></g>`;
    if ((n.includes("dumbbell")||n.includes("db"))&&n.includes("shoulder")) return `<style>@keyframes dbsp{0%,100%{transform:translateY(5px)}50%{transform:translateY(-8px)}}.dbsp{animation:dbsp 1.4s ease-in-out infinite;}</style><circle cx="40" cy="48" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="54" width="14" height="14" rx="4" fill="#00D4D4" opacity="0.8"/><g class="dbsp"><line x1="33" y1="58" x2="18" y2="50" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="58" x2="62" y2="50" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><rect x="12" y="46" width="10" height="6" rx="3" fill="#D4A017" opacity="0.9"/><rect x="58" y="46" width="10" height="6" rx="3" fill="#D4A017" opacity="0.9"/></g>`;
    if (n.includes("push")&&(n.includes("up")||n.includes("-up"))) return `<style>@keyframes pu{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}.pu{animation:pu 1.4s ease-in-out infinite;}</style><line x1="15" y1="68" x2="25" y2="68" stroke="#1E2530" stroke-width="4"/><line x1="55" y1="68" x2="65" y2="68" stroke="#1E2530" stroke-width="4"/><g class="pu"><circle cx="20" cy="48" r="7" fill="#00D4D4" opacity="0.9"/><line x1="20" y1="54" x2="60" y2="60" stroke="#00D4D4" stroke-width="8" stroke-linecap="round"/><line x1="20" y1="54" x2="22" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="58" y1="60" x2="60" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("dip")) return `<style>@keyframes dip{0%,100%{transform:translateY(-6px)}50%{transform:translateY(6px)}}.dip{animation:dip 1.4s ease-in-out infinite;}</style><line x1="15" y1="20" x2="15" y2="75" stroke="#1E2530" stroke-width="5" stroke-linecap="round"/><line x1="65" y1="20" x2="65" y2="75" stroke="#1E2530" stroke-width="5" stroke-linecap="round"/><g class="dip"><circle cx="40" cy="30" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="36" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="40" x2="17" y2="32" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="40" x2="63" y2="32" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="52" x2="34" y2="65" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="52" x2="46" y2="65" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("tricep")||n.includes("pushdown")) return `<style>@keyframes tp{0%,100%{transform:rotate(-30deg)}50%{transform:rotate(10deg)}}.tp{animation:tp 1.4s ease-in-out infinite;transform-origin:30px 48px;}</style><circle cx="40" cy="8" r="5" fill="#1E2530"/><line x1="40" y1="12" x2="40" y2="40" stroke="#D4A017" stroke-width="3"/><circle cx="40" cy="28" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="34" width="14" height="14" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="40" x2="28" y2="50" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="tp"><line x1="28" y1="50" x2="22" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g><line x1="47" y1="40" x2="52" y2="50" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="52" y1="50" x2="58" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/>`;
    if (n.includes("skull")) return `<style>@keyframes sc{0%,100%{transform:rotate(0deg)}50%{transform:rotate(-50deg)}}.sc{animation:sc 1.4s ease-in-out infinite;transform-origin:40px 44px;}</style><rect x="10" y="52" width="60" height="8" rx="3" fill="#1E2530"/><circle cx="40" cy="36" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="42" width="14" height="14" rx="4" fill="#00D4D4" opacity="0.8"/><g class="sc"><line x1="34" y1="44" x2="30" y2="32" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="46" y1="44" x2="50" y2="32" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="22" y1="32" x2="58" y2="32" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="22" cy="32" r="4" fill="#D4A017" opacity="0.8"/><circle cx="58" cy="32" r="4" fill="#D4A017" opacity="0.8"/></g>`;
    if (n.includes("barbell row")||(n.includes("row")&&!n.includes("dumbbell")&&!n.includes("db")&&!n.includes("cable")&&!n.includes("machine"))) return `<style>@keyframes br{0%,100%{transform:translateY(4px)}50%{transform:translateY(-6px)}}.br{animation:br 1.4s ease-in-out infinite;}</style><circle cx="40" cy="26" r="7" fill="#00D4D4" opacity="0.9"/><rect x="34" y="32" width="12" height="16" rx="4" fill="#00D4D4" opacity="0.8" transform="rotate(20 40 40)"/><line x1="40" y1="48" x2="32" y2="65" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="48" x2="48" y2="65" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="br"><line x1="12" y1="58" x2="68" y2="58" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="13" cy="58" r="5" fill="#D4A017" opacity="0.8"/><circle cx="67" cy="58" r="5" fill="#D4A017" opacity="0.8"/><line x1="30" y1="58" x2="32" y2="46" stroke="#00D4D4" stroke-width="3" stroke-linecap="round"/><line x1="50" y1="58" x2="48" y2="46" stroke="#00D4D4" stroke-width="3" stroke-linecap="round"/></g>`;
    if ((n.includes("dumbbell")||n.includes("db"))&&n.includes("row")) return `<style>@keyframes dbr{0%,100%{transform:translateY(5px)}50%{transform:translateY(-7px)}}.dbr{animation:dbr 1.4s ease-in-out infinite;}</style><rect x="8" y="52" width="20" height="8" rx="3" fill="#1E2530"/><circle cx="24" cy="36" r="6" fill="#00D4D4" opacity="0.9"/><rect x="18" y="42" width="12" height="14" rx="4" fill="#00D4D4" opacity="0.8" transform="rotate(15 24 48)"/><line x1="18" y1="44" x2="12" y2="52" stroke="#00D4D4" stroke-width="3" stroke-linecap="round"/><g class="dbr"><line x1="30" y1="46" x2="52" y2="52" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><rect x="50" y="48" width="14" height="7" rx="3" fill="#D4A017" opacity="0.9"/></g>`;
    if (n.includes("pull")&&(n.includes("up")||n.includes("-up"))) return `<style>@keyframes pup{0%,100%{transform:translateY(8px)}50%{transform:translateY(-4px)}}.pup{animation:pup 1.4s ease-in-out infinite;}</style><line x1="10" y1="10" x2="70" y2="10" stroke="#1E2530" stroke-width="6" stroke-linecap="round"/><g class="pup"><circle cx="40" cy="26" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="32" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="36" x2="22" y2="18" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="36" x2="58" y2="18" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="48" x2="34" y2="62" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="48" x2="46" y2="62" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("lat")&&n.includes("pull")) return `<style>@keyframes lp{0%,100%{transform:translateY(-6px)}50%{transform:translateY(6px)}}.lp{animation:lp 1.4s ease-in-out infinite;}</style><line x1="10" y1="10" x2="70" y2="10" stroke="#1E2530" stroke-width="5"/><line x1="40" y1="10" x2="40" y2="20" stroke="#D4A017" stroke-width="3"/><circle cx="40" cy="48" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="54" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="40" y1="54" x2="32" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="54" x2="48" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="lp"><line x1="14" y1="32" x2="66" y2="32" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><line x1="14" y1="32" x2="30" y2="46" stroke="#00D4D4" stroke-width="3" stroke-linecap="round"/><line x1="66" y1="32" x2="50" y2="46" stroke="#00D4D4" stroke-width="3" stroke-linecap="round"/></g>`;
    if (n.includes("curl")&&!n.includes("leg")&&!n.includes("lying")&&!n.includes("hammer")) return `<style>@keyframes cu{0%,100%{transform:rotate(10deg)}50%{transform:rotate(-60deg)}}.cu{animation:cu 1.4s ease-in-out infinite;transform-origin:28px 44px;}.cu2{animation:cu 1.4s ease-in-out infinite 0.7s;transform-origin:52px 44px;}</style><circle cx="40" cy="22" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="28" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="34" x2="28" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="34" x2="52" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="44" x2="32" y2="60" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="44" x2="48" y2="60" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="cu"><line x1="28" y1="44" x2="22" y2="56" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><rect x="14" y="52" width="12" height="6" rx="3" fill="#D4A017" opacity="0.9"/></g><g class="cu2"><line x1="52" y1="44" x2="58" y2="56" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><rect x="54" y="52" width="12" height="6" rx="3" fill="#D4A017" opacity="0.9"/></g>`;
    if (n.includes("hammer")) return `<style>@keyframes hc{0%,100%{transform:rotate(5deg)}50%{transform:rotate(-55deg)}}.hc{animation:hc 1.4s ease-in-out infinite;transform-origin:30px 48px;}</style><circle cx="40" cy="22" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="28" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="34" x2="30" y2="46" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="hc"><line x1="30" y1="46" x2="24" y2="60" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><rect x="18" y="56" width="8" height="14" rx="3" fill="#D4A017" opacity="0.9"/></g>`;
    if (n.includes("face pull")) return `<style>@keyframes fp{0%,100%{transform:translateX(8px)}50%{transform:translateX(-4px)}}.fp{animation:fp 1.4s ease-in-out infinite;}</style><circle cx="8" cy="40" r="5" fill="#1E2530"/><line x1="8" y1="40" x2="28" y2="40" stroke="#D4A017" stroke-width="3"/><circle cx="40" cy="30" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="36" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="40" y1="50" x2="32" y2="64" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="50" x2="48" y2="64" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><g class="fp"><line x1="33" y1="40" x2="24" y2="36" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="40" x2="24" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("deadlift")&&!n.includes("romanian")&&!n.includes("rdl")&&!n.includes("stiff")&&!n.includes("lying")) return `<style>@keyframes dl{0%,100%{transform:translateY(8px) rotate(15deg)}50%{transform:translateY(0) rotate(0deg)}}.dl{animation:dl 1.6s ease-in-out infinite;transform-origin:center bottom;}</style><g class="dl"><circle cx="40" cy="18" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="24" width="14" height="18" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="30" x2="22" y2="46" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="30" x2="58" y2="46" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="42" x2="34" y2="56" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="42" x2="46" y2="56" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g><line x1="10" y1="68" x2="70" y2="68" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="12" cy="68" r="6" fill="#D4A017" opacity="0.8"/><circle cx="68" cy="68" r="6" fill="#D4A017" opacity="0.8"/>`;
    if (n.includes("romanian")||n.includes("rdl")||(n.includes("stiff")&&n.includes("leg"))) return `<style>@keyframes rdl{0%,100%{transform:rotate(-5deg)}50%{transform:rotate(25deg)}}.rdl{animation:rdl 1.4s ease-in-out infinite;transform-origin:40px 58px;}</style><g class="rdl"><circle cx="40" cy="16" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="22" width="14" height="18" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="28" x2="20" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="28" x2="60" y2="44" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g><line x1="40" y1="40" x2="34" y2="72" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="40" x2="46" y2="72" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="10" y1="62" x2="70" y2="62" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="12" cy="62" r="5" fill="#D4A017" opacity="0.8"/><circle cx="68" cy="62" r="5" fill="#D4A017" opacity="0.8"/>`;
    if (n.includes("leg press")) return `<style>@keyframes lpr{0%,100%{transform:scaleX(0.7)}50%{transform:scaleX(1)}}.lpr{animation:lpr 1.4s ease-in-out infinite;transform-origin:right center;}</style><rect x="8" y="30" width="18" height="44" rx="4" fill="#1E2530"/><circle cx="58" cy="28" r="7" fill="#00D4D4" opacity="0.9"/><rect x="52" y="34" width="12" height="16" rx="4" fill="#00D4D4" opacity="0.8" transform="rotate(-80 58 42)"/><g class="lpr"><line x1="54" y1="48" x2="36" y2="56" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="54" y1="56" x2="36" y2="64" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="36" y1="56" x2="26" y2="50" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="36" y1="64" x2="26" y2="58" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><rect x="18" y="46" width="10" height="16" rx="3" fill="#D4A017" opacity="0.8"/></g>`;
    if (n.includes("leg extension")||n.includes("leg ext")) return `<style>@keyframes le{0%,100%{transform:rotate(60deg)}50%{transform:rotate(0deg)}}.le{animation:le 1.4s ease-in-out infinite;transform-origin:40px 46px;}</style><rect x="10" y="30" width="60" height="10" rx="4" fill="#1E2530"/><circle cx="40" cy="18" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="24" width="14" height="14" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="36" y1="38" x2="36" y2="46" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="44" y1="38" x2="44" y2="46" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><g class="le"><line x1="36" y1="46" x2="32" y2="68" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="44" y1="46" x2="48" y2="68" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="28" y1="66" x2="52" y2="70" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("lying")&&(n.includes("curl")||n.includes("leg curl"))) return `<style>@keyframes llc{0%,100%{transform:rotate(0deg)}50%{transform:rotate(-60deg)}}.llc{animation:llc 1.4s ease-in-out infinite;transform-origin:50px 48px;}</style><rect x="8" y="50" width="64" height="8" rx="3" fill="#1E2530"/><circle cx="22" cy="36" r="7" fill="#00D4D4" opacity="0.9"/><line x1="22" y1="43" x2="50" y2="48" stroke="#00D4D4" stroke-width="8" stroke-linecap="round"/><g class="llc"><line x1="50" y1="48" x2="66" y2="42" stroke="#00D4D4" stroke-width="6" stroke-linecap="round"/><rect x="62" y="32" width="8" height="10" rx="3" fill="#D4A017" opacity="0.8"/></g>`;
    if (n.includes("lunge")) return `<style>@keyframes lu{0%,100%{transform:translateY(0)}50%{transform:translateY(8px)}}.lu{animation:lu 1.4s ease-in-out infinite;}</style><g class="lu"><circle cx="40" cy="14" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="20" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="26" x2="24" y2="34" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="26" x2="56" y2="34" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="36" x2="28" y2="52" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="40" y1="36" x2="52" y2="52" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="28" y1="52" x2="20" y2="68" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="52" y1="52" x2="58" y2="68" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("belt squat")||n.includes("belt")) return `<style>@keyframes bsq{0%,100%{transform:translateY(0)}50%{transform:translateY(12px)}}.bsq{animation:bsq 1.4s ease-in-out infinite;transform-origin:center bottom;}</style><rect x="28" y="65" width="24" height="10" rx="3" fill="#D4A017" opacity="0.8"/><g class="bsq"><circle cx="40" cy="12" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="18" width="14" height="18" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="24" x2="22" y2="36" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="24" x2="58" y2="36" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="36" x2="34" y2="52" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="40" y1="36" x2="46" y2="52" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="34" y1="52" x2="30" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="46" y1="52" x2="50" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("plank")) return `<style>@keyframes pl{0%,100%{opacity:1}50%{opacity:0.6}}.pl{animation:pl 2s ease-in-out infinite;}</style><g class="pl"><circle cx="18" cy="44" r="7" fill="#00D4D4" opacity="0.9"/><line x1="18" y1="50" x2="62" y2="54" stroke="#00D4D4" stroke-width="9" stroke-linecap="round"/><line x1="18" y1="52" x2="14" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="60" y1="54" x2="62" y2="66" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("crunch")&&!n.includes("cable")) return `<style>@keyframes cr{0%,100%{transform:rotate(0deg)}50%{transform:rotate(-25deg)}}.cr{animation:cr 1.4s ease-in-out infinite;transform-origin:40px 54px;}</style><line x1="10" y1="70" x2="70" y2="70" stroke="#1E2530" stroke-width="4"/><line x1="30" y1="70" x2="28" y2="56" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="50" y1="70" x2="52" y2="56" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><g class="cr"><circle cx="40" cy="30" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="36" width="14" height="18" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="42" x2="20" y2="46" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="42" x2="60" y2="46" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("cable")&&n.includes("crunch")) return `<style>@keyframes cc{0%,100%{transform:rotate(-10deg)}50%{transform:rotate(20deg)}}.cc{animation:cc 1.4s ease-in-out infinite;transform-origin:40px 54px;}</style><circle cx="40" cy="8" r="5" fill="#1E2530"/><line x1="40" y1="12" x2="40" y2="30" stroke="#D4A017" stroke-width="3"/><g class="cc"><circle cx="40" cy="30" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="36" width="14" height="18" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="40" y1="54" x2="32" y2="68" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="54" x2="48" y2="68" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g>`;
    if (n.includes("hip thrust")||n.includes("hip hinge")) return `<style>@keyframes ht{0%,100%{transform:translateY(6px) rotate(10deg)}50%{transform:translateY(-4px) rotate(-5deg)}}.ht{animation:ht 1.4s ease-in-out infinite;transform-origin:center center;}</style><rect x="8" y="55" width="20" height="14" rx="4" fill="#1E2530"/><g class="ht"><circle cx="40" cy="30" r="7" fill="#00D4D4" opacity="0.9"/><line x1="20" y1="52" x2="60" y2="44" stroke="#00D4D4" stroke-width="9" stroke-linecap="round"/><line x1="55" y1="46" x2="58" y2="64" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/><line x1="64" y1="44" x2="67" y2="62" stroke="#00D4D4" stroke-width="5" stroke-linecap="round"/></g><line x1="20" y1="42" x2="70" y2="42" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="20" cy="42" r="5" fill="#D4A017" opacity="0.8"/><circle cx="70" cy="42" r="5" fill="#D4A017" opacity="0.8"/>`;
    return `<style>@keyframes gl{0%,100%{transform:translateY(4px)}50%{transform:translateY(-6px)}}.gl{animation:gl 1.4s ease-in-out infinite;}</style><g class="gl"><circle cx="40" cy="20" r="7" fill="#00D4D4" opacity="0.9"/><rect x="33" y="26" width="14" height="16" rx="4" fill="#00D4D4" opacity="0.8"/><line x1="33" y1="32" x2="20" y2="40" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="47" y1="32" x2="60" y2="40" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="42" x2="32" y2="58" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/><line x1="40" y1="42" x2="48" y2="58" stroke="#00D4D4" stroke-width="4" stroke-linecap="round"/></g><line x1="12" y1="52" x2="68" y2="52" stroke="#D4A017" stroke-width="4" stroke-linecap="round"/><circle cx="13" cy="52" r="5" fill="#D4A017" opacity="0.8"/><circle cx="67" cy="52" r="5" fill="#D4A017" opacity="0.8"/>`;
  };
  return (<div style={{width:size,height:size,flexShrink:0,background:'rgba(0,0,0,0.3)',borderRadius:'8px',border:'1px solid rgba(0,212,212,0.15)',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}} dangerouslySetInnerHTML={{__html:'<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" width="'+size+'" height="'+size+'">'+getSvg()+'</svg>'}} />);
}

// Add this import at the very top of App.js after the first line

function ExerciseDemoModal({ name, onClose }) {
  const n = (name || "").toLowerCase().trim();

  // FLAT BARBELL BENCH PRESS
  const BenchPress = () => {
    const teal = "#00E5E5";
    const tealDark = "#007A8C";
    const tealMid = "#00B8CC";
    const gold = "#FBB03B";
    const goldDark = "#C07800";
    const skin = teal;

    return (
      <svg viewBox="0 0 320 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <defs>
          {/* Body gradients */}
          <linearGradient id="torsoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={tealMid} stopOpacity="1"/>
            <stop offset="50%" stopColor={teal} stopOpacity="1"/>
            <stop offset="100%" stopColor={tealDark} stopOpacity="1"/>
          </linearGradient>
          <linearGradient id="limbGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={tealDark} stopOpacity="1"/>
            <stop offset="40%" stopColor={teal} stopOpacity="1"/>
            <stop offset="100%" stopColor={tealDark} stopOpacity="1"/>
          </linearGradient>
          <linearGradient id="headGrad" cx="40%" cy="40%" r="60%" fx="40%" fy="40%" gradientUnits="objectBoundingBox">
            <stop offset="0%" stopColor={tealMid}/>
            <stop offset="100%" stopColor={tealDark}/>
          </linearGradient>
          <linearGradient id="barGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFD700"/>
            <stop offset="50%" stopColor={gold}/>
            <stop offset="100%" stopColor={goldDark}/>
          </linearGradient>
          <linearGradient id="plateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFD060"/>
            <stop offset="100%" stopColor={goldDark}/>
          </linearGradient>
          <linearGradient id="benchGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2A3A4A"/>
            <stop offset="100%" stopColor="#1A2530"/>
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="shadow">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.4"/>
          </filter>
        </defs>

        {/* BENCH */}
        <rect x="40" y="148" width="240" height="18" rx="6" fill="url(#benchGrad)" filter="url(#shadow)"/>
        <rect x="40" y="144" width="240" height="8" rx="4" fill="#3A4A5A"/>
        {/* Bench legs */}
        <rect x="55" y="166" width="10" height="28" rx="3" fill="#1A2530"/>
        <rect x="255" y="166" width="10" height="28" rx="3" fill="#1A2530"/>
        <rect x="48" y="190" width="24" height="5" rx="2" fill="#151E28"/>
        <rect x="248" y="190" width="24" height="5" rx="2" fill="#151E28"/>

        {/* === ANIMATED BODY === */}
        {/* The bar animates up/down — body stays on bench */}

        {/* HEAD */}
        <ellipse cx="268" cy="130" rx="16" ry="18" fill="url(#torsoGrad)" filter="url(#shadow)"/>
        {/* neck */}
        <rect x="261" y="144" width="14" height="10" rx="4" fill={tealMid}/>

        {/* TORSO — wide athletic chest */}
        <path d="M 245 148 Q 235 128 238 118 Q 248 108 268 110 Q 288 108 298 118 Q 301 128 291 148 Z"
          fill="url(#torsoGrad)" filter="url(#shadow)"/>
        {/* Chest muscle definition */}
        <path d="M 248 128 Q 260 122 268 126 Q 276 122 288 128 Q 280 138 268 136 Q 256 138 248 128 Z"
          fill={tealMid} opacity="0.5"/>
        {/* Abs hint */}
        <ellipse cx="268" cy="140" rx="12" ry="5" fill={tealDark} opacity="0.3"/>

        {/* UPPER LEGS — lying flat */}
        <path d="M 248 148 Q 230 150 190 152 Q 182 153 180 150 Q 182 144 190 144 Q 230 142 248 144 Z"
          fill="url(#limbGrad)" filter="url(#shadow)"/>
        <path d="M 291 148 Q 309 150 349 152 Q 357 153 359 150 Q 357 144 349 144 Q 309 142 291 144 Z"
          fill="url(#limbGrad)" filter="url(#shadow)"/>

        {/* LOWER LEGS — bent at knee, feet flat */}
        <path d="M 180 150 Q 165 160 160 178 Q 160 185 168 186 Q 176 186 178 178 Q 180 165 190 152 Z"
          fill="url(#limbGrad)" filter="url(#shadow)"/>
        <path d="M 359 150 Q 374 160 379 178 Q 379 185 371 186 Q 363 186 361 178 Q 359 165 349 152 Z"
          fill="url(#limbGrad)" filter="url(#shadow)"/>

        {/* FEET */}
        <ellipse cx="166" cy="188" rx="12" ry="5" fill={tealDark}/>
        <ellipse cx="373" cy="188" rx="12" ry="5" fill={tealDark}/>

        {/* UPPER ARMS — animated with bar */}
        <motion.g
          animate={{ rotate: [-35, -55, -35] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "248px 120px" }}
        >
          <path d="M 244 118 Q 236 122 228 138 Q 226 144 230 146 Q 235 148 238 144 Q 244 130 250 122 Z"
            fill="url(#limbGrad)" filter="url(#shadow)"/>
          {/* bicep peak */}
          <ellipse cx="236" cy="130" rx="7" ry="5" fill={tealMid} opacity="0.6" transform="rotate(-30 236 130)"/>
        </motion.g>

        <motion.g
          animate={{ rotate: [35, 55, 35] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "288px 120px" }}
        >
          <path d="M 292 118 Q 300 122 308 138 Q 310 144 306 146 Q 301 148 298 144 Q 292 130 286 122 Z"
            fill="url(#limbGrad)" filter="url(#shadow)"/>
          <ellipse cx="300" cy="130" rx="7" ry="5" fill={tealMid} opacity="0.6" transform="rotate(30 300 130)"/>
        </motion.g>

        {/* FOREARMS + BAR — full animated group */}
        <motion.g
          animate={{ y: [-22, 0, -22] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Left forearm */}
          <path d="M 228 138 Q 222 128 218 112 Q 216 106 220 104 Q 225 102 228 108 Q 232 122 234 136 Z"
            fill="url(#limbGrad)" filter="url(#shadow)"/>
          {/* Right forearm */}
          <path d="M 308 138 Q 314 128 318 112 Q 320 106 316 104 Q 311 102 308 108 Q 304 122 302 136 Z"
            fill="url(#limbGrad)" filter="url(#shadow)"/>

          {/* HANDS */}
          <ellipse cx="220" cy="104" rx="8" ry="6" fill={teal}/>
          <ellipse cx="316" cy="104" rx="8" ry="6" fill={teal}/>

          {/* BARBELL */}
          <rect x="110" y="100" width="240" height="8" rx="4" fill="url(#barGrad)" filter="url(#glow)"/>
          {/* Bar shine */}
          <rect x="110" y="100" width="240" height="3" rx="2" fill="#FFE080" opacity="0.5"/>

          {/* LEFT PLATES */}
          <rect x="108" y="88" width="20" height="32" rx="4" fill="url(#plateGrad)" filter="url(#shadow)"/>
          <rect x="106" y="91" width="4" height="26" rx="2" fill="#FFE080" opacity="0.4"/>
          <rect x="88" y="90" width="18" height="28" rx="3" fill="url(#plateGrad)" filter="url(#shadow)"/>
          <rect x="86" y="93" width="4" height="22" rx="2" fill="#FFE080" opacity="0.3"/>

          {/* RIGHT PLATES */}
          <rect x="232" y="88" width="20" height="32" rx="4" fill="url(#plateGrad)" filter="url(#shadow)"/>
          <rect x="250" y="91" width="4" height="26" rx="2" fill="#FFE080" opacity="0.4"/>
          <rect x="252" y="90" width="18" height="28" rx="3" fill="url(#plateGrad)" filter="url(#shadow)"/>
          <rect x="268" y="93" width="4" height="22" rx="2" fill="#FFE080" opacity="0.3"/>

          {/* Collar clips */}
          <rect x="128" y="97" width="6" height="14" rx="2" fill={gold} opacity="0.8"/>
          <rect x="226" y="97" width="6" height="14" rx="2" fill={gold} opacity="0.8"/>
        </motion.g>
      </svg>
    );
  };


  const InclineBenchPress = () => {
    const teal = "#00E5E5";
    const tealDark = "#007A8C";
    const tealMid = "#00B8CC";
    const gold = "#FBB03B";
    const goldDark = "#C07800";

    return (
      <svg viewBox="0 0 320 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <defs>
          <linearGradient id="ig-torso" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={tealMid}/><stop offset="50%" stopColor={teal}/><stop offset="100%" stopColor={tealDark}/>
          </linearGradient>
          <linearGradient id="ig-limb" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={tealDark}/><stop offset="40%" stopColor={teal}/><stop offset="100%" stopColor={tealDark}/>
          </linearGradient>
          <linearGradient id="ig-bar" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFD700"/><stop offset="50%" stopColor={gold}/><stop offset="100%" stopColor={goldDark}/>
          </linearGradient>
          <linearGradient id="ig-plate" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFD060"/><stop offset="100%" stopColor={goldDark}/>
          </linearGradient>
          <filter id="ig-shadow"><feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.4"/></filter>
          <filter id="ig-glow"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>

        {/* INCLINE BENCH */}
        <rect x="30" y="160" width="180" height="14" rx="5" fill="#2A3A4A" filter="url(#ig-shadow)"/>
        <rect x="30" y="156" width="180" height="7" rx="3" fill="#3A4A5A"/>
        {/* Inclined backrest */}
        <rect x="160" y="90" width="60" height="14" rx="5" fill="#2A3A4A" transform="rotate(-40 160 160)" filter="url(#ig-shadow)"/>
        <rect x="160" y="86" width="60" height="7" rx="3" fill="#3A4A5A" transform="rotate(-40 160 160)"/>
        {/* Legs */}
        <rect x="40" y="174" width="10" height="22" rx="3" fill="#1A2530"/>
        <rect x="195" y="174" width="10" height="22" rx="3" fill="#1A2530"/>

        {/* HEAD — angled back on incline */}
        <ellipse cx="230" cy="95" rx="15" ry="17" fill="url(#ig-torso)" filter="url(#ig-shadow)" transform="rotate(-40 230 95)"/>
        <rect x="222" y="108" width="12" height="9" rx="4" fill={tealMid} transform="rotate(-40 228 112)"/>

        {/* TORSO — on incline */}
        <path d="M 205 165 Q 196 140 200 118 Q 210 100 230 100 Q 248 102 254 118 Q 256 138 248 162 Z"
          fill="url(#ig-torso)" filter="url(#ig-shadow)" transform="rotate(-12 228 130)"/>
        {/* Chest definition */}
        <path d="M 208 138 Q 220 130 228 134 Q 236 130 246 138 Q 238 148 228 146 Q 218 148 208 138 Z"
          fill={tealMid} opacity="0.45" transform="rotate(-12 228 138)"/>

        {/* UPPER LEGS */}
        <path d="M 200 163 Q 175 165 140 166 Q 132 167 130 163 Q 132 157 140 157 Q 175 156 200 157 Z"
          fill="url(#ig-limb)" filter="url(#ig-shadow)"/>
        <path d="M 248 163 Q 260 165 278 166 Q 284 167 286 163 Q 284 157 278 157 Q 260 156 248 157 Z"
          fill="url(#ig-limb)" filter="url(#ig-shadow)"/>

        {/* LOWER LEGS bent */}
        <path d="M 130 163 Q 118 172 114 188 Q 114 194 122 194 Q 130 194 132 186 Q 134 172 140 164 Z"
          fill="url(#ig-limb)" filter="url(#ig-shadow)"/>
        <path d="M 286 163 Q 298 172 302 188 Q 302 194 294 194 Q 286 194 284 186 Q 282 172 278 164 Z"
          fill="url(#ig-limb)" filter="url(#ig-shadow)"/>

        {/* FEET */}
        <ellipse cx="120" cy="195" rx="11" ry="5" fill={tealDark}/>
        <ellipse cx="294" cy="195" rx="11" ry="5" fill={tealDark}/>

        {/* UPPER ARMS */}
        <motion.g animate={{rotate:[-40,-62,-40]}} transition={{duration:2,repeat:Infinity,ease:"easeInOut"}} style={{transformOrigin:"210px 115px"}}>
          <path d="M 207 113 Q 198 118 188 132 Q 185 138 189 141 Q 194 143 197 139 Q 205 126 212 118 Z"
            fill="url(#ig-limb)" filter="url(#ig-shadow)"/>
          <ellipse cx="194" cy="126" rx="7" ry="5" fill={tealMid} opacity="0.55" transform="rotate(-35 194 126)"/>
        </motion.g>
        <motion.g animate={{rotate:[40,62,40]}} transition={{duration:2,repeat:Infinity,ease:"easeInOut"}} style={{transformOrigin:"246px 115px"}}>
          <path d="M 249 113 Q 258 118 268 132 Q 271 138 267 141 Q 262 143 259 139 Q 251 126 244 118 Z"
            fill="url(#ig-limb)" filter="url(#ig-shadow)"/>
          <ellipse cx="262" cy="126" rx="7" ry="5" fill={tealMid} opacity="0.55" transform="rotate(35 262 126)"/>
        </motion.g>

        {/* FOREARMS + BAR animated group */}
        <motion.g animate={{y:[-20,0,-20]}} transition={{duration:2,repeat:Infinity,ease:"easeInOut"}}>
          {/* Left forearm */}
          <path d="M 188 132 Q 182 120 178 105 Q 176 98 180 96 Q 185 94 188 100 Q 192 114 194 130 Z"
            fill="url(#ig-limb)" filter="url(#ig-shadow)"/>
          {/* Right forearm */}
          <path d="M 268 132 Q 274 120 278 105 Q 280 98 276 96 Q 271 94 268 100 Q 264 114 262 130 Z"
            fill="url(#ig-limb)" filter="url(#ig-shadow)"/>
          {/* Hands */}
          <ellipse cx="180" cy="96" rx="8" ry="6" fill={teal}/>
          <ellipse cx="276" cy="96" rx="8" ry="6" fill={teal}/>
          {/* BAR */}
          <rect x="80" y="92" width="220" height="8" rx="4" fill="url(#ig-bar)" filter="url(#ig-glow)"/>
          <rect x="80" y="92" width="220" height="3" rx="2" fill="#FFE080" opacity="0.5"/>
          {/* Left plates */}
          <rect x="78" y="82" width="18" height="28" rx="4" fill="url(#ig-plate)" filter="url(#ig-shadow)"/>
          <rect x="60" y="84" width="16" height="24" rx="3" fill="url(#ig-plate)" filter="url(#ig-shadow)"/>
          {/* Right plates */}
          <rect x="204" y="82" width="18" height="28" rx="4" fill="url(#ig-plate)" filter="url(#ig-shadow)"/>
          <rect x="224" y="84" width="16" height="24" rx="3" fill="url(#ig-plate)" filter="url(#ig-shadow)"/>
          {/* Collars */}
          <rect x="96" y="89" width="5" height="14" rx="2" fill={gold} opacity="0.8"/>
          <rect x="199" y="89" width="5" height="14" rx="2" fill={gold} opacity="0.8"/>
        </motion.g>
      </svg>
    );
  };

  const getDemo = () => {
    if (n.includes("incline")) return <InclineBenchPress />;
    if ((n.includes("bench") || (n.includes("flat") && n.includes("press")) || (n.includes("barbell") && n.includes("press") && !n.includes("incline") && !n.includes("overhead"))) && !n.includes("dumbbell")) return <BenchPress />;
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9BA3AF", fontSize: "14px", letterSpacing: "2px" }}>
        DEMO COMING SOON
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0D1117", border: "1px solid rgba(0,212,212,0.3)", borderRadius: "20px", padding: "24px", width: "100%", maxWidth: "360px" }}>
        <div style={{ width: "100%", height: "200px", marginBottom: "16px" }}>
          {getDemo()}
        </div>
        <div style={{ fontSize: "18px", fontWeight: "900", letterSpacing: "3px", color: "#00D4D4", textAlign: "center" }}>{name?.toUpperCase()}</div>
        <div style={{ fontSize: "11px", color: "#9BA3AF", textAlign: "center", marginTop: "6px", letterSpacing: "1px" }}>TAP ANYWHERE TO CLOSE</div>
      </div>
    </div>
  );
}

function BYOPToggle({ token, user, onLogged }) {
  const [mode, setMode] = useState(() => localStorage.getItem('plan_mode') || 'anima');
  const [programText, setProgramText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [myProgram, setMyProgram] = useState(null);
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchActiveProgram();
  }, []);

  const fetchActiveProgram = async () => {
    try {
      const res = await fetch(`${API_URL}/user_programs/active`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.parsed_plan) {
        setMyProgram(data);
        setMode('mine');
        localStorage.setItem('plan_mode', 'mine');
      }
    } catch(e) {}
    setLoadingProgram(false);
  };

  const switchMode = (m) => {
    setMode(m);
    localStorage.setItem('plan_mode', m);
  };

  const submitProgram = async () => {
    if (!programText.trim()) return;
    setParsing(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/user_programs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ content: programText.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setMyProgram(data);
        setProgramText("");
        switchMode('mine');
      } else {
        setError(data.error || "Could not parse program. Try adding more structure.");
      }
    } catch(e) {
      setError("Connection error. Try again.");
    }
    setParsing(false);
  };

  const clearProgram = async () => {
    try {
      await fetch(`${API_URL}/user_programs`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      setMyProgram(null);
      switchMode('anima');
    } catch(e) {}
  };

  if (loadingProgram) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: COLORS.silver, fontSize: "13px", letterSpacing: "3px" }}>LOADING...</div>
  );

  return (
    <div>
      <div style={{ display: "flex", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "3px", marginBottom: "16px" }}>
        <button onClick={() => switchMode('anima')} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: mode === 'anima' ? COLORS.teal : "transparent", color: mode === 'anima' ? COLORS.black : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>⚡ ANIMA PLAN</button>
        <button onClick={() => switchMode('mine')} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: mode === 'mine' ? COLORS.gold : "transparent", color: mode === 'mine' ? COLORS.black : COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "13px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>💪 MY PROGRAM</button>
      </div>

      {mode === 'anima' && <AnimaPlanChecklist token={token} user={user} onLogged={onLogged} />}

      {mode === 'mine' && (
        <div>
          {!myProgram ? (
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "2px", color: COLORS.teal, marginBottom: "8px" }}>PASTE YOUR PROGRAM</div>
              <div style={{ fontSize: "12px", color: COLORS.silver, marginBottom: "12px", lineHeight: "1.6" }}>
                Paste your workout program in any format. ANIMA will parse it into a trackable plan.
              </div>
              <textarea
                value={programText}
                onChange={e => setProgramText(e.target.value)}
                placeholder={"Monday - Chest/Triceps\nBench Press: 4x6 @ 185lbs\nIncline DB: 3x8 @ 70lbs\n\nTuesday - Back/Biceps\nDeadlift: 4x4 @ 275lbs\n..."}
                rows={10}
                style={{ width: "100%", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "12px", padding: "14px", color: COLORS.white, fontSize: "14px", fontFamily: "'Rajdhani', sans-serif", resize: "vertical", outline: "none", lineHeight: "1.6", boxSizing: "border-box" }}
              />
              {error && <div style={{ fontSize: "13px", color: COLORS.gold, marginTop: "8px" }}>⚠ {error}</div>}
              <button onClick={submitProgram} disabled={parsing || !programText.trim()} style={{ ...styles.btnPrimary, width: "100%", marginTop: "12px", opacity: parsing || !programText.trim() ? 0.5 : 1 }}>
                {parsing ? "⚡ ANIMA PARSING..." : "LOAD MY PROGRAM"}
              </button>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <div>
                  <div style={{ fontSize: "16px", fontWeight: "900", letterSpacing: "2px", color: COLORS.white }}>{myProgram.parsed_plan.program_name || "MY PROGRAM"}</div>
                  <div style={{ fontSize: "11px", color: COLORS.silver, marginTop: "2px" }}>Tracked by ANIMA</div>
                </div>
                <button onClick={clearProgram} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "6px 12px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "11px", letterSpacing: "2px", cursor: "pointer" }}>CLEAR</button>
              </div>
              {myProgram.parsed_plan.days && myProgram.parsed_plan.days.map((day, di) => (
                <div key={di} style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "11px", letterSpacing: "3px", color: day.focus === "Rest" ? COLORS.silver : COLORS.teal, marginBottom: "8px", paddingBottom: "6px", borderBottom: `1px solid ${COLORS.border}` }}>
                    {day.day.toUpperCase()} — {day.focus.toUpperCase()}
                  </div>
                  {day.focus === "Rest" ? (
                    <div style={{ fontSize: "13px", color: COLORS.silver, fontStyle: "italic", padding: "8px 0" }}>Recovery day. Sleep and nutrition.</div>
                  ) : (
                    day.exercises && day.exercises.map((ex, ei) => (
                      <div key={ei} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "12px", padding: "14px 16px", marginBottom: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "15px", fontWeight: "700", letterSpacing: "2px", color: COLORS.white }}>{ex.name.toUpperCase()}</div>
                          <div style={{ fontSize: "13px", color: COLORS.silver, marginTop: "2px" }}>
                            {ex.sets} sets x {ex.reps} reps{ex.weight > 0 ? ` @ ${ex.weight}lbs` : " (bodyweight)"}
                          </div>
                          {ex.notes && <div style={{ fontSize: "11px", color: COLORS.teal, marginTop: "4px", opacity: 0.8 }}>{ex.notes}</div>}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/workouts`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                                body: JSON.stringify({ exercise: ex.name, sets: ex.sets, reps: parseInt(ex.reps) || 8, weight: ex.weight || 0, date: new Date().toISOString().split("T")[0] })
                              });
                              if (res.ok && onLogged) onLogged();
                            } catch(e) {}
                          }}
                          style={{ background: "rgba(0,212,212,0.1)", border: `1px solid ${COLORS.teal}`, borderRadius: "8px", padding: "8px 12px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontSize: "12px", letterSpacing: "1px", cursor: "pointer", flexShrink: 0 }}
                        >LOG</button>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnimaPlanChecklist({ token, user, onLogged }) {
  const PLAN_KEY = 'anima_weekly_plan';
  const CHECKED_KEY = 'anima_plan_checked';

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CHECKED_KEY)) || {}; } catch { return {}; }
  });
  const [loggingKey, setLoggingKey] = useState(null);
  const [swappingKey, setSwappingKey] = useState(null);
  const [expandedExercise, setExpandedExercise] = useState(null);

  const swapExercise = async (dayIdx, exIdx) => {
    const key = `${dayIdx}-${exIdx}`;
    if (checked[key]) return;
    setSwappingKey(key);
    try {
      const currentExercise = plan.days[dayIdx].exercises[exIdx].name;
      const res = await fetch(`${API_URL}/workouts/swap_exercise`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ exercise: currentExercise })
      });
      const data = await res.json();
      if (data.exercise) {
        const newPlan = JSON.parse(JSON.stringify(plan));
        newPlan.days[dayIdx].exercises[exIdx] = {
          name: data.exercise.name || data.exercise.Name || "Alternative Exercise",
          sets: data.exercise.sets || data.exercise.Sets || 3,
          reps: data.exercise.reps || data.exercise.Reps || 8,
          weight: data.exercise.weight || data.exercise.Weight || 0
        };
        setPlan(newPlan);
        localStorage.setItem(PLAN_KEY, JSON.stringify(newPlan));
        setCelebrating(true);
        setTimeout(() => setCelebrating(false), 600);
      }
    } catch(e) {}
    setSwappingKey(null);
  };

  useEffect(() => {
    const saved = localStorage.getItem(PLAN_KEY);
    if (saved) {
      try { setPlan(JSON.parse(saved)); return; } catch {}
    }
    fetchPlan();
  }, []);

  const fetchPlan = async () => {
    setLoading(true);
    localStorage.removeItem(PLAN_KEY);
    localStorage.removeItem(CHECKED_KEY);
    try {
      const res = await fetch(`${API_URL}/workouts/weekly_plan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ today: getLocalDay() }),
      });
      const data = await res.json();
      if (data.plan) {
        setPlan(data.plan);
        localStorage.setItem(PLAN_KEY, JSON.stringify(data.plan));
        setChecked({});
        localStorage.removeItem(CHECKED_KEY);
      }
    } catch (e) {}
    setLoading(false);
  };

  const toggleExercise = async (dayIdx, exIdx, exercise) => {
    const key = `${dayIdx}-${exIdx}`;
    if (checked[key]) return;
    const newChecked = { ...checked, [key]: true };
    setChecked(newChecked);
    localStorage.setItem(CHECKED_KEY, JSON.stringify(newChecked));
    setCelebrating(true);
    setTimeout(() => setCelebrating(false), 900);
    setLoggingKey(key);
    const n = new Date();
    const today = n.getFullYear() + '-' + String(n.getMonth()+1).padStart(2,'0') + '-' + String(n.getDate()).padStart(2,'0');
    try {
      await fetch(`${API_URL}/workouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ exercise: exercise.name, sets: exercise.sets, reps: exercise.reps, weight: exercise.weight || 0, date: today }),
      });
      localStorage.removeItem('anima_analysis');
      if (onLogged) onLogged();
    } catch (e) {}
    setLoggingKey(null);
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: '13px', letterSpacing: '3px', color: COLORS.teal, textTransform: 'uppercase' }}>ANIMA IS BUILDING YOUR PLAN...</div>
      <div style={{ fontSize: '13px', color: COLORS.silver, marginTop: '8px' }}>Personalizing to your goals</div>
    </div>
  );

  if (!plan) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: '14px', color: COLORS.silver, marginBottom: '20px' }}>No plan generated yet.</div>
      <button style={styles.btnPrimary} onClick={fetchPlan}>Generate My Plan</button>
    </div>
  );

  const totalExercises = plan.days ? plan.days.reduce((acc, d) => acc + (d.exercises ? d.exercises.length : 0), 0) : 0;
  const completedCount = Object.keys(checked).length;

  return (
    <div style={{ paddingBottom: '20px' }}>
      <CelebrationBurst show={celebrating} />

      {expandedExercise && <ExerciseDemoModal name={expandedExercise} onClose={() => setExpandedExercise(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: '900', letterSpacing: '3px', color: COLORS.white }}>{plan.week || 'WEEK 1'}</div>
          <div style={{ fontSize: '12px', color: COLORS.teal, letterSpacing: '2px', marginTop: '2px' }}>{completedCount} / {totalExercises} COMPLETED</div>
        </div>
        <button onClick={fetchPlan} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '8px 14px', color: COLORS.silver, fontSize: '12px', letterSpacing: '2px', cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif" }}>NEW PLAN</button>
      </div>

      <div style={{ height: '4px', background: COLORS.border, borderRadius: '2px', marginBottom: '24px' }}>
        <div style={{ height: '100%', width: `${totalExercises > 0 ? (completedCount / totalExercises) * 100 : 0}%`, background: COLORS.teal, borderRadius: '2px', transition: 'width 0.4s' }} />
      </div>

      {plan.days && plan.days.map((day, dayIdx) => (
        <div key={dayIdx} style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: day.type === 'rest' ? COLORS.silver : COLORS.teal, textTransform: 'uppercase', marginBottom: '10px', paddingBottom: '8px', borderBottom: `1px solid ${COLORS.border}` }}>
            {day.day} — {day.label}
          </div>
          {day.type === 'rest' ? (
            <div style={{ fontSize: '14px', color: COLORS.silver, paddingLeft: '8px', fontStyle: 'italic' }}>Recovery day. Sleep and nutrition.</div>
          ) : (
            day.exercises && day.exercises.map((ex, exIdx) => {
              const key = `${dayIdx}-${exIdx}`;
              const isDone = !!checked[key];
              return (
                <div key={exIdx} onClick={() => !isDone && toggleExercise(dayIdx, exIdx, ex)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderRadius: '12px', marginBottom: '8px', background: isDone ? 'rgba(0,212,212,0.05)' : COLORS.card, border: `1px solid ${isDone ? COLORS.teal : COLORS.border}`, cursor: isDone ? 'default' : 'pointer', opacity: isDone ? 0.7 : 1, transition: 'all 0.2s' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0, border: `2px solid ${isDone ? COLORS.teal : COLORS.border}`, background: isDone ? COLORS.teal : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: COLORS.black, fontWeight: '900' }}>
                    {isDone ? '✓' : ''}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', letterSpacing: '2px', color: isDone ? COLORS.teal : COLORS.white, textDecoration: isDone ? 'line-through' : 'none' }}>
                      {ex.name.toUpperCase()}
                    </div>
                    <div style={{ fontSize: '13px', color: COLORS.silver }}>
                      {ex.sets} sets × {ex.reps} reps{ex.weight > 0 ? ` @ ${ex.weight}lbs` : ' (bodyweight)'}
                    </div>
                  </div>
                  {loggingKey === key && <div style={{ fontSize: '11px', color: COLORS.teal, letterSpacing: '1px' }}>LOGGING...</div>}
                  {!isDone && (
                    <button
                      onClick={e => { e.stopPropagation(); swapExercise(dayIdx, exIdx); }}
                      disabled={swappingKey === key}
                      style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: '6px', padding: '4px 8px', color: swappingKey === key ? COLORS.teal : COLORS.silver, fontSize: '14px', cursor: 'pointer', flexShrink: 0 }}
                      title="Ask ANIMA for an alternative"
                    >
                      {swappingKey === key ? '⏳' : '↻'}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
}

function TrainScreen({ token, user, workouts, onLogged, onMealUpdated, onTabChange }) {
  const [subTab, setSubTab] = useState("log");
  const tabStyle = (active) => ({
    flex: 1, padding: "10px", borderRadius: "8px", border: "none",
    cursor: "pointer", fontSize: "16px", fontWeight: "700",
    letterSpacing: "2px", fontFamily: "'Rajdhani', sans-serif",
    background: active ? COLORS.teal : "transparent",
    color: active ? COLORS.black : COLORS.silver,
    transition: "all 0.2s",
  });
  return (
    <div style={{ ...styles.screen, paddingBottom: "120px" }}>
      <AppHeader title="Train" user={user} onProfileTap={() => onTabChange && onTabChange("profile")} />
      <div style={{ padding: "12px 24px 0" }}>
        <div style={{ display: "flex", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "3px", marginBottom: "4px" }}>
          <button style={tabStyle(subTab === "log")} onClick={() => setSubTab("log")}>🏋️ LOG</button>
          <button style={tabStyle(subTab === "voice")} onClick={() => setSubTab("voice")}>🎤 VOICE</button>
          <button style={tabStyle(subTab === "plan")} onClick={() => setSubTab("plan")}>⚡ PLAN</button>
          <button style={tabStyle(subTab === "history")} onClick={() => setSubTab("history")}>📋 HISTORY</button>
        </div>
      </div>
      {subTab === "log" && <LogWorkoutScreen token={token} user={user} onLogged={onLogged} defaultMode="workout" hideHeader={true} />}
      {subTab === "voice" && <LogWorkoutScreen token={token} user={user} onLogged={onLogged} defaultMode="voice" hideHeader={true} />}
      {subTab === "plan" && (
        <div style={{ ...styles.content, paddingTop: "16px" }}>
          <BYOPToggle token={token} user={user} onLogged={onLogged} />
        </div>
      )}
      {subTab === "history" && (
        <div style={{ ...styles.content, paddingTop: "16px" }}>
          <WorkoutHistoryList workouts={workouts} token={token} onUpdated={onMealUpdated} />
        </div>
      )}
    </div>
  );
}

function NutritionScreen({ token, user, meals, workouts, onLogged, onMealUpdated, onTabChange }) {
  const [subTab, setSubTab] = useState("log");
  const tabStyle = (active) => ({
    flex: 1, padding: "10px", borderRadius: "8px", border: "none",
    cursor: "pointer", fontSize: "16px", fontWeight: "700",
    letterSpacing: "2px", fontFamily: "'Rajdhani', sans-serif",
    background: active ? COLORS.gold : "transparent",
    color: active ? COLORS.black : COLORS.silver,
    transition: "all 0.2s",
  });
  return (
    <div style={{ ...styles.screen, paddingBottom: "120px" }}>
      <AppHeader title="Nutrition" user={user} onProfileTap={() => onTabChange && onTabChange("profile")} />
      <div style={{ padding: "12px 24px 0" }}>
        <div style={{ display: "flex", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: "10px", padding: "3px", marginBottom: "4px" }}>
          <button style={tabStyle(subTab === "log")} onClick={() => setSubTab("log")}>🍽️ LOG</button>
          <button style={tabStyle(subTab === "voice")} onClick={() => setSubTab("voice")}>🎤 VOICE</button>
          <button style={tabStyle(subTab === "history")} onClick={() => setSubTab("history")}>📋 HISTORY</button>
        </div>
      </div>
      {subTab === "log" && <LogWorkoutScreen token={token} user={user} onLogged={onLogged} defaultMode="meal" hideHeader={true} />}
      {subTab === "voice" && <LogWorkoutScreen token={token} user={user} onLogged={onLogged} defaultMode="voice" hideHeader={true} />}
      {subTab === "history" && (
        <div style={{ ...styles.content, paddingTop: "16px" }}>
          <MealHistoryList meals={meals} token={token} user={user} onUpdated={onMealUpdated} />
        </div>
      )}
    </div>
  );
}

function WorkoutHistoryList({ workouts, token, onUpdated }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const deleteWorkout = async (id) => {
    if (!window.confirm("Delete this exercise?")) return;
    try {
      await fetch(`${API_URL}/workouts/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (onUpdated) onUpdated();
    } catch(e) {}
  };

  const now = new Date();
  const grouped = workouts.reduce((acc, w) => {
    if (!acc[w.date]) acc[w.date] = [];
    acc[w.date].push(w);
    return acc;
  }, {});

  if (workouts.length === 0) return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏋️</div>
      <div style={{ fontSize: "18px", letterSpacing: "3px", color: COLORS.silver }}>NO WORKOUTS LOGGED YET</div>
    </div>
  );

  return (
    <div>
      {Object.entries(grouped).sort((a,b) => new Date(b[0]) - new Date(a[0])).map(([date, entries]) => (
        <div key={date}>
          <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, textTransform: "uppercase", marginBottom: "10px", marginTop: "20px" }}>
            {new Date(date + "T12:00:00").toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}
          </div>
          {entries.map((entry, i) => (
            <div key={i} style={styles.workoutItem}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "16px", fontWeight: "700", letterSpacing: "2px", color: COLORS.white }}>{entry.exercise?.toUpperCase()}</div>
                <div style={{ fontSize: "16px", color: COLORS.silver }}>{entry.sets} × {entry.reps} @ {entry.weight}lbs</div>
                {editingId === entry.id ? (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                      {[["sets","Sets"],["reps","Reps"],["weight","Weight"]].map(([f,l]) => (
                        <div key={f}>
                          <div style={{ fontSize: "10px", color: COLORS.silver, marginBottom: "4px" }}>{l}</div>
                          <input type="number" value={editForm[f] || ""} onChange={e => setEditForm({...editForm, [f]: e.target.value})}
                            style={{ width: "100%", background: COLORS.charcoal, border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", color: COLORS.white, fontSize: "16px", fontFamily: "'Rajdhani', sans-serif", outline: "none", boxSizing: "border-box" }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={async () => {
                        await fetch(`${API_URL}/workouts/${entry.id}`, { method: "PUT", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(editForm) });
                        setEditingId(null);
                        if (onUpdated) onUpdated();
                      }} style={{ flex: 1, background: COLORS.teal, border: "none", borderRadius: "8px", padding: "8px", color: COLORS.black, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", fontSize: "16px", cursor: "pointer" }}>SAVE</button>
                      <button onClick={() => setEditingId(null)} style={{ flex: 1, background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", color: COLORS.silver, fontFamily: "'Rajdhani', sans-serif", fontSize: "16px", cursor: "pointer" }}>CANCEL</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                    <button onClick={() => { setEditingId(entry.id); setEditForm({ sets: entry.sets, reps: entry.reps, weight: entry.weight }); }}
                      style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: "6px", padding: "3px 10px", color: COLORS.silver, fontSize: "16px", cursor: "pointer", fontFamily: "'Rajdhani', sans-serif" }}>✏️ Edit</button>
                    <button onClick={() => deleteWorkout(entry.id)}
                      style={{ background: "transparent", border: "none", color: "rgba(255,68,68,0.5)", fontSize: "16px", cursor: "pointer", fontFamily: "'Rajdhani', sans-serif" }}>🗑️ Delete</button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: "20px", fontWeight: "900", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", textAlign: "center", minWidth: "50px" }}>
                {entry.weight}<div style={{ fontSize: "9px", color: COLORS.silver }}>LBS</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MealHistoryList({ meals, token, user, onUpdated }) {
  const [editingMeal, setEditingMeal] = useState(null);
  const [editForm, setEditForm] = useState({});

  const deleteMeal = async (id) => {
    if (!window.confirm("Delete this meal?")) return;
    try {
      await fetch(`${API_URL}/meals/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (onUpdated) onUpdated();
    } catch(e) {}
  };

  const saveMealEdit = async () => {
    try {
      await fetch(`${API_URL}/meals/${editingMeal}`, { method: "PUT", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(editForm) });
      setEditingMeal(null);
      if (onUpdated) onUpdated();
    } catch(e) {}
  };

  // Weekly chart
  const now = new Date();
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    const dateStr = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
    const dayMeals = meals.filter(m => m.date === dateStr);
    const cal = dayMeals.reduce((a,m) => a + (m.calories||0), 0);
    days.push({ dateStr, cal, label: ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()] });
  }
  const maxCal = Math.max(...days.map(d => d.cal), 1);
  const weightLbs = user?.weight || 180;
  const actMult = user?.activity_level === "sedentary" ? 1.2 : user?.activity_level === "light" ? 1.375 : user?.activity_level === "active_job" ? 1.65 : user?.activity_level === "very" ? 1.725 : user?.activity_level === "athlete" ? 1.9 : 1.55;
  const heightCm = (user?.height||70)*2.54; const weightKg = weightLbs*0.453592;
  const bmr = (10*weightKg)+(6.25*heightCm)-(5*(user?.age||30))+5;
  const calAdj = user?.fitness_goal === "aggressive_cut" ? -500 : user?.fitness_goal === "cut" ? -250 : user?.fitness_goal === "lean_bulk" ? 250 : user?.fitness_goal === "bulk" ? 500 : 0;
  const calorieTarget = Math.round(bmr*actMult)+calAdj;

  // Today summary
  const todayStr = now.getFullYear() + "-" + String(now.getMonth()+1).padStart(2,"0") + "-" + String(now.getDate()).padStart(2,"0");
  const todayMeals = meals.filter(m => m.date === todayStr);
  const totals = todayMeals.reduce((a,m) => ({ calories: a.calories+(m.calories||0), protein: a.protein+(m.protein||0), carbs: a.carbs+(m.carbs||0), fat: a.fat+(m.fat||0) }), {calories:0,protein:0,carbs:0,fat:0});
  const proteinTarget = Math.round(weightLbs*(user?.fitness_goal==="aggressive_cut"?1.2:user?.fitness_goal==="cut"?1.1:1.0));

  const grouped = meals.reduce((acc,m) => { if(!acc[m.date]) acc[m.date]=[]; acc[m.date].push(m); return acc; }, {});

  return (
    <div>
      {/* Weekly chart */}
      {days.some(d => d.cal > 0) && (
        <div style={{ background: `linear-gradient(160deg, ${COLORS.card}, ${COLORS.charcoal})`, border: `1px solid ${COLORS.border}`, borderRadius: "16px", padding: "20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.gold, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", marginBottom: "16px" }}>📅 7-DAY NUTRITION</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "60px", marginBottom: "6px" }}>
            {days.map((day,i) => (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" }}>
                <div style={{ width:"100%", height:`${Math.max((day.cal/maxCal)*50,day.cal>0?3:0)}px`, background: day.cal>=calorieTarget*0.85?COLORS.teal:day.cal>0?COLORS.gold:COLORS.border, borderRadius:"3px 3px 0 0" }} />
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:"6px" }}>
            {days.map((day,i) => (
              <div key={i} style={{ flex:1, textAlign:"center", fontSize:"10px", fontFamily:"'Rajdhani',sans-serif", color: day.dateStr===todayStr?COLORS.gold:COLORS.silver, fontWeight: day.dateStr===todayStr?"900":"400" }}>{day.label}</div>
            ))}
          </div>
        </div>
      )}

      {/* Today summary */}
      {todayMeals.length > 0 && (
        <div style={{ background: `linear-gradient(160deg, ${COLORS.card}, ${COLORS.charcoal})`, border: `1px solid ${COLORS.teal}`, borderRadius: "16px", padding: "20px", marginBottom: "16px", boxShadow: `0 0 20px ${COLORS.tealGlow}` }}>
          <div style={{ fontSize: "16px", letterSpacing: "3px", color: COLORS.teal, fontFamily: "'Rajdhani', sans-serif", fontWeight: "700", marginBottom: "12px" }}>⚡ TODAY</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "8px", marginBottom: "12px" }}>
            {[{l:"CAL",v:totals.calories,c:COLORS.gold},{l:"PROTEIN",v:totals.protein,c:COLORS.teal,u:"g"},{l:"CARBS",v:totals.carbs,c:"#9B59B6",u:"g"},{l:"FAT",v:totals.fat,c:"#E67E22",u:"g"}].map(({l,v,c,u}) => (
              <div key={l} style={{ textAlign:"center", background:"rgba(0,0,0,0.2)", borderRadius:"10px", padding:"10px 4px" }}>
                <div style={{ fontSize:"18px", fontWeight:"900", color:c, fontFamily:"'Rajdhani',sans-serif" }}>{Math.round(v)}{u||""}</div>
                <div style={{ fontSize:"9px", color:COLORS.silver, letterSpacing:"1px" }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom:"6px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"3px" }}>
              <span style={{ fontSize:"10px", color:COLORS.silver }}>PROTEIN</span>
              <span style={{ fontSize:"10px", color:COLORS.teal }}>{Math.round(totals.protein)}g / {proteinTarget}g</span>
            </div>
            <div style={{ height:"4px", background:COLORS.border, borderRadius:"2px", overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${Math.min(100,(totals.protein/proteinTarget)*100)}%`, background:COLORS.teal, borderRadius:"2px" }} />
            </div>
          </div>
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"3px" }}>
              <span style={{ fontSize:"10px", color:COLORS.silver }}>CALORIES</span>
              <span style={{ fontSize:"10px", color:COLORS.gold }}>{Math.round(totals.calories)} / {calorieTarget}</span>
            </div>
            <div style={{ height:"4px", background:COLORS.border, borderRadius:"2px", overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${Math.min(100,(totals.calories/calorieTarget)*100)}%`, background:COLORS.gold, borderRadius:"2px" }} />
            </div>
          </div>
        </div>
      )}

      {/* Meal history */}
      {Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0" }}>
          <div style={{ fontSize:"48px", marginBottom:"16px" }}>🍽️</div>
          <div style={{ fontSize:"18px", letterSpacing:"3px", color:COLORS.silver }}>NO MEALS LOGGED YET</div>
        </div>
      ) : (
        Object.entries(grouped).sort((a,b) => new Date(b[0])-new Date(a[0])).map(([date, entries]) => (
          <div key={date}>
            <div style={{ fontSize:"16px", letterSpacing:"3px", color:COLORS.gold, textTransform:"uppercase", marginBottom:"10px", marginTop:"20px" }}>
              {new Date(date+"T12:00:00").toLocaleDateString("en", { weekday:"long", month:"long", day:"numeric" })}
            </div>
            {entries.map((entry,i) => (
              <div key={i} style={{ ...styles.workoutItem, borderLeft:`3px solid ${COLORS.gold}` }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"16px", fontWeight:"700", color:COLORS.gold }}>{entry.meal_type?.toUpperCase()}</div>
                  <div style={{ fontSize:"16px", color:COLORS.silverBright }}>{entry.description}</div>
                  {(entry.calories||entry.protein) && (
                    <div style={{ fontSize:"16px", color:COLORS.silver, marginTop:"4px" }}>
                      {entry.calories?`${entry.calories} cal`:""}{entry.calories&&entry.protein?" · ":""}{entry.protein?`${entry.protein}g protein`:""}{entry.carbs?` · ${entry.carbs}g carbs`:""}{entry.fat?` · ${entry.fat}g fat`:""}
                    </div>
                  )}
                  {editingMeal === entry.id ? (
                    <div style={{ marginTop:"10px" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"8px" }}>
                        {[["calories","Cal"],["protein","Protein g"],["carbs","Carbs g"],["fat","Fat g"]].map(([f,l]) => (
                          <div key={f}>
                            <div style={{ fontSize:"10px", color:COLORS.silver, marginBottom:"4px" }}>{l}</div>
                            <input type="number" value={editForm[f]||""} onChange={e => setEditForm({...editForm,[f]:parseFloat(e.target.value)||""})}
                              style={{ width:"100%", background:COLORS.charcoal, border:`1px solid ${COLORS.border}`, borderRadius:"8px", padding:"8px", color:COLORS.white, fontSize:"16px", fontFamily:"'Rajdhani',sans-serif", outline:"none", boxSizing:"border-box" }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display:"flex", gap:"8px" }}>
                        <button onClick={saveMealEdit} style={{ flex:1, background:COLORS.teal, border:"none", borderRadius:"8px", padding:"8px", color:COLORS.black, fontFamily:"'Rajdhani',sans-serif", fontWeight:"700", fontSize:"16px", cursor:"pointer" }}>SAVE</button>
                        <button onClick={() => setEditingMeal(null)} style={{ flex:1, background:"transparent", border:`1px solid ${COLORS.border}`, borderRadius:"8px", padding:"8px", color:COLORS.silver, fontFamily:"'Rajdhani',sans-serif", fontSize:"16px", cursor:"pointer" }}>CANCEL</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display:"flex", gap:"8px", marginTop:"6px" }}>
                      <button onClick={() => { setEditingMeal(entry.id); setEditForm({calories:entry.calories||"",protein:entry.protein||"",carbs:entry.carbs||"",fat:entry.fat||""}); }}
                        style={{ background:"transparent", border:`1px solid ${COLORS.border}`, borderRadius:"6px", padding:"3px 10px", color:COLORS.silver, fontSize:"16px", cursor:"pointer", fontFamily:"'Rajdhani',sans-serif" }}>✏️ Edit Macros</button>
                      <button onClick={() => deleteMeal(entry.id)}
                        style={{ background:"transparent", border:"none", color:"rgba(255,68,68,0.5)", fontSize:"16px", cursor:"pointer", fontFamily:"'Rajdhani',sans-serif" }}>🗑️ Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function NavBar({ active, onChange, meals, user }) {
  const items = [
    { id: "dashboard", icon: "⚡", label: "Home" },
    { id: "train", icon: "🏋️", label: "Train" },
    { id: "nutrition", icon: "🍽️", label: "Nutrition" },
    { id: "commons", icon: "🔥", label: "Commons" },
    { id: "coach", icon: "🧠", label: "ANIMA" },
  ];

  const now = new Date();
  const todayStr = now.getFullYear() + "-" + String(now.getMonth()+1).padStart(2,"0") + "-" + String(now.getDate()).padStart(2,"0");
  const todayMeals = (meals || []).filter(m => m.date === todayStr);
  const totals = todayMeals.reduce((a,m) => ({
    calories: a.calories + (m.calories||0),
    protein: a.protein + (m.protein||0),
    carbs: a.carbs + (m.carbs||0),
    fat: a.fat + (m.fat||0),
  }), { calories:0, protein:0, carbs:0, fat:0 });

  const weightLbs = user?.weight || 180;
  const actMult = user?.activity_level === "sedentary" ? 1.2 : user?.activity_level === "light" ? 1.375 : user?.activity_level === "active_job" ? 1.65 : user?.activity_level === "very" ? 1.725 : user?.activity_level === "athlete" ? 1.9 : 1.55;
  const heightCm = (user?.height||70)*2.54;
  const weightKg = weightLbs*0.453592;
  const bmr = (10*weightKg)+(6.25*heightCm)-(5*(user?.age||30))+5;
  const calAdj = user?.fitness_goal === "aggressive_cut" ? -500 : user?.fitness_goal === "cut" ? -250 : user?.fitness_goal === "lean_bulk" ? 250 : user?.fitness_goal === "bulk" ? 500 : 0;
  const calorieTarget = Math.round(bmr*actMult)+calAdj;
  const proteinTarget = Math.round(weightLbs*(user?.fitness_goal==="aggressive_cut"?1.2:user?.fitness_goal==="cut"?1.1:1.0));

  return (
    <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "430px", zIndex: 100 }}>
      <div style={{ background: "rgba(17,19,24,0.97)", borderTop: `1px solid ${COLORS.border}`, padding: "6px 16px", display: "flex", justifyContent: "space-around", alignItems: "center" }}>
        {[
          { label: "CAL", value: Math.round(totals.calories), target: calorieTarget, color: COLORS.gold },
          { label: "PRO", value: Math.round(totals.protein), target: proteinTarget, color: COLORS.teal, unit: "g" },
          { label: "CARBS", value: Math.round(totals.carbs), target: 150, color: "#9B59B6", unit: "g" },
          { label: "FAT", value: Math.round(totals.fat), target: 80, color: "#E67E22", unit: "g" },
        ].map(({ label, value, target, color, unit }) => (
          <div key={label} style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: "16px", fontWeight: "900", color: value > 0 ? color : COLORS.border, fontFamily: "'Rajdhani', sans-serif", lineHeight: 1 }}>
              {value > 0 ? value : "—"}{value > 0 && unit ? unit : ""}
            </div>
            {target && value > 0 && (
              <div style={{ height: "2px", background: COLORS.border, borderRadius: "1px", margin: "2px 4px 0", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100,(value/target)*100)}%`, background: color, borderRadius: "1px" }} />
              </div>
            )}
            <div style={{ fontSize: "8px", color: COLORS.silver, letterSpacing: "1px", opacity: 0.6, marginTop: "1px" }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ background: COLORS.charcoal, borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-around", alignItems: "center", padding: "12px 0 24px" }}>
        {items.map(item => (
          <div key={item.id}
            style={{ ...styles.navItem, background: active === item.id ? COLORS.tealGlow : "transparent", minWidth: "56px" }}
            onClick={() => onChange(item.id)}
          >
            <span style={{ ...styles.navIcon, fontSize: active === item.id ? "26px" : "22px", transition: "font-size 0.15s" }}>{item.icon}</span>
            {active === item.id && (
              <span style={{ ...styles.navLabel, color: COLORS.teal, fontSize: "9px", letterSpacing: "1px" }}>{item.label}</span>
            )}
          </div>
        ))}

      </div>
    </div>
  );
}
export default function App() {
  const [screen, setScreen] = useState("landing");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [meals, setMeals] = useState([]);

  useEffect(() => {
    // Check for password reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("reset_token");
    if (resetToken) {
      setScreen("reset_password");
      setResetToken(resetToken);
      return;
    }
    const savedToken = localStorage.getItem("forima_token");
    const savedUser = localStorage.getItem("forima_user");
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setScreen("app");
      fetchWorkouts(savedToken);
      fetchMeals(savedToken);
    }
  }, []);

  const fetchWorkouts = async (t) => {
    try {
      const res = await fetch(`${API_URL}/workouts`, {
        headers: { "Authorization": `Bearer ${t || token}` },
      });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      if (Array.isArray(data)) setWorkouts(data);
    } catch (e) {}
  };
  const fetchMeals = async (t) => {
    try {
      const res = await fetch(`${API_URL}/meals`, {
        headers: { "Authorization": `Bearer ${t || token}` },
      });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      if (Array.isArray(data)) setMeals(data);
    } catch (e) {}
  };

  const handleAuth = async (t, u) => {
    setToken(t);
    setUser(u);
    fetchWorkouts(t);
    fetchMeals(t);

    try {
      const workoutsRes = await fetch(`${API_URL}/workouts`, { headers: { "Authorization": `Bearer ${t}` } });
      const workoutsData = await workoutsRes.json();
      const isNewUser = !Array.isArray(workoutsData) || workoutsData.length === 0;

      if (isNewUser) {
        setScreen("welcome");
      } else {
        setActiveTab("dashboard");
        setScreen("app");
      }
    } catch (e) {
      setScreen("app");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("forima_token");
    localStorage.removeItem("forima_user");
    setToken(null);
    setUser(null);
    setScreen("landing");
    setWorkouts([]);
    setMeals([]);
  };

  return (
    <div style={styles.app}>
      <div className="grid-overlay" />
      <link href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Bebas+Neue&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes waveform {
          0%, 100% { transform: scaleY(0.4); opacity: 0.6; }
          50% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes pulse-teal {
          0%, 100% { box-shadow: 0 0 20px rgba(0,212,212,0.2); }
          50% { box-shadow: 0 0 40px rgba(0,212,212,0.5); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        body, #root {
          background: radial-gradient(ellipse at 20% 10%, rgba(0,212,212,0.12) 0%, transparent 50%),
                      radial-gradient(ellipse at 80% 90%, rgba(212,160,23,0.08) 0%, transparent 50%),
                      linear-gradient(180deg, #0D1117 0%, #0A0A0A 100%) !important;
          min-height: 100vh;
        }
        body::before {
          content: '';
          position: fixed;
          inset: 0;
          background:
            radial-gradient(ellipse at 15% 15%, rgba(0,212,212,0.07) 0%, transparent 50%),
            radial-gradient(ellipse at 85% 85%, rgba(212,160,23,0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 0;
        }
        .grid-overlay {
          position: fixed;
          inset: 0;
          background-image:
            linear-gradient(rgba(0,212,212,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,212,212,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
          z-index: 0;
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0A0A0A; }
        ::-webkit-scrollbar-thumb { background: #1E2530; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #00D4D4; }
      `}</style>

      {screen === "reset_password" && (
          <ResetPasswordScreen resetToken={resetToken} onDone={() => setScreen("login")} />
        )}
        {screen === "landing" && (
        <LandingScreen
          onGetStarted={() => setScreen("onboarding")}
          onLogin={() => setScreen("login")}
        />
      )}

      {screen === "onboarding" && (
        <OnboardingScreen onComplete={handleAuth} />
      )}

      {screen === "login" && (
        <LoginScreen onLogin={handleAuth} onBack={() => setScreen("landing")} />
      )}
     
      {screen === "welcome" && (
        <AnimaWelcomeScreen
          user={user}
          token={token}
          onComplete={() => setScreen("app")}
        />
      )}

      {screen === "app" && (
        <>
          {activeTab === "dashboard" && (
            <DashboardScreen user={user} token={token} workouts={workouts} meals={meals} onRefresh={() => fetchWorkouts(token)} onTabChange={setActiveTab} />
          )}
          {activeTab === "train" && (
            <TrainScreen token={token} user={user} workouts={workouts} onTabChange={setActiveTab} onLogged={() => { fetchWorkouts(token); fetchMeals(token); }} onMealUpdated={() => { fetchWorkouts(token); fetchMeals(token); }} />
          )}
          {activeTab === "nutrition" && (
            <NutritionScreen token={token} user={user} meals={meals} workouts={workouts} onTabChange={setActiveTab} onLogged={() => { fetchWorkouts(token); fetchMeals(token); }} onMealUpdated={() => { fetchWorkouts(token); fetchMeals(token); }} />
          )}
          {activeTab === "coach" && (
            <CoachScreen token={token} workouts={workouts} user={user} onTabChange={setActiveTab} />
          )}
          {activeTab === "commons" && (
            <CommonsScreen token={token} user={user} onTabChange={setActiveTab} />
          )}
          {activeTab === "profile" && (
            <ProfileScreen user={user} token={token} onLogout={handleLogout} onSave={(u) => setUser(u)} />
          )}
          <NavBar active={activeTab} onChange={(tab) => { setActiveTab(tab); localStorage.setItem("forima_tab", tab); }} meals={meals} user={user} />
        </>
      )}
    </div>
  );
}
// Mon Apr 27 10:10:34 AM CDT 2026
